/**
 * Vlag-configurator: upload of kies een vlag, verplaats en schaal het ontwerp.
 * Eén gedeeld beeld — bij dubbelzijdig staan beide kanten om de mast heen.
 * Stuurt positie + één voorbeeldafbeelding mee als line item properties.
 */

const STANDAARDMAAT = { w: 150, h: 100 };

/** Bouwt het tekenvlak op de verhouding van het gekozen vlagformaat. */
function bouwLayout(verhouding, dubbel, focus) {
  // Focusstand bij een dubbelzijdige vlag: een zijde groot in beeld om in te
  // werken (zie #zetFocus). De zijde wordt gelijkmatig vergroot vanuit de maat
  // in de gewone weergave (600 breed), zodat de verhouding van het vlagvlak
  // exact gelijk blijft. Alles op de vlag staat in procenten van dat vlak: een
  // element op 50% blijft op 50%, en het drukbestand verandert niet.
  if (dubbel && (focus === 0 || focus === 1)) {
    const fh0 = Math.round(600 / verhouding);
    const k = 1.55;
    const fw = 600 * k;
    const fh = fh0 * k;
    const rand = 16;
    const mastBreed = 12;
    const tussen = 10;
    const view = { w: rand + fw + tussen + mastBreed + rand, h: rand + fh + rand };
    // De linkerzijde hangt links van de mast, de rechterzijde rechts ervan.
    const mastRechts = focus === 0;
    const vlagX = mastRechts ? rand : rand + mastBreed + tussen;
    const mastX = mastRechts ? rand + fw + tussen : rand;
    const inBeeld = { x: vlagX, y: rand, w: fw, h: fh };
    // De andere zijde blijft bestaan met dezelfde maat, maar buiten beeld. Zo
    // blijven alle berekeningen voor die zijde geldig (scherpte, drukbestand,
    // plaatsing); alleen tekenen en aanraken slaan hem over.
    const buitenBeeld = { x: view.w + 200, y: rand, w: fw, h: fh };
    return {
      view,
      mast: { x: mastX, y: 6, w: mastBreed, h: view.h - 12 },
      foot: null,
      flags: focus === 0 ? [inBeeld, buitenBeeld] : [buitenBeeld, inBeeld],
      ticks: [rand + fh * 0.175, rand + fh * 0.5, rand + fh * 0.825],
      focus,
    };
  }
  if (dubbel) {
    const fw = 600;
    const fh = Math.round(fw / verhouding);
    const view = { w: 1320, h: fh + 100 };
    return {
      view,
      mast: { x: 653, y: 14, w: 14, h: view.h - 28 },
      foot: { x: 643, y: view.h - 20, w: 34, h: 11 },
      flags: [
        { x: 25, y: 40, w: fw, h: fh },
        { x: 695, y: 40, w: fw, h: fh },
      ],
      ticks: [40 + fh * 0.175, 40 + fh * 0.5, 40 + fh * 0.825],
    };
  }
  const fw = 900;
  const fh = Math.round(fw / verhouding);
  const view = { w: 1000, h: fh + 60 };
  return {
    view,
    mast: { x: 58, y: 20, w: 12, h: view.h - 30 },
    foot: { x: 50, y: view.h - 16, w: 28, h: 10 },
    flags: [{ x: 70, y: 30, w: fw, h: fh }],
    ticks: [30 + fh * 0.167, 30 + fh * 0.5, 30 + fh * 0.833],
  };
}

/**
 * Beachflags zijn geen rechthoek: het doek is gebogen en zit met een sok om de
 * mast. De preview knipt het ontwerp daarom uit in de echte vlagvorm, zodat de
 * klant ziet wat er wegvalt aan de bovenrand.
 */
function beachflagLayout() {
  const view = { w: 760, h: 1180 };
  return {
    view,
    mast: { x: 150, y: 30, w: 9, h: view.h - 60 },
    foot: { x: 96, y: view.h - 34, w: 118, h: 9 },
    flags: [{ x: 159, y: 50, w: 340, h: 1010 }],
    ticks: [],
    beach: true,
  };
}

/**
 * Een spandoek hangt niet aan een mast maar wordt met ringen opgespannen.
 * De preview toont daarom het doek met zoom en zeilringen langs de rand,
 * zodat de klant ziet hoe dicht zijn ontwerp bij de ringen komt.
 */
function spandoekLayout(verhouding) {
  const fw = 900;
  const fh = Math.max(140, Math.round(fw / verhouding));
  const marge = 46;
  const view = { w: fw + marge * 2, h: fh + marge * 2 };
  return {
    view,
    mast: null,
    foot: null,
    flags: [{ x: marge, y: marge, w: fw, h: fh }],
    ticks: [],
    spandoek: true,
  };
}

/** Punten van de vlagvorm binnen het opgegeven vak. */
function vlagPad(vorm, r) {
  const { x, y, w, h } = r;
  const bz = (p0, p1, p2, p3, n = 48) => {
    const uit = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const u = 1 - t;
      uit.push([
        u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
        u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
      ]);
    }
    return uit;
  };

  if (vorm === 'recht') {
    return [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ];
  }
  if (vorm === 'druppel') {
    return [[x, y + h], [x, y]].concat(
      bz([x, y], [x + w * 1.45, y + h * 0.1], [x + w * 1.3, y + h * 0.7], [x + w * 0.3, y + h])
    );
  }
  // veervorm: rechte voorrand, brede ronde schouder, rechte achterrand
  const schouder = h * 0.3;
  return [[x, y + h], [x, y + schouder * 0.1]]
    .concat(bz([x, y + schouder * 0.1], [x + w * 0.12, y], [x + w * 0.72, y + schouder * 0.1], [x + w, y + schouder]))
    .concat([[x + w, y + h]]);
}

/**
 * Lettertypen voor tekst op de vlag. Bewust kort en bewust alleen fonts die op
 * vrijwel elke computer en telefoon staan: de tekst wordt in de browser van de
 * klant in het drukbestand gebakken, dus een lettertype dat hij niet heeft,
 * wordt stilletjes vervangen — en dan drukken we iets anders dan hij zag.
 */
// Kleur van het kader om het element dat je versleept. Paars, want dat komt
// nergens anders op de vlag of in de winkel voor: rood zou verward worden met
// de huisstijl en met de rode hulplijnen bij het uitlijnen.
const SELECTIE_KLEUR = '#8b5cf6';

const LETTERTYPEN = [
  // Op elk apparaat aanwezig
  { naam: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { naam: 'Impact', css: 'Impact, "Arial Black", sans-serif' },
  { naam: 'Times', css: '"Times New Roman", Times, serif' },
  { naam: 'Georgia', css: 'Georgia, "Times New Roman", serif' },
  { naam: 'Courier', css: '"Courier New", Courier, monospace' },
  { naam: 'Verdana', css: 'Verdana, Geneva, sans-serif' },
  // Meegeladen via Google Fonts. Voor het drukbestand wordt gewacht tot ze
  // binnen zijn — anders bakt de browser er stilletjes Arial in en krijgt de
  // klant een andere vlag dan hij zag.
  { naam: 'Anton', css: 'Anton, Impact, sans-serif', web: true },
  { naam: 'Bebas Neue', css: '"Bebas Neue", Impact, sans-serif', web: true },
  { naam: 'Oswald', css: 'Oswald, "Arial Narrow", sans-serif', web: true },
  { naam: 'Montserrat', css: 'Montserrat, Arial, sans-serif', web: true },
  { naam: 'Playfair Display', css: '"Playfair Display", Georgia, serif', web: true },
  { naam: 'Lobster', css: 'Lobster, Georgia, cursive', web: true },
  { naam: 'Pacifico', css: 'Pacifico, Georgia, cursive', web: true },
  { naam: 'Caveat', css: 'Caveat, Georgia, cursive', web: true },
  { naam: 'Righteous', css: 'Righteous, Impact, sans-serif', web: true },
  { naam: 'Fredoka', css: 'Fredoka, Verdana, sans-serif', web: true },
  // Gekozen op wat op een vlag werkt: zwaar genoeg om van afstand te lezen, of
  // sierlijk genoeg voor een bruiloft. Een dun lettertype verdwijnt op vier
  // meter afstand, hoe mooi het van dichtbij ook is.
  { naam: 'Archivo Black', css: '"Archivo Black", Impact, sans-serif', web: true },
  { naam: 'Alfa Slab One', css: '"Alfa Slab One", Georgia, serif', web: true },
  { naam: 'Staatliches', css: 'Staatliches, Impact, sans-serif', web: true },
  { naam: 'Teko', css: 'Teko, "Arial Narrow", sans-serif', web: true },
  { naam: 'Abril Fatface', css: '"Abril Fatface", Georgia, serif', web: true },
  { naam: 'Cinzel', css: 'Cinzel, Georgia, serif', web: true },
  { naam: 'Great Vibes', css: '"Great Vibes", Georgia, cursive', web: true },
  { naam: 'Permanent Marker', css: '"Permanent Marker", Georgia, cursive', web: true },
];

/**
 * Vormen voor de ontwerptool. Elke vorm wordt getekend in een vak van 1 bij 1
 * rond het middelpunt (dus van -0,5 tot 0,5), zodat dezelfde functie zowel het
 * knopje van 40 beeldpunten als de vlag van drie meter kan tekenen — één som,
 * geen tweede versie die uit de pas gaat lopen.
 *
 * Bewust getekend en niet als plaatje ingeladen: zo blijven ze op elk formaat
 * scherp, ook in het drukbestand, en er komt geen bestand bij het thema.
 */
const FLAGCFG_VERSIE = "2026-09-15 15:00";

const DPR = 1.6;

class FlagConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    this.noPreviewText = this.dataset.tNofile || '';
    this.pdfBezigText = this.dataset.tPdf || '';
    this.flagBase = this.dataset.flagBase || '';
    this.uploadUrl = this.dataset.uploadUrl || '';
    this.chosenText = this.dataset.tChosen || '';

    const panels = [...this.querySelectorAll('.flagcfg__side')];
    this.dubbel = panels.length > 1;

    try {
      this.maten = JSON.parse(this.dataset.variantSizes || '{}');
    } catch (err) {
      this.maten = {};
    }
    try {
      this.vormen = JSON.parse(this.dataset.variantVormen || '{}');
    } catch (err) {
      this.vormen = {};
    }
    this.isBeach = Object.keys(this.vormen).length > 0;

    this.isSpandoek = this.dataset.soort === 'spandoek';
    // Keuzerijen op de productpagina (zie #bindRijen).
    this.rijen = this.hasAttribute('data-rijen');

    if (this.isBeach) {
      this.vorm = this.vormen[String(this.dataset.variantId)] || 'veer';
      // Representatieve printbreedte van een beachflag; alleen gebruikt om de
      // scherpte in ppi te schatten, niet als maatvoering naar de klant.
      this.flagCm = { w: 72, h: 260 };
      this.layout = beachflagLayout();
    } else if (this.isSpandoek) {
      this.flagCm = this.#maatVan(this.dataset.variantId) || { w: 300, h: 100 };
      this.layout = spandoekLayout(this.flagCm.w / this.flagCm.h);
    } else {
      this.flagCm = this.#maatVan(this.dataset.variantId) || STANDAARDMAAT;
      this.layout = bouwLayout(this.flagCm.w / this.flagCm.h, this.dubbel);
    }

    this.canvas = this.querySelector('[data-canvas]');
    this.ctx = this.canvas.getContext('2d');
    this.emptyEl = this.querySelector('[data-empty]');
    this.previewInput = this.querySelector('[data-preview]');

    this.#zetCanvas();

    // op aanraakschermen de hint aanpassen (knijpen kan daar)
    if (this.dataset.tTouch && matchMedia('(pointer: coarse)').matches) {
      this.querySelectorAll('[data-hint]').forEach((el) => {
        el.innerHTML = this.dataset.tTouch;
      });
    }

    this.#initNames();
    this.sides = panels.map((el, i) => this.#initSide(el, i));
    this.#bindGestures();
    this.#bindKlikOpDoek();
    this.#bindBusyState();
    this.#bindScherpteRem();
    this.#bindVariant();
    this.#bindFormulier();
    this.#bindVolScherm();
    this.#bindPreview();
    this.#bindFocus();
    this.#bindHerstelDoek();
    this.#markeerFavoriet();
    this.#bindAfwerking();
    this.#bindRijen();
    this.#verzorgDoekkeuze();
    this.#render();
    this.#disableForeignProperties();
    this.#bindOntwerpOpslag();
  }

  /**
   * 150 x 100 cm is de gangbare gevelmaat en veruit de meest gekozen. Hem
   * voorselecteren kan niet zonder zichtbare sprong: Shopify rendert altijd de
   * eerste variant, en de variantvolgorde is bij Shopify gekoppeld aan de
   * volgorde van de maatlijst — je kunt dus niet én oplopend tonen én een
   * andere maat als standaard hebben. Daarom wijzen we hem alleen aan: de knop
   * krijgt een rand en het label "meest gekozen".
   */
  /**
   * Wisselt de tekening naast de afwerkingskeuze mee met wat er gekozen is.
   *
   * De vier tekeningen staan allemaal in de pagina en er is er steeds één
   * zichtbaar. Dat is eenvoudiger dan ze inladen, en het werkt ook als het
   * script om wat voor reden dan ook niet draait: dan staat de eerste er.
   */
  #bindAfwerking() {
    const keuze = this.querySelector('[data-finishkeuze]');
    if (!keuze) return;
    const beeld = this.querySelector('[data-finishbeeld]');
    const iconen = beeld ? [...beeld.querySelectorAll('[data-finishicoon]')] : [];
    // Als keuzerij (Jos, 15 sep 2026): vier tegels met de tekening, en de
    // gekozen naam in de kop van de rij. De keuzelijst blijft het veld dat
    // meegaat naar de bestelling; de tegels zetten alleen zijn waarde.
    const tegels = [...this.querySelectorAll('[data-afwerkingoptie]')];
    const naam = this.querySelector('[data-afwerkingwaarde]');
    const toon = () => {
      const i = keuze.selectedIndex < 0 ? 0 : keuze.selectedIndex;
      iconen.forEach((el, n) => { el.hidden = n !== i; });
      tegels.forEach((el, n) => { el.setAttribute('aria-pressed', n === i ? 'true' : 'false'); });
      const optie = keuze.options[i];
      const tekst = optie ? optie.textContent.trim() : '';
      if (naam && naam.textContent !== tekst) naam.textContent = tekst;
    };
    tegels.forEach((el) => {
      el.addEventListener('click', () => {
        const n = Number(el.dataset.afwerkingoptie);
        if (keuze.selectedIndex !== n) {
          keuze.selectedIndex = n;
          keuze.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // Gekozen, dus de rij weer dicht, net als een keuzelijst.
        this.#klapRij(this.querySelector('[data-afwerkingrij]'), false);
      });
    });
    keuze.addEventListener('change', toon);
    toon();
  }

  /**
   * Keuzerijen (Jos, 15 sep 2026). "Jouw ontwerp" en "Afwerking" staan elk op
   * één regel en klappen open met een tik. Het ontwerp begint dicht; de kop
   * laat zien hoe ver de klant is (#toonOntwerpStatus). Bestellen zonder
   * ontwerp zet de rij open (#vraagOmOntwerp).
   *
   * Op het volledige scherm doen de rijen niets: daar zet de opmaak ze op
   * display: contents en ziet de ontwerptool eruit als voorheen.
   */
  #bindRijen() {
    if (!this.rijen) return;
    this.ontwerpRij = this.querySelector('[data-ontwerprij]');
    this.ontwerpStatus = this.querySelector('[data-ontwerpstatus]');

    [this.ontwerpRij, this.querySelector('[data-afwerkingrij]')].forEach((rij) => {
      const knop = rij && rij.querySelector('.flagcfg__rijkop');
      if (!knop) return;
      knop.addEventListener('click', () => this.#klapRij(rij, rij.classList.contains('is-dicht')));
    });

    // Formaat en doek (variant-main-picker.liquid): de hele regel is een
    // onzichtbare keuzelijst met de gekozen waarde ernaast. Het thema bouwt de
    // kiezer na een wissel opnieuw op met de juiste waarde, maar dat wacht op
    // een netwerkverzoek. Tot die tijd hier alvast de nieuwe waarde tonen.
    document.addEventListener('change', (e) => {
      const lijst = e.target;
      if (!(lijst instanceof HTMLSelectElement) || !lijst.closest('.variant-picker--rijen')) return;
      const optie = lijst.closest('.variant-option');
      const waarde = optie && optie.querySelector('.variant-option__waarde');
      if (waarde && waarde.textContent !== lijst.value) waarde.textContent = lijst.value;
    });

    this.#toonOntwerpStatus();
  }

  /** Klapt een keuzerij open of dicht. */
  #klapRij(rij, open) {
    if (!rij || rij.classList.contains('is-dicht') !== open) return;
    rij.classList.toggle('is-dicht', !open);
    const knop = rij.querySelector('.flagcfg__rijkop');
    if (knop) knop.setAttribute('aria-expanded', String(open));
    // Het doek was onzichtbaar. Opnieuw tekenen, voor het geval Safari het
    // beeld intussen heeft weggegooid (zie "Lege vlag op de iPhone").
    if (open && rij === this.ontwerpRij) this.#render(true);
  }

  /** Staat er op deze zijde iets om te drukken? Ook een bestand zonder voorbeeld telt. */
  #zijdeHeeftOntwerp(s) {
    return !!(s.img || this.#heeftLagen(s) || (s.fileInput && s.fileInput.files && s.fileInput.files.length));
  }

  /** De kop van de rij "Jouw ontwerp": nog niets, één zijde, of klaar. */
  #toonOntwerpStatus() {
    const chip = this.ontwerpStatus;
    if (!chip || !this.sides) return;
    const d = this.dataset;
    const klaar = this.sides.map((s) => this.#zijdeHeeftOntwerp(s));
    let tekst = d.tStatusLeeg || '';
    let stand = 'leeg';
    if (klaar.every(Boolean)) {
      tekst = (klaar.length > 1 ? d.tStatusBeide : d.tStatusKlaar) || '';
      stand = 'klaar';
    } else if (klaar.some(Boolean)) {
      const namen = [d.tStatusLinks || '', d.tStatusRechts || ''];
      const i = klaar[0] ? 0 : 1;
      tekst = namen[i] + ' · ' + namen[1 - i].toLowerCase() + ' ' + (d.tStatusZijdeLeeg || '');
      stand = 'half';
    }
    // Alleen schrijven wat verandert: dit loopt mee met elke tekenbeurt.
    if (chip.textContent !== tekst) chip.textContent = tekst;
    if (chip.dataset.stand !== stand) {
      chip.dataset.stand = stand;
      chip.classList.toggle('is-leeg', stand === 'leeg');
      chip.classList.toggle('is-klaar', stand !== 'leeg');
    }
  }

  /** Bestellen zonder ontwerp tegenhouden? Alleen met keuzerijen, en niet bij het bijwerken van de winkelmand. */
  #moetOntwerpVragen() {
    return !!(this.rijen && this.ontwerpRij && !this.bewerk && this.sides &&
      !this.sides.some((s) => this.#zijdeHeeftOntwerp(s)));
  }

  /** Rij open, in beeld, en even laten oplichten. */
  #vraagOmOntwerp() {
    const rij = this.ontwerpRij;
    if (!rij) return;
    this.#klapRij(rij, true);
    rij.classList.add('is-nodig');
    clearTimeout(this.nodigKlok);
    this.nodigKlok = setTimeout(() => rij.classList.remove('is-nodig'), 2600);
    const zacht = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    rij.scrollIntoView({ behavior: zacht ? 'smooth' : 'auto', block: 'start' });
  }

  #markeerFavoriet() {
    if (this.isBeach || this.isSpandoek) return;
    const tekst = this.dataset.tFavoriet;
    if (!tekst) return;

    const past = (waarde) => /150\s*[x×]\s*100/.test(waarde || '');

    const markeer = () => {
      // Knoppen: een tweede regel in de knop zelf.
      const knop = [...document.querySelectorAll('fieldset.variant-option input[type="radio"]')]
        .find((i) => past(i.value));
      if (knop) {
        const label = knop.closest('label');
        if (!label || label.classList.contains('flagcfg-favoriet')) return true;
        label.classList.add('flagcfg-favoriet');
        const vlag = document.createElement('span');
        vlag.className = 'flagcfg-favoriet__label';
        vlag.textContent = tekst;
        label.appendChild(vlag);
        return true;
      }

      // Keuzelijst: er is geen ruimte voor een tweede regel, dus komt het
      // achter de maat te staan — "150 × 100 cm — meest gekozen".
      const optie = [...document.querySelectorAll('.variant-option select option')]
        .find((o) => past(o.value));
      if (optie) {
        if (optie.dataset.favoriet === '1') return true;
        optie.dataset.favoriet = '1';
        optie.textContent = optie.textContent.trim() + ' — ' + tekst;
        return true;
      }

      return false;
    };

    // Het thema bouwt de variantkiezer bij elke wissel opnieuw op, dus blijven
    // we kijken; markeer() doet niets meer zodra de markering er al staat.
    const kijken = (doel) => {
      this.favorietWacht = new MutationObserver(() => markeer());
      this.favorietWacht.observe(doel, { childList: true, subtree: true });
    };
    // Niet alleen fieldset: als keuzelijst staat de variantkiezer in een div.
    const veld = () => document.querySelector('.variant-option');
    if (markeer() && veld()) {
      kijken(veld().parentElement);
      return;
    }
    let pogingen = 0;
    const klok = setInterval(() => {
      if (markeer()) {
        clearInterval(klok);
        if (veld()) kijken(veld().parentElement);
      } else if (++pogingen > 20) {
        clearInterval(klok);
      }
    }, 200);
  }

  /**
   * De doekkeuze (standaard 115 g of brandvertragend B1) is een bijzaak naast
   * het formaat, maar het thema geeft elke variantoptie hetzelfde gewicht: twee
   * knoppen over de volle breedte. En de uitleg erover stond onderaan de
   * configurator, honderden pixels lager dan de keuze zelf.
   *
   * Hier wordt allebei rechtgezet: het fieldset krijgt een klasse waarmee de
   * opties compacte pillen worden, en het ingeklapte uitlegblokje verhuist naar
   * datzelfde fieldset. Lukt het niet, dan blijft de uitleg verborgen — dat is
   * beter dan een losse alinea ver van waar hij over gaat.
   */
  #verzorgDoekkeuze() {
    const rauw = this.dataset.doekIndex;
    // let op: Number('') is 0, en dat zou de formaatkeuze treffen
    if (rauw === undefined || rauw === '') return;
    const index = Number(rauw);
    if (!Number.isInteger(index)) return;

    // Het blokje losmaken en zelf vasthouden. Het thema bouwt de variantkiezer
    // bij elke variantwissel opnieuw op; stond het blokje dan nog in het oude
    // fieldset, dan verdween het met dat fieldset mee uit de pagina.
    const hint = this.querySelector('[data-doekhint]');
    if (hint) {
      hint.remove();
      hint.hidden = false;
      this.doekHint = hint;
    }

    const inrichten = () => {
      const veld = document.querySelectorAll('fieldset.variant-option')[index];
      if (!veld) return false;
      veld.classList.add('flagcfg-doek');
      if (this.doekHint && this.doekHint.parentElement !== veld) {
        veld.appendChild(this.doekHint);
      }
      return true;
    };

    // Na een herbouw opnieuw aankleden. De waarnemer legt geen lus aan: zodra
    // klasse en blokje op hun plek staan doet inrichten() niets meer.
    const kijken = (doel) => {
      this.doekWacht = new MutationObserver(() => inrichten());
      this.doekWacht.observe(doel, { childList: true, subtree: true });
    };

    if (inrichten()) {
      kijken(document.querySelectorAll('fieldset.variant-option')[index].parentElement);
      return;
    }
    // de variantkiezer kan later renderen dan de configurator
    let pogingen = 0;
    const klok = setInterval(() => {
      if (inrichten()) {
        clearInterval(klok);
        kijken(document.querySelectorAll('fieldset.variant-option')[index].parentElement);
      } else if (++pogingen > 20) {
        clearInterval(klok);
      }
    }, 200);
  }

  // ---------- gekozen formaat ----------

  /** Leest "150 × 100 cm" uit een varianttitel, in welke taal dan ook. */
  #leesMaat(tekst) {
    const m = /(\d{2,4})\s*[x×]\s*(\d{2,4})\s*cm/i.exec(tekst || '');
    return m ? { w: Number(m[1]), h: Number(m[2]) } : null;
  }

  #maatVan(variantId) {
    return this.#leesMaat(this.maten && this.maten[String(variantId)]);
  }

  #zetCanvas() {
    const { view } = this.layout;
    this.canvas.width = view.w * DPR;
    this.canvas.height = view.h * DPR;
    this.ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }

  /** Ander formaat gekozen: tekenvlak herbouwen, ontwerp blijft staan. */
  #zetMaat(cm) {
    this.flagCm = cm;
    this.layout = this.isSpandoek ? spandoekLayout(cm.w / cm.h) : bouwLayout(cm.w / cm.h, this.dubbel, this.focus);
    this.#zetCanvas();
    this.sides.forEach((side, i) => {
      side.rect = this.layout.flags[i];
    });
    this.#render();
  }

  /** Volgt de maatkeuze van de variantkiezer boven het formulier. */
  #bindVariant() {
    const bron = this.closest('.shopify-section') || document;
    bron.addEventListener('variant:update', (e) => {
      const id = e.detail && e.detail.resource && e.detail.resource.id;
      if (!id) return;

      if (this.isBeach) {
        const vorm = this.vormen[String(id)];
        if (!vorm || vorm === this.vorm) return;
        this.vorm = vorm;
        this.#render();
        return;
      }

      const cm = this.#maatVan(id);
      if (!cm || (cm.w === this.flagCm.w && cm.h === this.flagCm.h)) return;
      this.#zetMaat(cm);
    });
  }

  /**
   * Vangnet onder #bindVariant: houdt de preview gelijk aan het verborgen
   * variantveld van het formulier.
   *
   * Aanleiding is bestelling #1009: daar stond in de bestelling 120 x 90 cm
   * terwijl de plaatsing en het controlebeeld op 300 x 200 cm waren berekend.
   * De klant betaalde dus voor een andere maat dan waarop zijn ontwerp was
   * ingedeeld. Het `variant:update`-signaal bereikt de configurator blijkbaar
   * niet in elke situatie — een tweede kiezer buiten de sectie, een lade, een
   * herstelde pagina. Wat er ook gebeurt, `input[name="id"]` in het formulier
   * is wat er daadwerkelijk besteld wordt, dus daar kijken we naar.
   *
   * Een waarnemer op het attribuut vangt de meeste thema's; sommige zetten
   * alleen de eigenschap, en daarvoor is de trage controle erachter bedoeld.
   */
  #bindFormulier() {
    const formulier = this.closest('form[action*="/cart/add"]') || this.closest('form');
    const veld = formulier && formulier.querySelector('input[name="id"]');
    if (!veld) return;

    this.variantVeld = veld;
    let laatste = veld.value;

    const gelijktrekken = () => {
      if (veld.value === laatste) return;
      laatste = veld.value;

      if (this.isBeach) {
        const vorm = this.vormen[String(veld.value)];
        if (vorm && vorm !== this.vorm) {
          this.vorm = vorm;
          this.#render();
        }
        return;
      }

      const cm = this.#maatVan(veld.value);
      if (!cm || (cm.w === this.flagCm.w && cm.h === this.flagCm.h)) return;
      this.#zetMaat(cm);
    };

    new MutationObserver(gelijktrekken).observe(veld, { attributes: true, attributeFilter: ['value'] });
    formulier.addEventListener('change', () => setTimeout(gelijktrekken, 0));
    this.maatWacht = setInterval(gelijktrekken, 500);
  }

  disconnectedCallback() {
    clearInterval(this.maatWacht);
    if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
    if (this.klaarZwevend) { this.klaarZwevend.remove(); this.klaarZwevend = null; }
    document.body.style.overflow = '';
    this.#sluitPreview();
    document.body.classList.remove('flagcfg-vol-open');
    this.#stapelVrij(false);
    if (this.doekWacht) this.doekWacht.disconnect();
    if (this.favorietWacht) this.favorietWacht.disconnect();
  }

  /** Landnamen in de taal van de bezoeker (valt terug op Engels) */
  #initNames() {
    try {
      this.names = new Intl.DisplayNames([this.dataset.locale || 'nl'], { type: 'region' });
    } catch (err) {
      this.names = null;
    }
  }

  /**
   * Volgorde van de kopjes in de databank. Thema's staan bewust vooraan: wie
   * een landenvlag zoekt typt de naam, maar wie niet weet dat er themavlaggen
   * zijn kan die alleen vinden door ze te zien staan.
   */
  /**
   * Volgorde van de kopjes in de databank. Thema's staan vooraan: wie een
   * landenvlag zoekt typt de naam, maar wie niet weet dat er themavlaggen
   * zijn kan die alleen vinden door ze te zien staan.
   *
   * Shopify vertelt ons uit welk land de klant bestelt. Koopt iemand vanuit
   * Belgie, dan komen de Belgische vlaggen voor de Nederlandse.
   */
  #groepvolgorde() {
    const land = (this.dataset.koperland || '').toUpperCase();
    if (land === 'BE') return ['thema', 'belgie', 'provincie', 'gemeente', 'land'];
    return ['thema', 'provincie', 'gemeente', 'belgie', 'land'];
  }

  #countryName(code, fallback) {
    if (this.names && /^[a-z]{2}$/i.test(code)) {
      try {
        const n = this.names.of(code.toUpperCase());
        if (n && n.toUpperCase() !== code.toUpperCase()) return n;
      } catch (err) {
        /* val terug op Engels */
      }
    }
    return fallback;
  }

  async #flags() {
    if (this.flagCache) return this.flagCache;
    try {
      const res = await fetch(this.dataset.flagList);
      const raw = await res.json();
      const taal = (this.dataset.locale || 'nl').slice(0, 2);
      this.flagCache = raw
        .map((f) => ({
          code: f.c,
          // Landnamen vertaalt de browser zelf; themavlaggen zijn geen land,
          // dus die staan vertaald in het bestand onder "t".
          label: (f.t && f.t[taal]) || this.#countryName(f.c, f.n),
          groep: f.g || 'land',
          zoek: f.z || '',
        }))
        .sort((a, b) => {
          const v = this.#groepvolgorde();
          const g = v.indexOf(a.groep) - v.indexOf(b.groep);
          return g || a.label.localeCompare(b.label, this.dataset.locale || 'nl');
        });
    } catch (err) {
      this.flagCache = [];
    }
    return this.flagCache;
  }

  #initSide(root, index) {
    const side = {
      root,
      index,
      rect: this.layout.flags[index],
      fileInput: root.querySelector('[data-file]'),
      posInput: root.querySelector('[data-pos]'),
      controls: root.querySelector('[data-controls]'),
      zoomInput: root.querySelector('[data-zoom]'),
      bgInput: root.querySelector('[data-bg]'),
      nameEl: root.querySelector('[data-filename]'),
      noteEl: root.querySelector('[data-note]'),
      libToggle: root.querySelector('[data-libtoggle]'),
      libPanel: root.querySelector('[data-libpanel]'),
      libSearch: root.querySelector('[data-libsearch]'),
      libToggleWb: root.querySelector('[data-libtoggle-wb]'),
      libBlok: root.querySelector('.flagcfg__library'),
      werkbalk: root.querySelector('[data-werkbalk]'),
      libGrid: root.querySelector('[data-libgrid]'),
      libEmpty: root.querySelector('[data-libempty]'),
      libValue: root.querySelector('[data-libvalue]'),
      qualityEl: root.querySelector('[data-quality]'),
      drukInput: root.querySelector('[data-druk]'),
      img: null,
      vector: false,
      ppi: 0,
      fileName: '',
      cx: 50,
      cy: 50,
      zoom: 1,
      stretch: false,
      bg: '#ffffff',
      objectUrl: null,
      // Tekstlagen. Elke laag: { id, tekst, font, kleur, grootte, hoek, cx, cy }
      // grootte is een percentage van de vlaghoogte, cx/cy percentages van het
      // doek — net als bij het ontwerp zelf, zodat alles meeschaalt met de maat.
      lagen: [],
      tekstLijst: root.querySelector('[data-tekstlijst]'),
      tekstKnop: root.querySelector('[data-tekstadd]'),
    };

    side.fileInput.addEventListener('change', () => this.#onFile(side));
    side.zoomInput.addEventListener('input', () => {
      side.zoom = Number(side.zoomInput.value) / 100;
      side.stretch = false;
      this.#render();
    });
    // Twee kleurkiezers voor dezelfde achtergrond: een bij de uploadknoppen
    // en een in de ontwerptool. Ze houden elkaar bij, anders staat de ene op
    // rood terwijl de vlag blauw is.
    side.bgInput2 = side.root.querySelector('[data-bg2]');
    const zetBg = (waarde, bron) => {
      side.bg = waarde;
      [side.bgInput, side.bgInput2].forEach((el) => {
        if (el && el !== bron) el.value = waarde;
      });
      // Een nieuwe achtergrond kan de tekst onleesbaar maken. Lagen die nog
      // op de standaardkleur staan schuiven mee; wie zelf een kleur koos
      // houdt die.
      side.lagen.forEach((l) => {
        if (l.type === 'kleur' || !l.kleur) return;
        if (l.kleur === '#000000' || l.kleur === '#ffffff') l.kleur = this.#leesbareKleur(side);
      });
      this.#tekenLagenLijst(side);
      this.#render();
    };
    // De wisknop alleen tonen als er iets te wissen valt. Als methode, want
    // een sjabloon zet de achtergrond ook — en die kwam hier niet langs, zodat
    // de knop uitbleef terwijl er wel degelijk een kleur stond.
    side.bgWis = side.root.querySelector('[data-bgwis]');
    const toonWis = () => this.#toonBgWis(side);

    side.bgInput.addEventListener('input', () => { zetBg(side.bgInput.value, side.bgInput); toonWis(); });
    if (side.bgInput2) {
      side.bgInput2.addEventListener('input', () => { zetBg(side.bgInput2.value, side.bgInput2); toonWis(); });
    }
    if (side.bgWis) {
      side.bgWis.addEventListener('click', () => {
        zetBg('#ffffff', null);
        toonWis();
      });
    }
    toonWis();

    // Alles wissen. Een bevestiging ertussen, want dit haalt in een tik weg
    // waar iemand tien minuten aan gewerkt heeft en er is geen stap terug.
    side.wisAlles = side.root.querySelector('[data-wisalles]');
    if (side.wisAlles) {
      side.wisAlles.addEventListener('click', () => {
        const vraag = this.dataset.tWisVraag || 'Alles wissen?';
        if (!window.confirm(vraag)) return;
        this.#wisAlles(side);
      });
    }
    // Kopieer naar de andere zijde, alleen bij dubbelzijdig (#vraagKopieer).
    side.kopieer = side.root.querySelector('[data-kopieer]');
    if (side.kopieer) {
      side.kopieer.addEventListener('click', () => this.#vraagKopieer(side));
    }
    this.#toonWisAlles(side);

    root.querySelectorAll('[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => this.#action(side, btn.dataset.act));
    });

    this.#bindLibrary(side);
    this.#bindTekst(side);
    return side;
  }

  // ---------- lagen: tekst en vormen ----------

  /**
   * De ontwerptool draait op de gewone vlaggen en sinds 8 september 2026 ook op
   * het spandoek. Daar wordt namelijk wél een drukbestand gemaakt op de echte
   * maat, dus wat de klant ziet is wat er gedrukt wordt.
   *
   * Bij beachflags blijft hij uit, en dat is geen kwestie van tijd: het doek is
   * gebogen en de printmaat in deze code (72 x 260 cm) is een schatting, geen
   * template van de fabriek. Er wordt daarom bewust geen drukbestand gemaakt.
   * Een ontwerp tonen dat zo niet gedrukt wordt is erger dan het niet
   * aanbieden. Zet dit niet aan zonder echte fabrieksmaten.
   */
  get tekstToegestaan() {
    return !this.isBeach;
  }

  #bindTekst(side) {
    // Op producten zonder ontwerptool verdwijnt het hele blok, niet alleen de
    // knop — anders staat er een uitklapper die niets doet.
    if (!this.tekstToegestaan) {
      const blok = side.root.querySelector('.flagcfg__tekstblok');
      if (blok) blok.hidden = true;
      return;
    }
    if (side.tekstKnop) side.tekstKnop.addEventListener('click', () => this.#voegTekstToe(side));

    const eigen = side.root.querySelector('[data-eigenfile]');
    if (eigen) {
      eigen.addEventListener('change', () => {
        const bestand = eigen.files && eigen.files[0];
        if (bestand) this.#voegEigenToe(side, bestand);
        // leegmaken, anders kun je hetzelfde bestand niet twee keer kiezen
        eigen.value = '';
      });
    }


    const opener = side.root.querySelector('[data-tekstopen]');
    const paneel = side.root.querySelector('[data-tekstpaneel]');

    // Bij een dubbelzijdige vlag heeft alleen de laatste kant een openknop —
    // de ontwerptool bewerkt beide kanten tegelijk. De andere kant heeft dus
    // wel een paneel maar geen knop, en dat is geen reden om hieronder te
    // stoppen: de symbolenknop van die kant moet gewoon werken.
    if (opener && paneel) {
      opener.addEventListener('click', () => {
        paneel.hidden = false;
        opener.setAttribute('aria-expanded', 'true');
        opener.classList.add('is-open');
        // Bewust geen tekstlaag vooraf. Wie de tool opent hoort een lege vlag
        // te zien en zelf te kiezen wat erop komt; een "Jouw tekst" die er al
        // staat moet eerst weer weg voordat iemand kan beginnen.
        this.#laadSymbolen(side);
        this.#laadSjablonen(side);
        this.#volScherm(true);
      });
    }

    this.#bindSymboolKnop(side);
  }

  /** De knop Symbolen in de werkbalk vouwt de bibliotheek open en dicht. */
  #bindSymboolKnop(side) {
    const knop = side.root.querySelector('[data-symboolopen]');
    const blok = side.root.querySelector('[data-symboolblok]');
    if (!knop || !blok) return;
    // Het blok begint dicht; de kiezer wordt pas gevuld als de bibliotheek
    // binnen is, en die komt bij het openen van de ontwerptool.
    knop.addEventListener('click', () => {
      const open = blok.hidden;
      blok.hidden = !open;
      knop.setAttribute('aria-expanded', String(open));
      // Maar één paneel tegelijk: gaat Symbolen open, dan gaan de vlaggen dicht.
      // Anders stonden beide knoppen zwart en twee lijsten onder elkaar.
      if (open && side.libPanel && !side.libPanel.hidden) {
        side.libPanel.hidden = true;
        this.#libAria(side, false);
      }
    });
  }

  /**
   * Zet de configurator over het hele scherm, of weer terug.
   *
   * Ontwerpen op een gewone productpagina betekent voortdurend heen en weer
   * schuiven: een knop onderin indrukken en dan naar boven om te kijken wat
   * er gebeurde. In dit scherm blijft de vlag bovenaan staan.
   *
   * Bewust geen aparte pagina: dan zouden ontwerp, maat en afwerking mee
   * moeten reizen en weer terug naar de winkelwagen. Dit blijft hetzelfde
   * formulier, dus er kan niets zoekraken.
   */
  /**
   * Zet --flagcfg-balkhoogte op de werkelijke hoogte van de balk bovenin, plus
   * een paar pixels lucht. De vlag hangt daaronder vast tijdens het scrollen.
   *
   * Waarom gemeten en niet vastgezet: de balkhoogte hangt af van het
   * lettertype, de taal en de lengte van de titel. Een vast getal klopt dan
   * precies zolang niemand er iets aan verandert.
   */
  #meetBalk() {
    const balk = this.querySelector('[data-volbalk]');
    if (!balk) return;
    // De keuze links/rechts/beide staat vlak onder de titel, naast de zwevende
    // Klaar-knop. Houd rechts zoveel vrij dat hij niet onder die knop schuift.
    // Gemeten, want "Klaar" is in elke taal anders breed.
    const keuze = balk.querySelector('[data-zijdekeuze]');
    if (keuze && !keuze.hidden && this.klaarZwevend) {
      const k = this.klaarZwevend.getBoundingClientRect();
      if (k.width) {
        const b = balk.getBoundingClientRect();
        const inhoudRechts = b.right - (parseFloat(getComputedStyle(balk).paddingRight) || 0);
        keuze.style.marginInlineEnd = Math.max(0, Math.ceil(inhoudRechts - k.left + 8)) + 'px';
      }
    }
    const h = balk.getBoundingClientRect().height;
    if (!h) return;
    this.style.setProperty('--flagcfg-balkhoogte', Math.round(h + 8) + 'px');
  }

  /**
   * Bepaalt of een element een eigen stapelcontext maakt. Dat is de reden dat
   * een z-index van 9999 soms toch verliest van een z-index van 8: die twee
   * getallen worden alleen met elkaar vergeleken als ze in dezelfde context
   * zitten. Zit de tool in een eigen context, dan telt alleen de plek van die
   * context zelf mee — hoe hoog het getal daarbinnen ook staat.
   */
  #maaktStapel(el) {
    const s = getComputedStyle(el);
    // sticky en fixed maken er altijd een, ook zonder z-index. Precies dit
    // deed de productkolom, en dat was de oorzaak.
    if (s.position === 'sticky' || s.position === 'fixed') return true;
    if (s.zIndex !== 'auto' && s.position !== 'static') return true;
    if (s.transform !== 'none' || s.filter !== 'none' || s.perspective !== 'none') return true;
    if (s.opacity !== '1' || s.isolation === 'isolate' || s.mixBlendMode !== 'normal') return true;
    if (s.willChange !== 'auto') return true;
    if (s.contain && /paint|layout|strict|content/.test(s.contain)) return true;
    if (s.containerType && s.containerType !== 'normal') return true;
    if (s.viewTransitionName && s.viewTransitionName !== 'none') return true;
    return false;
  }

  /**
   * Zorgt dat het volledige scherm ook echt bovenop ligt.
   *
   * De ontwerptool staat in de productkolom, en die kolom is `position: sticky`
   * zodat hij meeloopt bij het scrollen. Een sticky element maakt altijd een
   * eigen stapelcontext. Alles daarbinnen stapelt daardoor als een geheel, en
   * de menubalk bovenaan de winkel (z-index 8) kwam zo over het ontwerpscherm
   * heen te liggen. Zichtbaar zodra de menubalk vastgeplakt stond — vandaar dat
   * het de ene keer wel gebeurde en de andere keer niet.
   *
   * De voorouders die zo'n context maken krijgen zolang de tool openstaat zelf
   * een hoge z-index. Dat verschuift niets aan de opmaak en gaat bij het
   * sluiten exact terug naar wat er stond. Bewust niet het element verhuizen
   * naar de body: het staat in het bestelformulier, en daar mag niets tussen
   * de vlag en de bestelling in komen.
   */
  #stapelVrij(aan) {
    if (aan) {
      if (this.stapelHerstel) return;
      this.stapelHerstel = [];
      let p = this.parentElement;
      while (p && p !== document.body) {
        if (this.#maaktStapel(p)) {
          this.stapelHerstel.push({ el: p, z: p.style.zIndex, pos: p.style.position });
          // Zonder positie doet z-index niets; relatief zonder verschuiving
          // verandert de opmaak niet.
          if (getComputedStyle(p).position === 'static') p.style.position = 'relative';
          p.style.zIndex = '9999';
        }
        p = p.parentElement;
      }
    } else if (this.stapelHerstel) {
      this.stapelHerstel.forEach(({ el, z, pos }) => {
        el.style.zIndex = z;
        el.style.position = pos;
      });
      this.stapelHerstel = null;
    }
  }

  /**
   * Tekent een vlagzijde op een eigen doek, op elk gewenst formaat.
   *
   * Dezelfde som als het drukbestand, alleen kleiner: achtergrondkleur, de
   * geuploade afbeelding op zijn plek, en daarbovenop de lagen. Bewust een
   * aparte methode en niet meeliften op #maakDrukbestand — dat pad gaat naar
   * de drukker en daar hoort geen schermfunctie tussen te komen.
   */
  #previewDoek(side, breed) {
    const r = side.rect;
    const hoog = Math.max(1, Math.round((breed * r.h) / r.w));
    const doek = document.createElement('canvas');
    doek.width = breed;
    doek.height = hoog;
    const d = doek.getContext('2d');
    d.imageSmoothingQuality = 'high';
    d.fillStyle = side.bg || '#ffffff';
    d.fillRect(0, 0, breed, hoog);
    if (side.img) {
      this.#clamp(side);
      const g = this.#geometry(side);
      const schaal = breed / r.w;
      d.drawImage(side.img, (g.x - r.x) * schaal, (g.y - r.y) * schaal, g.w * schaal, g.h * schaal);
    }
    this.#tekenLagen(d, side, { x: 0, y: 0, w: breed, h: hoog });
    return doek;
  }

  /** Valt er iets te bekijken? Een leeg wit vlak vergroten heeft geen zin. */
  #ietsTeZien() {
    return this.sides.some((s) => s.img || this.#heeftLagen(s));
  }

  #toonPreviewKnop() {
    const knop = this.querySelector('[data-previewknop]');
    if (!knop) return;
    // Beachflag heeft geen rechthoekig doek en geen ontwerptool; daar zou de
    // vergroting een andere vorm tonen dan wat er gedrukt wordt.
    const verberg = this.isBeach || !this.#ietsTeZien();
    if (knop.hidden !== verberg) knop.hidden = verberg;
  }

  /**
   * Toont de vlag zo groot als het scherm toelaat.
   *
   * Het venster hangt rechtstreeks aan de pagina met eigen stijlen, net als de
   * Klaar-knop. Reden: de opmaak zit in een gebundeld themabestand dat niet
   * altijd meteen ververst, en een vergroting die half opgemaakt over het
   * scherm hangt is erger dan geen vergroting. Zo hangt het nergens van af.
   */
  #openPreview() {
    if (this.previewVenster) return;
    if (!this.#ietsTeZien()) return;

    const breedScherm = window.innerWidth > window.innerHeight && this.sides.length > 1;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Ruimte per zijde: bij twee zijden naast elkaar krijgt elke zijde de helft.
    const perZijde = breedScherm ? window.innerWidth / 2 : window.innerWidth;
    const breed = Math.max(900, Math.min(2400, Math.round(perZijde * dpr)));

    const venster = document.createElement('div');
    venster.setAttribute('data-flagcfg-preview', '');
    venster.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:2147483647',
      'background:rgb(18 19 21 / .93)', 'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center', 'gap:14px',
      'padding:56px 16px 24px', 'box-sizing:border-box', 'overflow:auto',
    ].join(';');

    const rij = document.createElement('div');
    rij.style.cssText = [
      'display:flex', 'gap:18px',
      'align-items:center', 'justify-content:center',
      'flex-direction:' + (breedScherm ? 'row' : 'column'),
      'width:100%', 'height:100%',
    ].join(';');

    const dubbel = this.sides.length > 1;
    this.sides.forEach((side) => {
      const vak = document.createElement('div');
      // Naast elkaar krijgt elke kant hoogstens de helft, anders wipt de tweede
      // vlag van het scherm af.
      vak.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:7px;min-width:0;flex:0 1 auto;max-width:' +
        (breedScherm ? 'calc(50% - 9px)' : '100%');

      if (dubbel) {
        const titel = side.root.querySelector('.flagcfg__sidetitle');
        if (titel && titel.textContent.trim()) {
          const t = document.createElement('span');
          t.textContent = titel.textContent.trim();
          t.style.cssText = 'color:rgb(255 255 255 / .72);font:600 11px/1 system-ui,sans-serif;letter-spacing:.09em;text-transform:uppercase';
          vak.appendChild(t);
        }
      }

      const doek = this.#previewDoek(side, breed);
      // De hoogte per zijde beperken zodat twee kanten onder elkaar allebei
      // in beeld passen, ook op een telefoon.
      const hoogteRuimte = dubbel && !breedScherm ? '36vh' : '74vh';
      doek.style.cssText = [
        'display:block', 'max-width:100%', 'max-height:' + hoogteRuimte,
        'width:auto', 'height:auto', 'border-radius:3px',
        'box-shadow:0 8px 30px rgb(0 0 0 / .5)', 'background:#fff',
      ].join(';');
      vak.appendChild(doek);
      rij.appendChild(vak);
    });

    const sluit = document.createElement('button');
    sluit.type = 'button';
    sluit.textContent = this.dataset.tPreviewSluit || 'Sluiten';
    sluit.style.cssText = [
      'position:fixed', 'top:12px', 'right:12px', 'z-index:1',
      'min-height:44px', 'padding:10px 22px', 'border:none', 'border-radius:999px',
      'font:600 15px/1 system-ui, sans-serif', 'cursor:pointer',
      'background:#fff', 'color:#1c1c1c', 'box-shadow:0 2px 10px rgb(0 0 0 / .3)',
    ].join(';');
    sluit.addEventListener('click', () => this.#sluitPreview());

    venster.append(rij, sluit);
    // Klikken naast de vlag sluit ook; op de vlag zelf niet, anders sluit het
    // venster zodra iemand het beeld aanraakt om te zoomen.
    venster.addEventListener('click', (e) => {
      if (e.target === venster || e.target === rij) this.#sluitPreview();
    });

    // De zwevende Klaar-knop staat op dezelfde hoogte als de sluitknop en zou
    // er doorheen prikken.
    if (this.klaarZwevend) this.klaarZwevend.style.display = 'none';

    this.previewScrollTerug = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.appendChild(venster);
    this.previewVenster = venster;
    sluit.focus();
  }

  #sluitPreview() {
    if (!this.previewVenster) return;
    // De vergroting is tot 2400 px breed per zijde. Vrijgeven, anders blijft dat
    // geheugen op een iPhone nog een tijd bezet.
    this.previewVenster.querySelectorAll('canvas').forEach((c) => {
      c.width = 0;
      c.height = 0;
    });
    this.previewVenster.remove();
    this.previewVenster = null;
    document.body.style.overflow = this.previewScrollTerug || '';
    if (this.klaarZwevend) this.klaarZwevend.style.display = '';
  }

  #bindPreview() {
    const knop = this.querySelector('[data-previewknop]');
    if (!knop) return;
    knop.addEventListener('click', () => this.#openPreview());
  }

  /** Kan deze vlag in de focusstand? Alleen een gewone dubbelzijdige vlag. */
  #kanFocussen() {
    return !!(this.dubbel && !this.isSpandoek && !this.isBeach && this.sides && this.sides.length === 2);
  }

  /**
   * Zet de focusstand: een zijde groot in beeld (0 = links, 1 = rechts), of
   * beide zijden (null).
   *
   * Het ontwerp zelf verandert niet. Alles op de vlag staat in procenten van het
   * vlagvlak; alleen de indeling van het canvas wordt anders. Slepen, knijpen,
   * uitlijnen, hulplijnen en de schuifregelaars rekenen in die procenten en
   * werken dus op elk formaat hetzelfde. Een aanraking wordt omgerekend met de
   * werkelijke maat van het canvas op het scherm (naarVlak in #bindGestures),
   * dus ook dat schaalt vanzelf mee.
   */
  #zetFocus(focus) {
    if (!this.#kanFocussen() || !this.classList.contains('is-vol')) return;
    const nieuw = focus === 0 || focus === 1 ? focus : null;
    if (this.focus === nieuw) return;
    this.focus = nieuw;
    this.#zetMaat(this.flagCm);
    this.#toonFocus();
    // De bediening van de gekozen zijde van boven laten beginnen.
    this.scrollTop = 0;
  }

  /** Knoppen en panelen gelijk laten lopen met de focusstand. */
  #toonFocus() {
    const focus = this.#kanFocussen() && this.classList.contains('is-vol') ? this.focus : null;
    const inFocus = focus === 0 || focus === 1;
    this.classList.toggle('is-focus', inFocus);
    this.querySelectorAll('[data-focusknop]').forEach((knop) => {
      const waarde = knop.getAttribute('data-focusknop');
      const actief = waarde === 'beide' ? !inFocus : inFocus && Number(waarde) === focus;
      knop.setAttribute('aria-pressed', actief ? 'true' : 'false');
    });
    // Alleen de bediening van de zijde die in beeld is. Anders zet je een tekst
    // op een zijde die je op dat moment niet ziet. Rechtstreeks op het element,
    // net als de rest van het volledige scherm (zie #volScherm).
    if (this.sides) {
      this.sides.forEach((s, i) => {
        s.root.style.display = inFocus && i !== focus ? 'none' : '';
      });
    }
    // De vlag onder de balk kan een andere hoogte krijgen.
    this.#meetBalk();
  }

  #bindFocus() {
    this.querySelectorAll('[data-focusknop]').forEach((knop) => {
      knop.addEventListener('click', () => {
        const waarde = knop.getAttribute('data-focusknop');
        this.#zetFocus(waarde === 'beide' ? null : Number(waarde));
      });
    });
  }

  #volScherm(aan) {
    if (this.classList.contains('is-vol') === aan) return;
    this.classList.toggle('is-vol', aan);
    // Wie na de ontwerptool op Klaar tikt, hoort zijn ontwerp terug te zien en
    // geen dichte rij.
    if (aan && this.ontwerpRij) this.#klapRij(this.ontwerpRij, true);

    // Merkteken voor de rest van de winkel: zolang dit erop staat is het
    // ontwerpscherm open. De WhatsApp-knop verstopt zich daarop.
    document.body.classList.toggle('flagcfg-vol-open', aan);
    this.#stapelVrij(aan);

    this.sides.forEach((side) => this.#libVerhuis(side, aan));

    // De opmaak staat in een {% stylesheet %}-blok, en dat bundelt Shopify in
    // een themabestand dat niet altijd meteen ververst. Dan draait de bezoeker
    // met nieuwe code en oude opmaak, komt het volledige scherm niet op en
    // staat hij vast tussen de ontwerpknoppen zonder Klaar-knop. Wat het
    // scherm echt moet doen, zetten we daarom rechtstreeks op het element.
    const stijl = this.style;
    if (aan) {
      stijl.position = 'fixed';
      stijl.inset = '0';
      stijl.zIndex = '9999';
      stijl.margin = '0';
      stijl.overflowY = 'auto';
      stijl.background = 'var(--color-background, #fff)';
    } else {
      ['position', 'inset', 'zIndex', 'margin', 'overflowY', 'background'].forEach((n) => {
        stijl[n] = '';
      });
    }

    // Zelfde reden: de panelen mogen buiten dit scherm niet blijven staan.
    this.sides.forEach((s) => {
      const paneel = s.root.querySelector('[data-tekstpaneel]');
      if (paneel) paneel.style.display = aan ? '' : 'none';
    });

    this.#zwevendeKlaar(aan);
    this.#doekAanwijzer();

    // Ook de balk met de Klaar-knop krijgt zijn stand rechtstreeks mee, zodat
    // hij er staat ook als de gebundelde opmaak nog niet ververst is.
    const balk = this.querySelector('[data-volbalk]');
    if (balk) {
      balk.hidden = !aan;
      if (aan) {
        balk.style.display = 'flex';
        balk.style.position = 'sticky';
        balk.style.top = '0';
        balk.style.zIndex = '3';
        balk.style.alignItems = 'center';
        balk.style.justifyContent = 'space-between';
        balk.style.background = 'var(--color-background, #fff)';
        // Met de keuze links/rechts/beide erin minder lucht boven, onder en
        // tussen titel en keuze. Op een telefoon nam de balk anders bijna een
        // vijfde van het scherm in (Jos, 11 sep).
        const metKeuze = this.#kanFocussen();
        balk.style.padding = metKeuze ? '0.5rem 1rem' : '0.7rem 1rem';
        balk.style.rowGap = metKeuze ? '0.3rem' : '';
        balk.style.flexWrap = 'wrap';
      } else {
        balk.removeAttribute('style');
      }
      // De keuze links/rechts/beide staat in deze balk, zodat hij in beeld
      // blijft terwijl de klant door de bediening schuift.
      const keuze = balk.querySelector('[data-zijdekeuze]');
      if (keuze) keuze.hidden = !(aan && this.#kanFocussen());
    }

    // De vlag plakt vast onder deze balk. Meet hoe hoog hij werkelijk is in
    // plaats van een vast getal aan te houden: bij een langere titel of een
    // ander lettertype schoof de vlag anders onder de balk en werd de
    // bovenkant afgesneden.
    if (aan) {
      this.#meetBalk();
      // Draaien van de telefoon of het venster verslepen verandert de hoogte.
      this.balkMeter = () => this.#meetBalk();
      window.addEventListener('resize', this.balkMeter);
      window.addEventListener('orientationchange', this.balkMeter);
    } else {
      if (this.balkMeter) {
        window.removeEventListener('resize', this.balkMeter);
        window.removeEventListener('orientationchange', this.balkMeter);
        this.balkMeter = null;
      }
      this.style.removeProperty('--flagcfg-balkhoogte');
    }

    if (aan) {
      // Bij een dubbelzijdige vlag horen beide kanten open te staan. De knop
      // om een kant te openen is in dit scherm verborgen, dus wie hier binnen
      // komt via de voorkant zou de achterkant nooit meer kunnen bereiken.
      this.sides.forEach((s) => {
        const paneel = s.root.querySelector('[data-tekstpaneel]');
        const opener = s.root.querySelector('[data-tekstopen]');
        if (paneel) paneel.hidden = false;
        if (opener) {
          opener.setAttribute('aria-expanded', 'true');
          opener.classList.add('is-open');
        }
        this.#laadSymbolen(s);
        this.#laadSjablonen(s);
      });
    }

    if (!aan) {
      // Buiten de ontwerptool geen gekozen element, dus geen kader over de vlag.
      this.sides.forEach((s) => { s.gekozenLaag = null; });
      // Terug naar de hoofdpagina: de bediening van de ontwerptool hoort daar
      // niet thuis. Het ontwerp zelf blijft staan — dat zit in de lagen, niet
      // in deze panelen — dus de klant ziet zijn vlag gewoon.
      this.sides.forEach((s) => {
        const paneel = s.root.querySelector('[data-tekstpaneel]');
        const opener = s.root.querySelector('[data-tekstopen]');
        const symBlok = s.root.querySelector('[data-symboolblok]');
        const symKnop = s.root.querySelector('[data-symboolopen]');
        if (paneel) paneel.hidden = true;
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          opener.classList.remove('is-open');
        }
        if (symBlok) symBlok.hidden = true;
        if (symKnop) symKnop.setAttribute('aria-expanded', 'false');
      });
    }

    // Achtergrond niet mee laten schuiven; op iOS scrollt de pagina anders
    // door onder het venster.
    if (aan) {
      this.scrollTerug = window.scrollY;
      document.body.style.overflow = 'hidden';
      // Twee keer terug naar boven: meteen, en nog eens na een beeldopbouw.
      // De eerste keer staat de nieuwe hoogte soms nog niet vast, en dan
      // begint het scherm halverwege — met de balk buiten beeld.
      this.scrollTop = 0;
      requestAnimationFrame(() => {
        this.scrollTop = 0;
      });
    } else {
      document.body.style.overflow = '';
      if (typeof this.scrollTerug === 'number') window.scrollTo(0, this.scrollTerug);
    }

    // Een dubbelzijdige vlag begint in het ontwerpscherm op de linkerzijde,
    // groot in beeld. Bij het sluiten terug naar beide zijden: buiten dit
    // scherm hoort de vlag er helemaal te staan.
    if (this.#kanFocussen()) {
      this.focus = aan ? 0 : null;
      this.layout = bouwLayout(this.flagCm.w / this.flagCm.h, this.dubbel, this.focus);
      this.sides.forEach((side, i) => {
        side.rect = this.layout.flags[i];
      });
    }
    this.#toonFocus();

    // Het doek is nu anders groot; opnieuw tekenen zodat het scherp blijft.
    this.#zetCanvas();
    this.#render();
  }

  /**
   * Zwevende Klaar-knop, rechtstreeks aan de pagina gehangen en niet aan de
   * configurator. Die knop is de enige uitweg uit het ontwerpscherm; hij mag
   * dus nergens van afhangen — niet van de gebundelde opmaak, niet van waar
   * de bezoeker gescrold heeft, en niet van de plek van het element in de
   * pagina. Alles staat rechtstreeks op de knop.
   */
  #zwevendeKlaar(aan) {
    if (aan) {
      if (this.klaarZwevend) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = this.dataset.tVolKlaar || 'Klaar';
      // Versie in de zweefhulp, zodat bij twijfel te zien is of de nieuwste
      // code draait. Verdwijnt zodra dit uitgetest is.
      b.title = 'Ontwerptool ' + FLAGCFG_VERSIE;
      b.setAttribute('data-flagcfg-klaar', '');
      b.style.cssText = [
        'position:fixed', 'top:12px', 'right:12px', 'z-index:2147483647',
        'min-height:44px', 'padding:10px 22px', 'border:none', 'border-radius:999px',
        'font:600 15px/1 system-ui, sans-serif', 'cursor:pointer',
        'background:#1c1c1c', 'color:#fff', 'box-shadow:0 2px 10px rgb(0 0 0 / .25)',
      ].join(';');
      b.addEventListener('click', () => this.#volScherm(false));
      document.body.appendChild(b);
      this.klaarZwevend = b;
    } else if (this.klaarZwevend) {
      this.klaarZwevend.remove();
      this.klaarZwevend = null;
    }
  }

  #bindVolScherm() {
    this.querySelectorAll('[data-volsluit]').forEach((b) =>
      b.addEventListener('click', () => this.#volScherm(false))
    );
    // Ontsnappen met Escape hoort te werken; dat verwacht iedereen.
    this.escHandler = (e) => {
      if (e.key !== 'Escape') return;
      // Staat de vergroting open, dan sluit Escape eerst die - anders klapt het
      // hele ontwerpscherm dicht terwijl de klant alleen het beeld wegklikte.
      if (this.previewVenster) { this.#sluitPreview(); return; }
      if (this.classList.contains('is-vol')) this.#volScherm(false);
    };
    document.addEventListener('keydown', this.escHandler);
  }

  /**
   * Haalt de symbolenbibliotheek pas op als iemand de ontwerptool opent.
   * Het bestand is een paar honderd kilobyte; dat wil je niet meesturen met
   * elke productpagina, waar de meeste bezoekers gewoon een bestand uploaden.
   */
  #laadSymbolen(side) {
    if (window.FLAGCFG_KLEUR_INDEX) {
      this.#bouwSymboolKiezer(side);
      return;
    }
    // Alleen het overzicht van categorieen; dat is twee kilobyte. De symbolen
    // zelf komen pas als de klant een categorie kiest.
    const url = this.dataset.kleurIndexUrl;
    if (!url) return;

    if (!FlagConfigurator.symbolenBelofte) {
      FlagConfigurator.symbolenBelofte = new Promise((klaar, mislukt) => {
        const s = document.createElement('script');
        s.src = url;
        s.onload = klaar;
        s.onerror = mislukt;
        document.head.appendChild(s);
      });
    }
    FlagConfigurator.symbolenBelofte
      .then(() => this.#bouwSymboolKiezer(side))
      .catch(() => {
        // Lukt het laden niet, dan blijft de rest gewoon werken. Beter een
        // tool zonder symbolen dan een kapotte tool.
        const knop = side.root.querySelector('[data-symboolopen]');
        if (knop) knop.hidden = true;
      });
  }

  /**
   * Zwart of wit, afhankelijk van waar de laag op komt te staan. Zonder dit
   * krijgt de eerste laag een vaste kleur, en dat is op de helft van de
   * vlaggen onzichtbaar — wit op een wit doek lijkt op een kapotte knop.
   */
  #leesbareKleur(side) {
    const hex = (side.bg || '#ffffff').replace('#', '');
    if (hex.length !== 6) return '#000000';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    // waargenomen helderheid: groen telt zwaarder dan blauw
    const licht = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return licht > 0.55 ? '#000000' : '#ffffff';
  }

  /**
   * Staat er iets op deze zijde dat gedrukt moet worden? Een lege tekstlaag
   * telt niet mee, een vorm altijd wel — die heeft geen inhoud nodig.
   */
  #heeftLagen(side) {
    return !!(side.lagen && side.lagen.some((l) => l.type !== 'tekst' || l.tekst));
  }

  /** Nieuwe lagen zakken iets naar beneden, zodat ze niet op elkaar landen. */
  #vrijePlek(side) {
    return { cx: 50, cy: side.lagen.length ? Math.min(85, 50 + side.lagen.length * 12) : 50 };
  }

  #nieuwId() {
    return 'l' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  #voegTekstToe(side) {
    const plek = this.#vrijePlek(side);
    side.lagen.unshift({
      id: this.#nieuwId(),
      type: 'tekst',
      tekst: this.dataset.tTekstVoorbeeld || 'Jouw tekst',
      font: 'Arial, Helvetica, sans-serif',
      kleur: this.#leesbareKleur(side),
      grootte: 14,
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
    });
    this.#tekenLagenLijst(side);
    this.#render();
  }

  #verwijderLaag(side, id) {
    side.lagen = side.lagen.filter((l) => l.id !== id);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /** Rij knopjes met een voorbeeldje van elke vorm, getekend met dezelfde som. */
  /**
   * Bouwt de symbolenkiezer: een keuzelijst met categorieen en een raster met
   * de symbolen van de gekozen categorie. De symbolen van een categorie
   * worden pas opgehaald zodra iemand die categorie kiest.
   */
  #bouwSymboolKiezer(side) {
    const blok = side.root.querySelector('[data-symboolblok]');
    const keuze = side.root.querySelector('[data-symboolcat]');
    const raster = side.root.querySelector('[data-symboolraster]');
    if (!blok || !keuze || !raster) return;
    if (side.symboolKlaar) return;
    side.symboolKlaar = true;

    const index = window.FLAGCFG_KLEUR_INDEX;
    const knop = side.root.querySelector('[data-symboolopen]');
    if (!index || !index.length) {
      // Geen bibliotheek: dan ook geen knop die niets doet.
      if (knop) knop.hidden = true;
      return;
    }

    const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    keuze.textContent = '';
    index.forEach((c, i) => {
      const o = document.createElement('option');
      o.value = String(i);
      o.textContent = c.namen[taal] || c.namen.nl;
      keuze.appendChild(o);
    });
    keuze.value = '0';

    const knopVoor = (sym) => {
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'flagcfg__vormknop flagcfg__vormknop--kleur';
      knop.title = sym.t;
      knop.setAttribute('aria-label', sym.t);
      knop.innerHTML = sym.s;
      const svg = knop.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
      }
      knop.addEventListener('click', () => {
        if (sym.vorm) this.#voegVormToe(side, sym);
        else this.#voegKleurToe(side, sym);
      });
      return knop;
    };

    const vul = () => {
      const cat = index[Number(keuze.value) || 0];
      const klaar = window.FLAGCFG_KLEUR && window.FLAGCFG_KLEUR[cat.id];
      if (!klaar) {
        raster.textContent = '';
        const p = document.createElement('p');
        p.className = 'flagcfg__symboolladen';
        p.textContent = this.dataset.tSymboolLaden || 'Even geduld…';
        raster.appendChild(p);
        this.#laadKleurCategorie(cat, () => {
          // alleen bijwerken als de klant nog naar deze categorie kijkt
          const nu = index[Number(keuze.value) || 0];
          if (nu && nu.id === cat.id) vul();
        });
        return;
      }
      raster.textContent = '';
      window.FLAGCFG_KLEUR[cat.id].forEach((sym) => raster.appendChild(knopVoor(sym)));
    };

    keuze.addEventListener('change', vul);
    vul();
  }

  /** Haalt de symbolen van een kleurencategorie op; elke categorie apart. */
  #laadKleurCategorie(cat, klaar) {
    window.FLAGCFG_KLEUR = window.FLAGCFG_KLEUR || {};
    if (window.FLAGCFG_KLEUR[cat.id]) { klaar(); return; }

    FlagConfigurator.kleurBeloften = FlagConfigurator.kleurBeloften || {};
    if (!FlagConfigurator.kleurBeloften[cat.id]) {
      let urls = {};
      try {
        urls = JSON.parse(this.dataset.kleurUrls || '{}');
      } catch (err) {
        urls = {};
      }
      const url = urls[cat.id];
      if (!url) return;
      FlagConfigurator.kleurBeloften[cat.id] = new Promise((ok, fout) => {
        const s = document.createElement('script');
        s.src = url;
        s.onload = ok;
        s.onerror = fout;
        document.head.appendChild(s);
      });
    }
    FlagConfigurator.kleurBeloften[cat.id].then(klaar).catch(() => klaar());
  }

  /**
   * Kleurensymbolen hebben kleurverlopen en tientallen paden; die kun je niet
   * als pad natekenen. Ze worden daarom als afbeelding geladen en getekend.
   * Dat werkt ook op drukformaat: de browser rekent de vector uit op de maat
   * waarop hij getekend wordt, dus hij blijft scherp.
   *
   * Laden gaat niet meteen, dus na afloop opnieuw tekenen. Zonder dat blijft
   * het symbool onzichtbaar tot de klant toevallig iets anders aanraakt.
   */
  #laadPlaatje(l) {
    if (l.plaatje || l.plaatjeBezig || l.plaatjeStuk) return;
    l.plaatjeBezig = true;
    const img = new Image();
    img.onload = () => {
      l.plaatje = img;
      l.plaatjeBezig = false;
      this.#render();
    };
    img.onerror = () => {
      l.plaatjeBezig = false;
      l.plaatjeStuk = true;
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(l.svg);
  }

  /**
   * Voegt een eigen afbeelding toe als element: verslepen en schalen zoals
   * een symbool, en je kunt er meerdere op een vlag zetten.
   *
   * Anders dan het uploadveld op de productpagina, dat er precies een neemt
   * en die over de hele vlag legt. Dat blijft zoals het was — een landenvlag
   * wil je nu eenmaal gewoon vullend.
   */
  #voegEigenToe(side, bestand) {
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'eigen',
      naam: bestand.naam || bestand.name,
      grootte: 30,
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
      verhouding: 1,
    };

    // Het beeld staat als blob in de browser; dat is genoeg voor de preview
    // en voor het drukbestand, want dat wordt hier getekend.
    const url = URL.createObjectURL(bestand);
    l.objectUrl = url;
    // Het bestand zelf blijft erbij, zodat het ontwerp later vanuit de
    // winkelmand opnieuw geopend kan worden (#momentopname). Geen eigenschap
    // voor het kenmerk: het beeld zelf komt al uit l.plaatje.
    l.bronBestand = bestand;
    // Een SVG is een tekening en blijft op elk formaat scherp. Zonder deze
    // markering zou de scherptemeting zijn intrinsieke maat (vaak 128 px)
    // aanzien voor de echte resolutie en onterecht waarschuwen.
    l.vector = /svg/i.test(bestand.type || '') || /\.svg$/i.test(l.naam || '');
    const img = new Image();
    img.onload = () => {
      l.plaatje = img;
      l.verhouding = img.naturalWidth / Math.max(img.naturalHeight, 1);
      this.#tekenLagenLijst(side);
      this.#render();
    };
    img.onerror = () => {
      l.plaatjeStuk = true;
      URL.revokeObjectURL(url);
      this.#verwijderLaag(side, l.id);
    };
    img.src = url;

    side.lagen.unshift(l);
    // Het bestand moet ook naar de drukker: die wil het origineel, niet
    // alleen ons drukbestand. Uploaden gebeurt op de achtergrond.
    this.#uploadEigen(side, l, bestand);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /** Voegt een landvlag uit de databank toe als element. */
  #voegVlagToe(side, flag) {
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'eigen',
      naam: flag.label,
      vlagCode: flag.code,
      grootte: 30,
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
      verhouding: 1.5,
      // De databank bestaat uit tekeningen; die zijn altijd scherp.
      vector: true,
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      l.plaatje = img;
      l.verhouding = img.naturalWidth / Math.max(img.naturalHeight, 1);
      this.#tekenLagenLijst(side);
      this.#render();
    };
    img.onerror = () => {
      l.plaatjeStuk = true;
      this.#verwijderLaag(side, l.id);
    };
    img.src = this.flagBase + 'flag-' + flag.code + '.svg';

    side.lagen.unshift(l);
    // paneel sluiten, anders staat de landenlijst over je ontwerp heen
    if (side.libPanel) {
      side.libPanel.hidden = true;
      this.#libAria(side, false);
    }
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /** Stuurt een element-afbeelding naar het uploadpunt en bewaart het adres. */
  async #uploadEigen(side, l, bestand) {
    if (!this.uploadUrl) return;
    try {
      const body = new FormData();
      body.append('file', bestand, bestand.name);
      const res = await fetch(this.uploadUrl, { method: 'POST', body });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.url) {
        l.bronUrl = data.url;
        this.#output();
      }
    } catch (err) {
      /* Lukt het niet, dan staat het beeld nog wel in het drukbestand. */
    }
  }

  /* ---------- sjablonen ---------- */

  /**
   * Haalt de sjablonen op en bouwt de balk. Het bestand is een paar kilobyte en
   * wordt pas opgehaald zodra iemand de ontwerptool opent.
   */
  #laadSjablonen(side) {
    if (window.FLAGCFG_SJABLONEN) { this.#bouwSjablonen(side); return; }
    const url = this.dataset.sjabloonUrl;
    if (!url) return;

    if (!FlagConfigurator.sjabloonBelofte) {
      FlagConfigurator.sjabloonBelofte = new Promise((klaar, mislukt) => {
        const s = document.createElement('script');
        s.src = url;
        s.onload = klaar;
        s.onerror = mislukt;
        document.head.appendChild(s);
      });
    }
    // De vormen komen er meteen bij. Dat bestand is vier kilobyte, en zonder de
    // vormenlijst kan een vorm uit een sjabloon zijn naam en icoontje niet
    // opzoeken — dan staat er een naamloze regel in de lijst.
    const vormcat = (window.FLAGCFG_KLEUR_INDEX || []).find((c) => c.id === 'vormen');
    if (vormcat) this.#laadKleurCategorie(vormcat, () => this.#tekenLagenLijst(side));

    FlagConfigurator.sjabloonBelofte
      .then(() => this.#bouwSjablonen(side))
      .catch(() => {
        // Lukt het niet, dan blijft de tool gewoon werken zonder sjablonen.
        const balk = side.root.querySelector('[data-sjabloonbalk]');
        if (balk) balk.hidden = true;
      });
  }

  /**
   * Bouwt de sjabloonbalk. Die verschijnt alleen zolang de vlag nog leeg is:
   * een sjabloon is een startpunt, en wie al bezig is heeft er niets meer aan.
   * Zo komt er ook geen vierde knop bij in de werkbalk.
   */
  #bouwSjablonen(side) {
    const balk = side.root.querySelector('[data-sjabloonbalk]');
    if (!balk) return;

    // Alleen sjablonen die bij deze doekvorm passen. Een vlag is ongeveer
    // 1,5 : 1, een spandoek 2 : 1 tot 4 : 1. Een vlagsjabloon op een spandoek
    // zet alles als een postzegel in het midden met lege zijkanten; dat ziet
    // eruit als een fout. Zonder 'voor' hoort een sjabloon bij de vlaggen.
    const dezeVorm = this.isSpandoek ? 'spandoek' : 'vlag';
    const alle = window.FLAGCFG_SJABLONEN || [];
    const lijst = alle.filter((s) => (s.voor || 'vlag') === dezeVorm || s.voor === 'beide');
    if (!lijst.length) { balk.hidden = true; return; }
    if (side.sjabloonKlaar) { this.#toonSjablonen(side); return; }
    side.sjabloonKlaar = true;

    const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    const rij = balk.querySelector('[data-sjabloonrij]');
    rij.textContent = '';

    // Eerst "Geen": een leeg doek. Zonder deze knop moet iemand die per ongeluk
    // een sjabloon koos elk element apart weghalen voordat hij zelf kan
    // beginnen.
    const leeg = document.createElement('button');
    leeg.type = 'button';
    leeg.className = 'flagcfg__sjabloon flagcfg__sjabloon--leeg';
    const leegNaam = this.dataset.tSjabloonGeen || 'Geen';
    leeg.setAttribute('aria-label', leegNaam);
    const leegDoek = document.createElement('canvas');
    leegDoek.width = 120;
    leegDoek.height = this.isSpandoek ? 42 : 80;
    leegDoek.className = 'flagcfg__sjabloonbeeld';
    const lh = leegDoek.height;
    const lc = leegDoek.getContext('2d');
    lc.fillStyle = '#ffffff';
    lc.fillRect(0, 0, 120, lh);
    lc.strokeStyle = 'rgba(0,0,0,0.14)';
    lc.lineWidth = 1;
    lc.strokeRect(0.5, 0.5, 119, lh - 1);
    // schuine streep, zoals bij "geen kleur"
    lc.strokeStyle = 'rgba(0,0,0,0.22)';
    lc.beginPath();
    lc.moveTo(20, lh * 0.78);
    lc.lineTo(100, lh * 0.22);
    lc.stroke();
    const leegLabel = document.createElement('span');
    leegLabel.textContent = leegNaam;
    leeg.append(leegDoek, leegLabel);
    leeg.addEventListener('click', () => this.#leegDeVlag(side));
    rij.appendChild(leeg);

    lijst.forEach((sj) => {
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'flagcfg__sjabloon';
      const naam = sj.namen[taal] || sj.namen.nl;
      knop.setAttribute('aria-label', naam);

      const doek = document.createElement('canvas');
      // Het voorbeeldje krijgt de verhouding van het echte doek, anders belooft
      // een vierkant plaatje iets anders dan de klant straks ziet.
      doek.width = 120;
      doek.height = this.isSpandoek ? 42 : 80;
      doek.className = 'flagcfg__sjabloonbeeld';
      this.#tekenSjabloonVoorbeeld(doek, sj);

      const label = document.createElement('span');
      label.textContent = naam;

      knop.append(doek, label);
      knop.addEventListener('click', () => this.#pasSjabloonToe(side, sj));
      rij.appendChild(knop);
    });

    this.#bindSjabloonKlap(side);
    this.#toonSjablonen(side);
  }

  /**
   * Verkleint tekstlagen van een pas geplaatst sjabloon tot ze op het doek
   * passen.
   *
   * De grootte van tekst is een percentage van de HOOGTE, maar of hij past
   * hangt af van de BREEDTE en van hoeveel letters er staan. Een sjabloon dat
   * mooi op een vlag van 1,5 : 1 staat, loopt op een spandoek van 4 : 1 aan
   * beide kanten van het doek af — "NU 50% KORTING" werd "50% KORT".
   *
   * Eén vaste maat per sjabloon kan dus niet kloppen: dezelfde tekst moet op
   * 200 x 100 en op 400 x 100 passen. Daarom hier meten en zo nodig
   * verkleinen. Alleen verkleinen — een sjabloon mag nooit groter worden dan
   * de ontwerper bedoelde.
   */
  #pasTekstInBreedte(side) {
    const r = side.rect;
    if (!r || !r.w || !r.h || !this.ctx) return;
    const MARGE = 0.9;                     // 90% van de breedte; de rest is lucht

    side.lagen.forEach((l) => {
      if (l.type !== 'tekst' || !l.tekst) return;
      let px = (l.grootte / 100) * r.h;
      this.ctx.save();
      this.ctx.font = '700 ' + px + 'px ' + l.font;
      const breed = this.ctx.measureText(l.tekst).width;
      this.ctx.restore();
      if (breed <= r.w * MARGE) return;

      const factor = (r.w * MARGE) / breed;
      const nieuw = Math.max(3, Math.floor(l.grootte * factor));
      l.grootte = nieuw;
    });
  }

  /** Toont de wisknop zodra de achtergrond iets anders is dan wit. */
  #toonBgWis(side) {
    if (side.bgWis) {
      side.bgWis.hidden = (side.bg || '#ffffff').toLowerCase() === '#ffffff';
    }
  }

  /**
   * Haalt alles van de vlag en zet de achtergrond terug op wit.
   *
   * Het geuploade ontwerp blijft staan: dat heeft de klant zelf aangeleverd en
   * hoort niet weg te vallen doordat hij op "Geen" tikt.
   */
  #leegDeVlag(side) {
    side.lagen = [];
    side.bg = '#ffffff';
    const veld = side.root.querySelector('[data-bg2]');
    if (veld) veld.value = '#ffffff';
    this.#toonBgWis(side);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /**
   * Toont de knop "alles wissen" alleen als er ook echt iets op de vlag staat.
   * Op een lege vlag zou het een knop zijn die niets doet, en dat is precies
   * het soort knop dat de tool onoverzichtelijk maakt.
   */
  #toonWisAlles(side) {
    const ietsErop = this.#heeftIetsErop(side);
    if (side.wisAlles) side.wisAlles.hidden = !ietsErop;
    // Kopieer naar de andere zijde verschijnt op hetzelfde moment.
    if (side.kopieer && side.kopieer.hidden === ietsErop) side.kopieer.hidden = !ietsErop;
  }

  /** Elementen, een vullende afbeelding of een andere achtergrond dan wit. */
  #heeftIetsErop(side) {
    return (
      (side.lagen && side.lagen.length > 0) ||
      !!side.img ||
      (side.bg || '#ffffff').toLowerCase() !== '#ffffff'
    );
  }

  /**
   * Maakt deze kant helemaal leeg en zet hem terug in de begintoestand.
   *
   * Anders dan #leegDeVlag gaat het geuploade ontwerp hier wel weg: de knop
   * heet "alles wissen", en dan is het gek als juist het grootste ding op de
   * vlag blijft staan. #wisOntwerp zet meteen ook alle velden terug die naar
   * de bestelling gaan, zodat er geen drukbestand van een gewiste vlag
   * achterblijft.
   */
  #wisAlles(side) {
    side.lagen = [];
    side.sleepLaag = null;
    side.hulplijnen = null;
    this.#wisOntwerp(side);
    if (side.bgInput2) side.bgInput2.value = '#ffffff';
    this.#toonBgWis(side);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /**
   * Kopieer naar de andere zijde, na een bevestiging (Jos, 15 sep 2026). Staat
   * er op de andere zijde al iets, dan zegt de vraag er ook bij dat dat
   * vervangen wordt.
   */
  #vraagKopieer(bron) {
    if (!this.sides || this.sides.length !== 2) return;
    const doel = this.sides[1 - bron.index];
    if (!doel || !this.#heeftIetsErop(bron)) return;
    const knop = bron.kopieer;
    let vraag = (knop && knop.dataset.vraag) || 'Kopiëren naar de andere zijde?';
    if (this.#heeftIetsErop(doel) && knop && knop.dataset.vervang) vraag += '\n\n' + knop.dataset.vervang;
    if (!window.confirm(vraag)) return;
    this.#kopieerZijde(bron, doel);
  }

  /**
   * Zet het ontwerp van de ene zijde ook op de andere: de achtergrondkleur, de
   * vullende afbeelding met zijn plaats en grootte, en alle elementen. Alles
   * staat in procenten van het vlagvlak en beide zijden zijn even groot, dus
   * het komt precies op dezelfde plek.
   *
   * De andere zijde wordt eerst gewist met #wisOntwerp. Zo worden ook de velden
   * voor de bestelling (plaatsing, drukbestand) opnieuw opgebouwd, en krijgt de
   * drukker geen oud bestand.
   *
   * Beelden worden gedeeld, niet opnieuw ingelezen: een grote foto twee keer in
   * het geheugen is precies wat een iPhone niet trekt (zie "Lege vlag op de
   * iPhone"). #beeldGedeeld zorgt dat de ene zijde het beeld van de andere dan
   * niet vrijgeeft.
   */
  #kopieerZijde(bron, doel) {
    doel.lagen = [];
    doel.sleepLaag = null;
    doel.hulplijnen = null;
    doel.gekozenLaag = null;
    this.#wisOntwerp(doel);

    doel.bg = bron.bg || '#ffffff';
    [doel.bgInput, doel.bgInput2].forEach((el) => {
      if (el) el.value = doel.bg;
    });

    if (bron.img) {
      doel.img = bron.img;
      doel.vector = !!bron.vector;
      doel.vlagCode = bron.vlagCode || null;
      doel.bronBestand = bron.bronBestand || null;
      doel.fileName = bron.fileName || '';
      doel.zoom = typeof bron.zoom === 'number' ? bron.zoom : 1;
      doel.cx = typeof bron.cx === 'number' ? bron.cx : 50;
      doel.cy = typeof bron.cy === 'number' ? bron.cy : 50;
      doel.stretch = !!bron.stretch;
      if (doel.zoomInput) doel.zoomInput.value = String(Math.round(doel.zoom * 100));
      doel.nameEl.textContent = bron.nameEl.textContent;
      doel.libValue.value = bron.libValue.value;
      doel.libValue.disabled = bron.libValue.disabled;
      doel.controls.hidden = false;
      doel.noteEl.hidden = true;
      if (doel.vlagCode && doel.libGrid) {
        const knop = doel.libGrid.querySelector('[data-code="' + doel.vlagCode + '"]');
        if (knop) knop.classList.add('is-chosen');
      }
      // Een eigen bestand moet ook voor deze zijde mee naar de drukker. Staat
      // het al bij de uploadserver, dan gaat dezelfde link mee.
      if (!doel.vlagCode && doel.bronBestand) {
        if (bron.vooraf) {
          doel.vooraf = bron.vooraf;
          this.#schakelBestandsveld(doel);
        } else {
          try {
            const dt = new DataTransfer();
            dt.items.add(doel.bronBestand);
            doel.fileInput.files = dt.files;
          } catch (err) {
            /* oudere browser: dan is de upload hieronder de weg */
          }
          this.#alvastUploaden(doel, doel.bronBestand);
        }
      }
    }

    // Elk element als eigen kopie met een nieuwe id, zodat verschuiven op de ene
    // zijde de andere niet meeneemt. Het plaatje zelf wordt gedeeld.
    doel.lagen = (bron.lagen || []).map((l) => {
      const kopie = Object.assign({}, this.#laagGegevens(l), { id: this.#nieuwId() });
      if (l.plaatje) kopie.plaatje = l.plaatje;
      if (l.plaatjeStuk) kopie.plaatjeStuk = true;
      if (l.bronBestand) kopie.bronBestand = l.bronBestand;
      if (!kopie.plaatje && kopie.type === 'kleur' && kopie.svg) this.#laadPlaatje(kopie);
      return kopie;
    });

    this.#toonBgWis(doel);
    this.#tekenLagenLijst(doel);
    this.#toonWisAlles(bron);
    this.#render();

    // In de focusstand meteen naar de zijde waar het ontwerp naartoe ging, zodat
    // de klant ziet dat het gelukt is.
    if (this.focus === bron.index) this.#zetFocus(doel.index);
  }

  /** Gebruikt de andere zijde hetzelfde beeld? Dan mag deze het niet vrijgeven. */
  #beeldGedeeld(side) {
    return !!(side.img && this.sides && this.sides.some((z) => z !== side && z.img === side.img));
  }

  /**
   * Klapt de sjabloonbalk open of dicht, en onthoudt dat.
   *
   * Bij een dubbelzijdige vlag staan er twee panelen onder elkaar, en dan nemen
   * twee volle sjabloonbalken te veel ruimte in — zeker op een telefoon. Daarom
   * begint hij daar dichtgeklapt; bij een enkelzijdige vlag gewoon open.
   */
  #bindSjabloonKlap(side) {
    const balk = side.root.querySelector('[data-sjabloonbalk]');
    if (!balk) return;
    const knop = balk.querySelector('[data-sjabloonklap]');
    const rij = balk.querySelector('[data-sjabloonrij]');
    if (!knop || !rij) return;

    let open;
    try {
      const bewaard = localStorage.getItem(FlagConfigurator.SJABLOON_SLEUTEL);
      open = bewaard === null ? this.sides.length < 2 : bewaard === 'open';
    } catch (e) {
      open = this.sides.length < 2;
    }

    const zet = (nu) => {
      rij.hidden = !nu;
      knop.setAttribute('aria-expanded', nu ? 'true' : 'false');
      knop.classList.toggle('is-open', nu);
    };
    zet(open);

    knop.addEventListener('click', () => {
      open = !open;
      try {
        localStorage.setItem(FlagConfigurator.SJABLOON_SLEUTEL, open ? 'open' : 'dicht');
      } catch (e) { /* niet kunnen bewaren mag de knop niet breken */ }
      // beide kanten gelijk laten lopen
      this.sides.forEach((s) => {
        const b = s.root.querySelector('[data-sjabloonbalk]');
        if (!b) return;
        const k = b.querySelector('[data-sjabloonklap]');
        const r = b.querySelector('[data-sjabloonrij]');
        if (r) r.hidden = !open;
        if (k) {
          k.setAttribute('aria-expanded', open ? 'true' : 'false');
          k.classList.toggle('is-open', open);
        }
      });
    });
  }

  /** Sleutel waaronder de open of dichte stand van de sjabloonbalk staat. */
  static get SJABLOON_SLEUTEL() {
    return 'flagcfg-sjabloonbalk';
  }

  /**
   * Laat de sjabloonbalk staan zolang de ontwerptool open is.
   *
   * Eerder verdween hij zodra je iets koos, maar dan kun je geen tweede
   * sjabloon meer bekijken — en juist het vergelijken is waar zo'n balk voor
   * dient. Hij blijft dus staan; een volgende keuze vervangt de vorige.
   */
  #toonSjablonen(side) {
    const balk = side.root.querySelector('[data-sjabloonbalk]');
    if (balk) balk.hidden = !window.FLAGCFG_SJABLONEN;
  }

  /**
   * Tekent een voorbeeld met dezelfde code als de vlag zelf, zodat wat de klant
   * in de keuzebalk ziet altijd klopt met wat hij krijgt. Een met de hand
   * nagemaakt plaatje zou na elke wijziging in het sjabloon gaan afwijken.
   */
  #tekenSjabloonVoorbeeld(doek, sj) {
    const ctx = doek.getContext('2d');
    const b = doek.width;
    const h = doek.height;
    ctx.fillStyle = sj.bg || '#ffffff';
    ctx.fillRect(0, 0, b, h);

    [...sj.lagen].reverse().forEach((l) => {
      const x = (l.cx / 100) * b;
      const y = (l.cy / 100) * h;
      ctx.save();
      ctx.translate(x, y);
      if (l.hoek) ctx.rotate((l.hoek * Math.PI) / 180);
      if (l.type === 'vorm') {
        const hoog = (l.grootte / 100) * h;
        this.#tekenVorm(ctx, l, hoog * (l.verhouding || 1), hoog);
      } else if (l.type === 'kleur') {
        // In het voorbeeld het emoji-teken zelf tekenen. De echte vlag gebruikt
        // straks de nette Noto-tekening, maar daarvoor zou hier een bestand van
        // honderden kilobytes geladen moeten worden — voor een plaatje van
        // 120 bij 80 is dat de moeite niet waard.
        const px = (l.grootte / 100) * h;
        ctx.font = px + 'px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(l.teken, 0, 0);
      } else {
        // Dezelfde inperking als bij het echte doek (#pasTekstInBreedte):
        // anders belooft het voorbeeldje grote letters die na het aanklikken
        // kleiner blijken.
        let px = (l.grootte / 100) * h;
        ctx.font = '700 ' + px + 'px ' + l.font;
        const breed = ctx.measureText(l.tekst || '').width;
        if (breed > b * 0.9) px = px * ((b * 0.9) / breed);
        ctx.font = '700 ' + px + 'px ' + l.font;
        ctx.fillStyle = l.kleur;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(l.tekst, 0, 0);
      }
      ctx.restore();
    });

    ctx.strokeStyle = 'rgba(0,0,0,0.14)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, b - 1, h - 1);
  }

  /**
   * Zet een sjabloon op de vlag. Alles blijft daarna gewoon aan te passen —
   * het is een beginpunt, geen eindresultaat.
   */
  #pasSjabloonToe(side, sj) {
    // Een sjabloon mag ook emoji bevatten. Die staan per categorie in een apart
    // bestand dat pas wordt opgehaald als het nodig is, dus eerst ophalen en
    // daarna pas plaatsen — anders staat er een gat waar het symbool hoort.
    const nodig = [...new Set(
      sj.lagen.filter((l) => l.type === 'kleur' && l.cat).map((l) => l.cat)
    )];

    const zetNeer = () => {
      side.lagen = sj.lagen.map((l) => {
        const laag = Object.assign({}, l, { id: this.#nieuwId() });
        if (l.type === 'kleur') {
          const pot = (window.FLAGCFG_KLEUR || {})[l.cat] || [];
          const sym = pot.find((x) => x.t === l.teken);
          if (!sym) return null;              // symbool niet gevonden: overslaan
          laag.svg = sym.s;
          delete laag.cat;
        }
        return laag;
      }).filter(Boolean);

      if (sj.bg) {
        side.bg = sj.bg;
        const veld = side.root.querySelector('[data-bg2]');
        if (veld) veld.value = sj.bg;
      }
      this.#toonBgWis(side);
      this.#pasTekstInBreedte(side);
      side.lagen.forEach((l) => { if (l.type === 'kleur') this.#laadPlaatje(l); });
      this.#toonSjablonen(side);
      this.#tekenLagenLijst(side);
      this.#render();
    };

    if (!nodig.length) { zetNeer(); return; }

    const index = window.FLAGCFG_KLEUR_INDEX || [];
    let open = nodig.length;
    nodig.forEach((id) => {
      const cat = index.find((c) => c.id === id);
      if (!cat) { open -= 1; if (!open) zetNeer(); return; }
      this.#laadKleurCategorie(cat, () => {
        open -= 1;
        if (!open) zetNeer();
      });
    });
  }

  /**
   * Zet een vorm op de vlag.
   *
   * Krijgt een startkleur die op het doek te zien is: op een witte vlag zou een
   * witte vorm onzichtbaar zijn, en dan lijkt het alsof de knop niets deed.
   */
  #voegVormToe(side, sym) {
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'vorm',
      vorm: sym.vorm,
      teken: sym.t,
      namen: sym.namen,
      verhouding: sym.verhouding || 1,
      kleur: this.#leesbareKleur(side),
      grootte: 25,
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
    };
    side.lagen.unshift(l);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  #voegKleurToe(side, sym) {
    const plek = this.#vrijePlek(side);
    const l = {
      id: this.#nieuwId(),
      type: 'kleur',
      teken: sym.t,
      svg: sym.s,
      grootte: 25,
      hoek: 0,
      cx: plek.cx,
      cy: plek.cy,
    };
    side.lagen.unshift(l);
    this.#laadPlaatje(l);
    this.#tekenLagenLijst(side);
    this.#render();
  }

  /**
   * Slepen aan de greep verandert de volgorde. Bewust met pointer-events en
   * niet met de sleepfunctie van de browser: die werkt op een telefoon niet.
   *
   * Tijdens het slepen wordt de rij zelf in de lijst verplaatst en de laag in
   * de gegevens meeverschoven; de lijst wordt pas na afloop opnieuw opgebouwd.
   * Halverwege opnieuw opbouwen zou de rij onder je vinger vandaan halen.
   */
  #bindSleepVolgorde(side, rij, greep, laag) {
    const lijst = side.tekstLijst;
    if (!lijst) return;

    // De greep is maar een paar millimeter groot. Wie de rij ergens anders
    // vastpakt verwacht ook dat hij meekomt, dus de hele rij luistert mee —
    // behalve op de bedieningsonderdelen, want daar wil je schuiven en typen.
    rij.addEventListener('pointerdown', (e) => {
      const opBediening = e.target.closest('input, select, textarea, a') ||
                          (e.target.closest('button') && !e.target.closest('.flagcfg-tekst__greep'));
      if (opBediening) return;

      const startY = e.clientY;
      let bezig = false;

      try {
        rij.setPointerCapture(e.pointerId);
      } catch (err) {
        /* Vastpakken mag mislukken; slepen moet dan gewoon doorgaan. */
      }

      const verplaats = (y) => {
        const anderen = [...lijst.children].filter((r) => r !== rij);
        let voor = null;
        for (const r of anderen) {
          const b = r.getBoundingClientRect();
          if (y < b.top + b.height / 2) { voor = r; break; }
        }
        if (voor) lijst.insertBefore(rij, voor);
        else lijst.appendChild(rij);
      };

      const beweeg = (ev) => {
        // Pas slepen na een paar pixels. Zonder die drempel telt elke klik
        // als een minisleep en verspringt de lijst bij het aantikken.
        if (!bezig && Math.abs(ev.clientY - startY) < 5) return;
        if (!bezig) {
          bezig = true;
          rij.classList.add('is-sleep');
        }
        ev.preventDefault();
        verplaats(ev.clientY);
      };

      const stop = () => {
        rij.removeEventListener('pointermove', beweeg);
        rij.removeEventListener('pointerup', stop);
        rij.removeEventListener('pointercancel', stop);
        if (!bezig) return;
        rij.classList.remove('is-sleep');

        // volgorde in de gegevens gelijktrekken met wat er op het scherm staat
        const volgorde = [...lijst.children].map((r) => r.dataset.id);
        side.lagen.sort((a, b) => volgorde.indexOf(a.id) - volgorde.indexOf(b.id));
        this.#tekenLagenLijst(side);
        this.#render();
      };

      rij.addEventListener('pointermove', beweeg);
      rij.addEventListener('pointerup', stop);
      rij.addEventListener('pointercancel', stop);
    });
  }

  /** Bouwt de bedieningsrijtjes onder de preview opnieuw op. */
  /**
   * Rij voor de afbeelding die op de productpagina is geupload. Die is geen
   * laag maar de achtergrond van het doek, en de knop om hem weg te halen
   * zit in het blok dat binnen de ontwerptool verborgen is. Zonder deze rij
   * zit je er dus aan vast zodra je de tool opent.
   */
  #achtergrondRij(side) {
    const rij = document.createElement('div');
    rij.className = 'flagcfg-tekst flagcfg-tekst--achtergrond';

    const merk = document.createElement('span');
    merk.className = 'flagcfg-tekst__greep flagcfg-tekst__greep--vast';
    merk.innerHTML = '<span class="flagcfg-tekst__nummer">◻</span>';
    merk.title = this.dataset.tAchtergrondUitleg || 'Achtergrond';

    const naam = document.createElement('span');
    naam.className = 'flagcfg-tekst__vormnaam flagcfg-tekst__bestand';
    naam.textContent = side.fileName || this.dataset.tGeupload || 'Afbeelding';

    const schuif = document.createElement('label');
    schuif.className = 'flagcfg-tekst__schuif';
    const sp = document.createElement('span');
    sp.textContent = this.dataset.tTekstGrootte || 'Grootte';
    const inp = document.createElement('input');
    inp.type = 'range';
    inp.min = 20;
    inp.max = 300;
    inp.step = 1;
    inp.value = String(Math.round(side.zoom * 100));
    inp.addEventListener('input', () => {
      side.zoom = Number(inp.value) / 100;
      side.stretch = false;
      this.#render();
    });
    schuif.append(sp, inp);

    const knoppen = document.createElement('div');
    knoppen.className = 'flagcfg-tekst__knoppen';

    const vullen = document.createElement('button');
    vullen.type = 'button';
    vullen.className = 'flagcfg-tekst__midden';
    vullen.textContent = this.dataset.tFill || 'Vlag vullen';
    vullen.addEventListener('click', () => this.#action(side, 'fill'));

    const midden = document.createElement('button');
    midden.type = 'button';
    midden.className = 'flagcfg-tekst__midden';
    midden.textContent = this.dataset.tCentreren || 'Centreren';
    midden.addEventListener('click', () => {
      side.cx = 50;
      side.cy = 50;
      this.#render();
    });

    const weg = document.createElement('button');
    weg.type = 'button';
    weg.className = 'flagcfg-tekst__weg';
    weg.textContent = this.dataset.tTekstWeg || 'Verwijderen';
    weg.addEventListener('click', () => {
      this.#wisOntwerp(side);
      this.#tekenLagenLijst(side);
    });

    knoppen.append(vullen, midden, weg);
    rij.append(merk, naam, schuif, knoppen);
    return rij;
  }

  #tekenLagenLijst(side) {
    // De sjabloonbalk hoort weg zodra er iets op de vlag staat, en terug zodra
    // alles weer weg is. Deze methode draait bij elke wijziging in de lagen.
    this.#toonSjablonen(side);
    this.#toonWisAlles(side);

    const lijst = side.tekstLijst;
    if (!lijst) return;
    lijst.textContent = '';

    side.lagen.forEach((l, nr) => {
      const rij = document.createElement('div');
      rij.className = 'flagcfg-tekst';
      rij.dataset.id = l.id;

      // Greep om de volgorde te veranderen. Wat bovenaan staat ligt op de
      // vlag bovenop; sleep je iets omhoog, dan komt het voor de rest.
      const greep = document.createElement('button');
      greep.type = 'button';
      greep.className = 'flagcfg-tekst__greep';
      greep.setAttribute('aria-label', this.dataset.tVolgorde || 'Volgorde');
      greep.innerHTML =
        '<span class="flagcfg-tekst__nummer">' + (nr + 1) + '</span>' +
        '<svg viewBox="0 0 24 24" aria-hidden="true">' +
        '<circle cx="9" cy="6" r="1.7"/><circle cx="15" cy="6" r="1.7"/>' +
        '<circle cx="9" cy="12" r="1.7"/><circle cx="15" cy="12" r="1.7"/>' +
        '<circle cx="9" cy="18" r="1.7"/><circle cx="15" cy="18" r="1.7"/></svg>';
      this.#bindSleepVolgorde(side, rij, greep, l);
      rij.append(greep);

      const schuif = (label, min, max, waarde, bij) => {
        const wrap = document.createElement('label');
        wrap.className = 'flagcfg-tekst__schuif';
        const sp = document.createElement('span');
        sp.textContent = label;
        const inp = document.createElement('input');
        inp.type = 'range';
        inp.min = min;
        inp.max = max;
        inp.step = 1;
        inp.value = waarde;
        inp.addEventListener('input', () => {
          bij(Number(inp.value));
          this.#render();
        });
        wrap.append(sp, inp);
        return wrap;
      };

      if (l.type === 'tekst') {
        const invoer = document.createElement('input');
        invoer.type = 'text';
        invoer.className = 'flagcfg-tekst__invoer';
        invoer.value = l.tekst;
        invoer.maxLength = 60;
        invoer.setAttribute('aria-label', this.dataset.tTekstLabel || 'Tekst');
        invoer.addEventListener('input', () => {
          l.tekst = invoer.value;
          this.#render();
        });

        const font = document.createElement('select');
        font.className = 'flagcfg-tekst__font';
        font.setAttribute('aria-label', this.dataset.tTekstFont || 'Lettertype');
        LETTERTYPEN.forEach((f) => {
          const o = document.createElement('option');
          o.value = f.css;
          o.textContent = f.naam;
          o.style.fontFamily = f.css;
          font.appendChild(o);
        });
        font.value = l.font;
        font.addEventListener('change', () => {
          l.font = font.value;
          this.#render();
        });

        rij.append(invoer, font);
      } else {
        const naam = document.createElement('span');
        naam.className = 'flagcfg-tekst__vormnaam';
        if (l.type === 'eigen') {
          naam.textContent = l.naam || this.dataset.tAfbeelding || 'afbeelding';
          naam.classList.add('flagcfg-tekst__bestand');
          // Stipje met de scherpte van dit ene element. Alleen bij bitmaps:
          // een SVG of een vlag uit de databank is een tekening en altijd
          // scherp. De verwijzing blijft bewaard zodat het slepen aan de
          // Grootte-schuif hem live kan bijwerken zonder de lijst opnieuw op
          // te bouwen — dat laatste zou op een telefoon gaan haperen.
          //
          // Bewust NAAST de naam en niet erin: een foto uit een fotorol heet
          // "EA7291CC-42B5-4B74-8145-A…" en die naam wordt afgekapt. Zat het
          // stipje daarbinnen, dan werd het mee afgekapt en zag je juist het
          // getal niet meer waar het om ging.
          if (!l.vector) {
            const stip = document.createElement('span');
            stip.className = 'flagcfg-tekst__scherpte';
            stip.hidden = true;
            l.stipEl = stip;
            l.stipNaNaam = true;
          } else {
            l.stipEl = null;
          }
        } else {
          const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
          if (l.type === 'vorm') {
            // De naam wordt hier opgezocht en niet uit de laag gelezen: een vorm
            // die uit een sjabloon komt heeft er geen bij zich, en dan zag je in
            // de lijst alleen een kleurvakje zonder te weten wat het was.
            const bron = ((window.FLAGCFG_KLEUR || {}).vormen || [])
              .find((v) => v.vorm === l.vorm);
            // Het vormpje er als klein icoontje bij, zodat in één oogopslag te
            // zien is of een regel een vorm of een tekst is.
            if (bron && bron.s) {
              const beeld = document.createElement('span');
              beeld.className = 'flagcfg-tekst__vormicoon';
              beeld.innerHTML = bron.s;
              const svg = beeld.querySelector('svg');
              if (svg) {
                svg.setAttribute('width', '100%');
                svg.setAttribute('height', '100%');
                svg.setAttribute('color', l.kleur || 'currentColor');
              }
              rij.append(beeld);
            }
            naam.textContent = bron
              ? (bron.namen[taal] || bron.namen.nl)
              : (l.teken || this.dataset.tVorm || 'vorm');
          } else {
            naam.textContent = l.teken;
          }
          naam.classList.add('flagcfg-tekst__teken');
        }
        rij.append(naam);
        if (l.stipNaNaam && l.stipEl) rij.append(l.stipEl);
      }

      // Tekst en vormen hebben een kleur om te kiezen. Een kleursymbool en een
      // eigen afbeelding brengen hun eigen kleuren mee; een kleurkiezer die
      // niets doet is erger dan geen kleurkiezer.
      if (l.type === 'tekst' || l.type === 'vorm') {
        const kleur = document.createElement('input');
        kleur.type = 'color';
        kleur.className = 'flagcfg-tekst__kleur';
        kleur.value = l.kleur;
        kleur.setAttribute('aria-label', this.dataset.tTekstKleur || 'Kleur');
        kleur.addEventListener('input', () => {
          l.kleur = kleur.value;
          this.#render();
        });
        rij.append(kleur);
      }

      // Een afbeelding mag verder dan 100: om een liggende vlag te vullen met
      // een vierkant logo is meer dan de vlaghoogte nodig, en anders zou de
      // knop "Vlag vullen" een waarde zetten die de schuif niet kan tonen.
      const maxMaat = l.type === 'tekst' ? 40 : (l.type === 'eigen' ? 300 : 100);
      rij.append(schuif(this.dataset.tTekstGrootte || 'Grootte', 4, maxMaat, l.grootte, (v) => (l.grootte = v)));
      rij.append(schuif(this.dataset.tTekstHoek || 'Draaien', -180, 180, l.hoek, (v) => (l.hoek = v)));

      const knoppen = document.createElement('div');
      knoppen.className = 'flagcfg-tekst__knoppen';

      // Pijltjes naast het slepen. Slepen is prettig als het werkt, maar het
      // hangt af van browser en invoerapparaat; een knop doet het altijd.
      const schuifLaag = (richting) => {
        const i = side.lagen.indexOf(l);
        const j = i + richting;
        if (i < 0 || j < 0 || j >= side.lagen.length) return;
        side.lagen.splice(i, 1);
        side.lagen.splice(j, 0, l);
        this.#tekenLagenLijst(side);
        this.#render();
      };

      const pijl = (richting, label, pad) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'flagcfg-tekst__pijl';
        b.setAttribute('aria-label', label);
        b.title = label;
        b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + pad + '"/></svg>';
        const eerste = side.lagen.indexOf(l) === 0;
        const laatste = side.lagen.indexOf(l) === side.lagen.length - 1;
        b.disabled = richting < 0 ? eerste : laatste;
        b.addEventListener('click', () => schuifLaag(richting));
        return b;
      };

      knoppen.append(
        pijl(-1, this.dataset.tNaarVoren || 'Naar voren', 'M12 6l6 7h-4v5h-4v-5H6l6-7Z'),
        pijl(1, this.dataset.tNaarAchteren || 'Naar achteren', 'M12 18l-6-7h4V6h4v5h4l-6 7Z')
      );

      // Iets terugzetten naar het midden is met slepen lastig precies te
      // krijgen, zeker op een telefoon.
      const midden = document.createElement('button');
      midden.type = 'button';
      midden.className = 'flagcfg-tekst__midden';
      midden.textContent = this.dataset.tCentreren || 'Centreren';
      midden.addEventListener('click', () => {
        l.cx = 50;
        l.cy = 50;
        this.#render();
      });

      // Alleen bij een afbeelding of landvlag. Een landvlag die je uit de
      // databank kiest komt binnen als klein element; wie er een landvlag van
      // wil maken moest hem tot nu toe met de hand groot slepen.
      let vullen = null;
      if (l.type === 'eigen') {
        vullen = document.createElement('button');
        vullen.type = 'button';
        vullen.className = 'flagcfg-tekst__midden';
        vullen.textContent = this.dataset.tFill || 'Vlag vullen';
        vullen.addEventListener('click', () => {
          // Zo groot maken dat hij het doek helemaal bedekt, zonder de
          // verhouding te vervormen — een uitgerekt logo is erger dan een
          // logo dat niet helemaal past.
          const r = side.rect;
          const verh = l.verhouding || 1;
          const nodig = (r.w / r.h) / verh * 100;
          l.grootte = Math.round(Math.max(100, nodig));
          l.cx = 50;
          l.cy = 50;
          l.hoek = 0;
          this.#tekenLagenLijst(side);
          this.#render();
        });
      }

      const weg = document.createElement('button');
      weg.type = 'button';
      weg.className = 'flagcfg-tekst__weg';
      weg.textContent = this.dataset.tTekstWeg || 'Verwijderen';
      weg.addEventListener('click', () => this.#verwijderLaag(side, l.id));

      if (vullen) knoppen.append(vullen);
      knoppen.append(midden, weg);
      rij.append(knoppen);

      lijst.appendChild(rij);
    });

    // Onderaan, want de achtergrond ligt ook onder alle elementen.
    if (side.img) lijst.appendChild(this.#achtergrondRij(side));
  }

  /**
   * Rekent uit waar een laag staat en hoe groot hij is, binnen een gegeven
   * doek. Wordt gedeeld door de preview en het drukbestand: dezelfde som op
   * een andere schaal, zodat wat de klant ziet ook gedrukt wordt.
   */
  #laagVlak(ctx, rect, l) {
    const x = rect.x + (l.cx / 100) * rect.w;
    const y = rect.y + (l.cy / 100) * rect.h;
    if (l.type !== 'tekst') {
      const hoog = (l.grootte / 100) * rect.h;
      // Een kleursymbool is vierkant; een eigen afbeelding houdt zijn eigen
      // verhouding, anders wordt een logo uitgerekt.
      const breed = (l.type === 'eigen' || l.type === 'vorm') ? hoog * (l.verhouding || 1) : hoog;
      return { x: x, y: y, breed: breed, hoog: hoog };
    }
    const px = (l.grootte / 100) * rect.h;
    ctx.font = '700 ' + px + 'px ' + l.font;
    return { x: x, y: y, px: px, breed: ctx.measureText(l.tekst || '').width, hoog: px };
  }

  /* ---------- hulplijnen bij het uitlijnen ---------- */

  /**
   * Hoe dicht een element bij een uitlijnpunt moet komen voordat het vastklikt,
   * uitgedrukt in canvaspixels. Bewust klein: te ruim en de klant kan niets meer
   * bewust net naast het midden zetten.
   */
  static get SNAP() {
    return 8;
  }

  /**
   * De omhullende rechthoek van een laag, in percentages van het vlagvlak.
   *
   * Bij een gedraaide laag wordt de rechthoek om de gedraaide vorm heen
   * genomen, niet de oorspronkelijke breedte en hoogte. Anders lijkt een schuin
   * gezet symbool uitgelijnd terwijl het dat zichtbaar niet is.
   */
  #laagKader(side, l) {
    const rect = side.rect;
    const v = this.#laagVlak(this.ctx, rect, l);
    let breed = v.breed;
    let hoog = v.hoog;
    if (l.hoek) {
      const r = (l.hoek * Math.PI) / 180;
      const c = Math.abs(Math.cos(r));
      const s = Math.abs(Math.sin(r));
      const b = breed * c + hoog * s;
      const h = breed * s + hoog * c;
      breed = b;
      hoog = h;
    }
    const halfB = (breed / rect.w) * 50;
    const halfH = (hoog / rect.h) * 50;
    return {
      links: l.cx - halfB,
      midX: l.cx,
      rechts: l.cx + halfB,
      boven: l.cy - halfH,
      midY: l.cy,
      onder: l.cy + halfH,
      halfB: halfB,
      halfH: halfH,
    };
  }

  /**
   * Verzamelt waar een element op uit kan lijnen, in percentages.
   *
   * De prioriteit bepaalt welke lijn wint als er meerdere binnen bereik liggen:
   * 1 het midden van de vlag, 2 het midden van een ander element, 3 een rand.
   * Zonder die volgorde zou een element bij het midden van de vlag ineens aan de
   * rand van iets anders blijven hangen.
   */
  #uitlijnDoelen(side, negeer) {
    const x = [{ waarde: 50, prio: 1 }];
    const y = [{ waarde: 50, prio: 1 }];
    (side.lagen || []).forEach((l) => {
      if (l === negeer) return;
      const k = this.#laagKader(side, l);
      x.push({ waarde: k.midX, prio: 2 });
      y.push({ waarde: k.midY, prio: 2 });
      x.push({ waarde: k.links, prio: 3 }, { waarde: k.rechts, prio: 3 });
      y.push({ waarde: k.boven, prio: 3 }, { waarde: k.onder, prio: 3 });
    });
    return { x: x, y: y };
  }

  /**
   * Past de positie aan als er iets binnen bereik ligt, en zegt welke lijnen er
   * getekend moeten worden.
   *
   * Het element lijnt met drie punten tegelijk uit: zijn eigen middelpunt en
   * zijn twee randen. Zo klikt hij zowel vast op "midden onder midden" als op
   * "linkerrand gelijk", wat je nodig hebt om een rijtje symbolen netjes onder
   * elkaar te krijgen.
   */
  #uitlijnen(side, laag, cx, cy, doelen) {
    const rect = side.rect;
    // De andere elementen staan stil zolang er gesleept wordt, dus hun
    // uitlijnpunten worden eenmalig bij het oppakken berekend en meegegeven.
    // Ze elke vingerbeweging opnieuw uitrekenen kost een tekstmeting per laag,
    // en dat telt op zodra er veel op de vlag staat.
    if (!doelen) doelen = this.#uitlijnDoelen(side, laag);
    const k = this.#laagKader(side, Object.assign({}, laag, { cx: cx, cy: cy }));
    const tolX = (FlagConfigurator.SNAP / rect.w) * 100;
    const tolY = (FlagConfigurator.SNAP / rect.h) * 100;

    // Per as de beste kandidaat zoeken: eerst op prioriteit, dan op afstand.
    const zoek = (kanten, lijst, tol) => {
      let best = null;
      kanten.forEach((kant) => {
        lijst.forEach((d) => {
          const afstand = Math.abs(kant.positie - d.waarde);
          if (afstand > tol) return;
          if (!best || d.prio < best.prio || (d.prio === best.prio && afstand < best.afstand)) {
            best = { prio: d.prio, afstand: afstand, lijn: d.waarde, verschuif: d.waarde - kant.positie };
          }
        });
      });
      return best;
    };

    const bX = zoek(
      [{ positie: k.midX }, { positie: k.links }, { positie: k.rechts }],
      doelen.x, tolX
    );
    const bY = zoek(
      [{ positie: k.midY }, { positie: k.boven }, { positie: k.onder }],
      doelen.y, tolY
    );

    const lijnen = [];
    if (bX) { cx += bX.verschuif; lijnen.push({ richting: 'x', pos: bX.lijn }); }
    if (bY) { cy += bY.verschuif; lijnen.push({ richting: 'y', pos: bY.lijn }); }
    return { cx: cx, cy: cy, lijnen: lijnen };
  }

  /**
   * Tekent de hulplijnen over de vlag heen. Alleen tijdens het slepen; ze staan
   * los van de lagen en komen dus nooit op het drukbestand terecht.
   */
  #tekenHulplijnen(ctx, side, rect) {
    const lijnen = side.hulplijnen;
    if (!lijnen || !lijnen.length) return;
    ctx.save();
    ctx.strokeStyle = '#e5484d';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 5]);
    lijnen.forEach((l) => {
      ctx.beginPath();
      if (l.richting === 'x') {
        const x = rect.x + (l.pos / 100) * rect.w;
        ctx.moveTo(x, rect.y);
        ctx.lineTo(x, rect.y + rect.h);
      } else {
        const y = rect.y + (l.pos / 100) * rect.h;
        ctx.moveTo(rect.x, y);
        ctx.lineTo(rect.x + rect.w, y);
      }
      ctx.stroke();
    });
    ctx.restore();
  }

  /**
   * Tekent een kader om het element dat op dit moment versleept wordt.
   *
   * Waarom: op een telefoon bedekt je vinger juist het element dat je
   * verplaatst, en bij een vlag vol tekst en symbolen zie je niet welk
   * onderdeel meebeweegt. Het kader beantwoordt die ene vraag: dit heb je vast.
   *
   * Staat bewust hier en niet in #tekenLagen. Die methode wordt namelijk ook
   * gebruikt om het drukbestand te maken, en dan zou dit kader meegedrukt
   * worden. Dit volgt hetzelfde patroon als #tekenHulplijnen: alleen in het
   * scherm, nooit in de export.
   */
  /** Hoeveel canvaseenheden er in een schermpixel gaan. */
  #viewPerPx() {
    // Tijdens het slepen de maat van het oppakken (zie naarVlak in #bindGestures).
    const r = this.gebaarRect || (this.canvas ? this.canvas.getBoundingClientRect() : null);
    const breed = r ? r.width : 0;
    return breed ? this.layout.view.w / breed : 1;
  }

  /** Middelpunt en de vier hoekjes van het kader om een laag, in canvaseenheden. */
  #hoekpunten(side, l) {
    const v = this.#laagVlak(this.ctx, side.rect, l);
    const marge = 6 * this.#viewPerPx();
    const b = v.breed / 2 + marge;
    const h = v.hoog / 2 + marge;
    const hoek = ((l.hoek || 0) * Math.PI) / 180;
    const cos = Math.cos(hoek);
    const sin = Math.sin(hoek);
    return {
      midden: { x: v.x, y: v.y },
      vlak: v,
      punten: [[-b, -h], [b, -h], [-b, h], [b, h]].map(([dx, dy]) => ({
        x: v.x + dx * cos - dy * sin,
        y: v.y + dx * sin + dy * cos,
      })),
    };
  }

  /**
   * Welk hoekje van het gekozen element ligt onder dit punt? -1 als geen.
   *
   * Het aanraakgebied is 22 schermpixels rond het hoekje, dus 44 breed: groot
   * genoeg voor een vinger, ook al is het hoekje zelf kleiner getekend. Bij een
   * klein element zou dat gebied het hele element bedekken en kon je het niet
   * meer verschuiven. Daarom telt een aanraking binnen het element zelf alleen
   * als hoekje als hij echt vlak bij de hoek is.
   */
  #hoekjeOpPunt(side, l, p) {
    if (!l || !side.lagen || !side.lagen.includes(l)) return -1;
    if (l.type === 'tekst' && !l.tekst) return -1;
    const s = this.#viewPerPx();
    const bereik = 22 * s;
    const { punten, vlak, midden } = this.#hoekpunten(side, l);
    const hoek = (-(l.hoek || 0) * Math.PI) / 180;
    const dx = p.x - midden.x;
    const dy = p.y - midden.y;
    const lx = dx * Math.cos(hoek) - dy * Math.sin(hoek);
    const ly = dx * Math.sin(hoek) + dy * Math.cos(hoek);
    const binnen = Math.abs(lx) <= vlak.breed / 2 && Math.abs(ly) <= vlak.hoog / 2;
    let beste = -1;
    let dichtst = Infinity;
    punten.forEach((q, i) => {
      const d = Math.hypot(p.x - q.x, p.y - q.y);
      if (d > bereik || d >= dichtst) return;
      if (binnen && d > 10 * s) return;
      beste = i;
      dichtst = d;
    });
    return beste;
  }

  /** De zichtbare zijde waarvan een hoekje van het gekozen element onder dit punt ligt. */
  #zijdeMetHoekje(p) {
    if (!this.classList.contains('is-vol') || !this.tekstToegestaan) return null;
    const { view } = this.layout;
    return this.sides.find((s) => s.rect.x < view.w && this.#hoekjeOpPunt(s, s.gekozenLaag, p) >= 0) || null;
  }

  #tekenSelectie(ctx, side, rect) {
    // Tijdens het slepen het element dat je vasthebt; daarbuiten, in de
    // ontwerptool, het gekozen element (met hoekjes om te schalen). Nooit in het
    // controlebeeld: zie #makePreview.
    if (this.zonderSelectie) return;
    const l = side.sleepLaag || (this.classList.contains('is-vol') ? side.gekozenLaag : null);
    // includes(): een sjabloon of "alles wissen" vervangt de lagen, en dan mag
    // er geen kader blijven staan om een element dat er niet meer is.
    if (!l || !side.lagen || !side.lagen.includes(l)) return;
    if (l.type === 'tekst' && !l.tekst) return;

    const v = this.#laagVlak(ctx, rect, l);
    // Iets ruimer dan het element zelf, zodat de lijn eromheen ligt en niet
    // dwars door de letters of het logo.
    // Maten in schermpixels, omgerekend naar canvaseenheden: het doek staat op
    // een telefoon veel kleiner in beeld dan op een computer, en een kader of
    // hoekje hoort er op elk scherm even groot uit te zien.
    const s = this.#viewPerPx();
    const marge = 6 * s;
    const b = v.breed + marge * 2;
    const h = v.hoog + marge * 2;

    ctx.save();
    ctx.translate(v.x, v.y);
    // Meedraaien met het element; #laagVlak geeft de maten zonder rotatie.
    if (l.hoek) ctx.rotate((l.hoek * Math.PI) / 180);

    // Eerst een donkere schaduwlijn, dan de paarse erover. Zonder die onderlaag
    // valt paars weg op een donkerpaarse of zwarte vlag.
    ctx.lineWidth = 3.5 * s;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.28)';
    ctx.strokeRect(-b / 2, -h / 2, b, h);

    ctx.lineWidth = 2 * s;
    ctx.strokeStyle = SELECTIE_KLEUR;
    ctx.strokeRect(-b / 2, -h / 2, b, h);

    // Hoekpunten: wit gevuld met een paarse rand, zodat ze op elke
    // achtergrondkleur zichtbaar blijven.
    // Rond, wit met een paarse rand en een zachte schaduw, zodat ze op elke
    // kleur zichtbaar zijn. Het aanraakgebied is groter (#hoekjeOpPunt).
    const straal = 7 * s;
    [[-b / 2, -h / 2], [b / 2, -h / 2], [-b / 2, h / 2], [b / 2, h / 2]].forEach(([hx, hy]) => {
      ctx.beginPath();
      ctx.arc(hx, hy, straal + 1.5 * s, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(hx, hy, straal, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 2 * s;
      ctx.strokeStyle = SELECTIE_KLEUR;
      ctx.stroke();
    });

    ctx.restore();
  }

  /** Tekent alle lagen van een zijde binnen het opgegeven doek. */
  #tekenLagen(ctx, side, rect) {
    if (!this.tekstToegestaan || !side.lagen.length) return;
    // Alleen op het scherm een kopie op schermformaat (#schermKopie). Het
    // drukbestand en de vergroting geven een eigen tekenvlak mee en tekenen dus
    // altijd het origineel, op volle scherpte.
    const scherm = ctx === this.ctx;
    const snel = scherm && this.#bezigMetGebaar();
    const beeld = (l, v) => (scherm && this.#schermKopie(l.plaatje, v.breed, v.hoog, snel)) || l.plaatje;
    // Van achteren naar voren: de laag bovenaan de lijst (index 0) wordt als
    // laatste getekend en ligt dus bovenop, zoals je verwacht.
    [...side.lagen].reverse().forEach((l) => {
      if (l.type === 'tekst' && !l.tekst) return;
      const v = this.#laagVlak(ctx, rect, l);
      ctx.save();
      try {
        ctx.translate(v.x, v.y);
        if (l.hoek) ctx.rotate((l.hoek * Math.PI) / 180);
        ctx.fillStyle = l.kleur;
        if (l.type === 'eigen') {
          if (!l.plaatje) return;
          ctx.drawImage(beeld(l, v), -v.breed / 2, -v.hoog / 2, v.breed, v.hoog);
        } else if (l.type === 'vorm') {
          this.#tekenVorm(ctx, l, v.breed, v.hoog);
        } else if (l.type === 'kleur') {
          // Nog niet binnen? Dan alvast beginnen met laden en deze ronde
          // overslaan; zodra het plaatje er is wordt er opnieuw getekend.
          if (!l.plaatje) {
            this.#laadPlaatje(l);
            return;
          }
          ctx.drawImage(beeld(l, v), -v.breed / 2, -v.hoog / 2, v.breed, v.hoog);
        } else {
          ctx.font = '700 ' + v.px + 'px ' + l.font;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(l.tekst, 0, 0);
        }
      } catch (err) {
        // Op het scherm een kapot element overslaan, zodat de rest zichtbaar
        // blijft. Voor het drukbestand juist niet: daar liever geen bestand dan
        // een bestand waar stilletjes een element uit mist.
        if (!scherm) throw err;
      } finally {
        ctx.restore();
      }
    });
  }

  /**
   * Tekent een vorm rond het nulpunt, in de kleur van de laag.
   *
   * Getekend en niet als plaatje geladen: zo kan de klant er een eigen kleur
   * aan geven, en blijft de rand haarscherp — ook op een vlag van vier meter,
   * waar een plaatje van 128 bij 128 zichtbaar zou vervagen.
   */
  #tekenVorm(ctx, l, breed, hoog) {
    const b = breed / 2;
    const h = hoog / 2;
    const dik = Math.max(2, Math.min(breed, hoog) * 0.14);
    ctx.fillStyle = l.kleur;
    ctx.strokeStyle = l.kleur;
    ctx.lineWidth = dik;
    ctx.lineJoin = 'round';
    ctx.beginPath();

    switch (l.vorm) {
      case 'cirkel':
        ctx.ellipse(0, 0, b, h, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'ring':
        ctx.ellipse(0, 0, b - dik / 2, h - dik / 2, 0, 0, Math.PI * 2);
        ctx.stroke();
        break;
      case 'vierkant':
        ctx.rect(-b, -h, breed, hoog);
        ctx.fill();
        break;
      case 'kader':
        ctx.rect(-b + dik / 2, -h + dik / 2, breed - dik, hoog - dik);
        ctx.stroke();
        break;
      case 'balk':
      case 'streep':
        ctx.rect(-b, -h, breed, hoog);
        ctx.fill();
        break;
      case 'driehoek':
        ctx.moveTo(0, -h);
        ctx.lineTo(b, h);
        ctx.lineTo(-b, h);
        ctx.closePath();
        ctx.fill();
        break;
      case 'ruit':
        ctx.moveTo(0, -h);
        ctx.lineTo(b, 0);
        ctx.lineTo(0, h);
        ctx.lineTo(-b, 0);
        ctx.closePath();
        ctx.fill();
        break;
      case 'ster': {
        // vijfpuntige ster: afwisselend een punt op de buiten- en binnenring
        const punten = 5;
        for (let i = 0; i < punten * 2; i += 1) {
          const straal = i % 2 === 0 ? 1 : 0.42;
          const hoek = (Math.PI / punten) * i - Math.PI / 2;
          const x = Math.cos(hoek) * b * straal;
          const y = Math.sin(hoek) * h * straal;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'hart': {
        // twee bogen bovenin die samenkomen in een punt onderaan
        const t = -h * 0.35;
        ctx.moveTo(0, h);
        ctx.bezierCurveTo(-b * 1.35, -h * 0.1, -b * 0.55, -h * 1.25, 0, t);
        ctx.bezierCurveTo(b * 0.55, -h * 1.25, b * 1.35, -h * 0.1, 0, h);
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'pijl': {
        const schacht = h * 0.42;
        const kop = b * 0.42;
        ctx.moveTo(-b, -schacht);
        ctx.lineTo(b - kop, -schacht);
        ctx.lineTo(b - kop, -h);
        ctx.lineTo(b, 0);
        ctx.lineTo(b - kop, h);
        ctx.lineTo(b - kop, schacht);
        ctx.lineTo(-b, schacht);
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'vlag':
        ctx.moveTo(-b, -h);
        ctx.lineTo(b, -h);
        ctx.lineTo(b * 0.42, 0);
        ctx.lineTo(b, h);
        ctx.lineTo(-b, h);
        ctx.closePath();
        ctx.fill();
        break;
      default:
        ctx.rect(-b, -h, breed, hoog);
        ctx.fill();
    }
  }

  /** Welke laag ligt er onder dit punt? Bovenste eerst. */
  #laagOpPunt(side, p) {
    if (!this.tekstToegestaan) return null;
    const ctx = this.ctx;
    const r = side.rect;
    // Bovenste laag eerst: die ligt er ook visueel bovenop.
    for (let i = 0; i < side.lagen.length; i++) {
      const l = side.lagen[i];
      if (l.type === 'tekst' && !l.tekst) continue;
      const v = this.#laagVlak(ctx, r, l);
      // terugdraaien naar het assenstelsel van de laag, dan simpel rechthoek toetsen
      const dx = p.x - v.x;
      const dy = p.y - v.y;
      const hoek = (-l.hoek * Math.PI) / 180;
      const lx = dx * Math.cos(hoek) - dy * Math.sin(hoek);
      const ly = dx * Math.sin(hoek) + dy * Math.cos(hoek);
      // Raakmarge meegroeien met de laag: op een telefoon is het doek maar een
      // paar honderd beeldpunten breed, dus een vaste marge is onraakbaar.
      const marge = Math.max(10, Math.min(v.breed, v.hoog) * 0.2);
      if (Math.abs(lx) <= v.breed / 2 + marge && Math.abs(ly) <= v.hoog / 2 + marge) return l;
    }
    return null;
  }

  /** Korte samenvatting van de lagen, voor de instructie aan de drukker. */
  #laagOmschrijving(side) {
    if (!this.tekstToegestaan || !side.lagen.length) return '';
    const fonts = Object.fromEntries(LETTERTYPEN.map((f) => [f.css, f.naam]));
    const delen = side.lagen
      .filter((l) => l.type !== 'tekst' || l.tekst)
      .map((l) => {
        const draai = l.hoek ? ', ' + Math.round(l.hoek) + ' graden gedraaid' : '';
        if (l.type === 'eigen') {
          return 'eigen afbeelding "' + (l.naam || '?') + '" (' +
            Math.round(l.grootte) + '% van de hoogte' + draai +
            (l.bronUrl ? ', bestand: ' + l.bronUrl : '') + ')';
        }
        if (l.type === 'kleur') {
          return 'kleursymbool ' + l.teken + ' (' +
            Math.round(l.grootte) + '% van de hoogte' + draai + ')';
        }
        if (l.type === 'vorm') {
          return 'vorm ' + ((l.namen && l.namen.nl) || l.vorm) + ' (' + l.kleur + ', ' +
            Math.round(l.grootte) + '% van de hoogte' + draai + ')';
        }
        return '"' + l.tekst + '" (' + (fonts[l.font] || 'Arial') + ', ' + l.kleur + ', ' +
          Math.round(l.grootte) + '% van de hoogte' + draai + ')';
      });
    return delen.length ? ' — ontwerptool: ' + delen.join(' + ') : '';
  }

  // ---------- vlaggendatabank ----------

  /** Zet aria-expanded op beide knoppen die de databank openen. */
  #libAria(side, open) {
    [side.libToggle, side.libToggleWb].forEach((k) => {
      if (k) k.setAttribute('aria-expanded', String(open));
    });
  }

  #bindLibrary(side) {
    if (!side.libToggle && !side.libToggleWb) return;

    // Onthouden waar het paneel thuishoort, zodat het na de ontwerptool
    // weer onder de rode knop komt te staan.
    if (side.libBlok) {
      side.libThuis = { ouder: side.libBlok.parentNode, na: side.libBlok.previousElementSibling };
    }

    const klap = async () => {
      const open = side.libPanel.hidden;
      side.libPanel.hidden = !open;
      this.#libAria(side, open);
      // Maar één paneel tegelijk: gaan de vlaggen open, dan gaat Symbolen dicht.
      if (open) {
        const symboolBlok = side.root.querySelector('[data-symboolblok]');
        const symboolKnop = side.root.querySelector('[data-symboolopen]');
        if (symboolBlok && !symboolBlok.hidden) {
          symboolBlok.hidden = true;
          if (symboolKnop) symboolKnop.setAttribute('aria-expanded', 'false');
        }
      }
      if (open && !side.libBuilt) {
        side.libBuilt = true;
        await this.#buildGrid(side);
      }
      if (open) side.libSearch.focus();
    };

    [side.libToggle, side.libToggleWb].forEach((k) => {
      if (k) k.addEventListener('click', klap);
    });

    side.libSearch.addEventListener('input', () => this.#filterGrid(side));
  }

  /**
   * Verhuist het paneel van de vlaggendatabank naar de werkbalk en terug.
   * Zonder dit klap je onderin een tegel open en verschijnt de lijst boven
   * in beeld, buiten het zicht.
   */
  #libVerhuis(side, naarWerkbalk) {
    if (!side.libBlok || !side.werkbalk || !side.libThuis) return;
    if (naarWerkbalk) {
      side.werkbalk.after(side.libBlok);
    } else if (side.libThuis.na) {
      side.libThuis.na.after(side.libBlok);
    } else {
      side.libThuis.ouder.prepend(side.libBlok);
    }
  }

  async #buildGrid(side) {
    const flags = await this.#flags();
    const frag = document.createDocumentFragment();
    const koppen = {
      thema: this.dataset.tGrpThema || 'Thema’s',
      provincie: this.dataset.tGrpProvincie || 'Provincies',
      gemeente: this.dataset.tGrpGemeente || 'Gemeenten',
      belgie: this.dataset.tGrpBelgie || 'België',
      land: this.dataset.tGrpLand || 'Landen',
    };
    // Bij vierhonderd vlaggen telt elke besparing per toetsaanslag. Daarom
    // onthouden we hier alvast waar het zoeken naar kijkt, in plaats van dat
    // straks vierhonderd keer uit de data-attributen te lezen.
    side.libItems = [];
    side.libKoppen = [];
    let vorigeGroep = null;
    flags.forEach((f) => {
      if (f.groep !== vorigeGroep) {
        vorigeGroep = f.groep;
        const kop = document.createElement('p');
        kop.className = 'flagcfg__libkop';
        kop.textContent = koppen[f.groep] || '';
        frag.appendChild(kop);
        side.libKoppen.push({ el: kop, groep: f.groep, verborgen: false });
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'flagcfg__libitem';
      btn.title = f.label;
      const img = document.createElement('img');
      img.src = this.flagBase + 'flag-' + f.code + '.svg';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 40;
      img.height = 30;
      const naam = document.createElement('span');
      naam.textContent = f.label;
      btn.append(img, naam);
      btn.addEventListener('click', () => this.#pickFlag(side, f));
      frag.appendChild(btn);
      side.libItems.push({
        el: btn,
        groep: f.groep,
        zoek: (f.label + ' ' + f.code + ' ' + (f.zoek || '')).toLowerCase(),
        verborgen: false,
      });
    });
    side.libGrid.appendChild(frag);
  }

  #filterGrid(side) {
    const q = side.libSearch.value.trim().toLowerCase();
    const items = side.libItems || [];
    const gevuld = {};
    let zichtbaar = 0;
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      const hit = !q || it.zoek.indexOf(q) >= 0;
      const moetWeg = !hit;
      // Alleen schrijven als het echt verandert. Zonder deze regel rekent de
      // browser bij elke toetsaanslag de opmaak van vierhonderd elementen
      // opnieuw uit, ook als er niets verandert.
      if (it.verborgen !== moetWeg) {
        it.el.hidden = moetWeg;
        it.verborgen = moetWeg;
      }
      if (hit) { zichtbaar += 1; gevuld[it.groep] = true; }
    }
    // Een kopje zonder vlaggen eronder verdwijnt mee, anders blijft er een
    // losse kop "Provincies" staan boven niets.
    (side.libKoppen || []).forEach((k) => {
      const weg = !gevuld[k.groep];
      if (k.verborgen !== weg) { k.el.hidden = weg; k.verborgen = weg; }
    });
    side.libEmpty.hidden = zichtbaar > 0;
  }

  #pickFlag(side, flag) {
    // Binnen de ontwerptool wordt een landvlag een element, net als een eigen
    // afbeelding: je kunt hem verslepen, schalen en combineren met tekst.
    // Buiten de tool blijft het zoals het was — daar vult de vlag het doek,
    // want dat is wat iemand wil die alleen een landvlag bestelt.
    if (this.classList.contains('is-vol')) {
      this.#voegVlagToe(side, flag);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (side.objectUrl) URL.revokeObjectURL(side.objectUrl);
      side.objectUrl = null;
      side.drukKenmerk = null;
      side.drukPixels = null;
      if (side.drukInput) {
        side.drukInput.value = '';
        side.drukInput.disabled = true;
      }
      side.img = img;
      side.vector = true; // vlaggen uit de databank zijn vectoren: altijd scherp
      side.fileName = flag.label;
      // Om het ontwerp later te kunnen herstellen: welke vlag het was, en dat
      // er geen eigen bestand bij hoort.
      side.vlagCode = flag.code;
      side.bronBestand = null;
      side.cx = 50;
      side.cy = 50;
      side.zoom = 1;
      side.zoomInput.value = '100';
      // landvlag: exact op vlagformaat, zoals vlaggenfabrikanten doen
      side.stretch = true;
      side.controls.hidden = false;
      side.noteEl.hidden = true;
      this.#tekenLagenLijst(side);
      side.libValue.value = flag.label + ' (' + flag.code.toUpperCase() + ')';
      side.libValue.disabled = false;
      side.nameEl.textContent = this.chosenText + ': ' + flag.label;
      side.fileInput.value = '';
      side.libPanel.hidden = true;
      this.#libAria(side, false);
      side.libGrid.querySelectorAll('.is-chosen').forEach((e) => e.classList.remove('is-chosen'));
      const btn = side.libGrid.querySelector('[data-code="' + flag.code + '"]');
      if (btn) btn.classList.add('is-chosen');
      this.#render();
    };
    img.onerror = () => {
      side.noteEl.textContent = this.noPreviewText;
      side.noteEl.hidden = false;
    };
    img.src = this.flagBase + 'flag-' + flag.code + '.svg';
  }

  // ---------- eigen upload ----------

  #onFile(side) {
    const file = side.fileInput.files && side.fileInput.files[0];
    if (side.objectUrl && !this.#beeldGedeeld(side)) URL.revokeObjectURL(side.objectUrl);
    side.img = null;
    side.objectUrl = null;

    if (side.libValue) {
      // zichtbare eigenschap: alleen de naam, niet de downloadlink
      side.libValue.value = file ? file.name : '';
      side.libValue.disabled = !file;
    }
    side.vooraf = null;
    side.fileInput.disabled = false;
    if (side.urlInput) {
      side.urlInput.value = '';
      side.urlInput.disabled = true;
    }
    if (side.libGrid) side.libGrid.querySelectorAll('.is-chosen').forEach((e) => e.classList.remove('is-chosen'));

    if (!file) {
      side.fileName = '';
      side.nameEl.textContent = '';
      side.controls.hidden = true;
      side.noteEl.hidden = true;
      this.#render();
      return;
    }

    side.stretch = false;
    side.vector = file.type === 'image/svg+xml';
    side.fileName = file.name;
    side.vlagCode = null;
    side.bronBestand = file;
    side.nameEl.textContent = file.name;
    this.#alvastUploaden(side, file);

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      side.img = img;
      side.objectUrl = url;
      side.cx = 50;
      side.cy = 50;
      side.zoom = 1;
      side.zoomInput.value = '100';
      side.controls.hidden = false;
      side.noteEl.hidden = true;
      this.#tekenLagenLijst(side);
      this.#render();
    };
    img.onerror = async () => {
      URL.revokeObjectURL(url);
      // Een PDF kan de browser niet als <img> laden, maar met pdf.js wel
      // tekenen. Dat is de moeite waard: een vector-PDF is juist het beste
      // drukbestand dat een klant kan aanleveren, en zonder voorbeeld denkt
      // hij dat de configurator stuk is.
      if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
        const bitmap = await this.#pdfNaarBeeld(file, side);
        if (bitmap) {
          side.img = bitmap;
          side.vector = true;
          side.cx = 50;
          side.cy = 50;
          side.zoom = 1;
          side.zoomInput.value = '100';
          side.controls.hidden = false;
          side.noteEl.hidden = true;
          this.#tekenLagenLijst(side);
          this.#render();
          return;
        }
      }
      // AI / EPS en mislukte PDF's: geen voorbeeld, bestand gaat wel mee
      side.controls.hidden = true;
      side.noteEl.textContent = this.noPreviewText;
      side.noteEl.hidden = false;
      this.#render();
    };
    img.src = url;
  }

  /**
   * Tekent de eerste pagina van een PDF naar een canvas, zodat de klant zijn
   * ontwerp gewoon ziet en kan plaatsen. pdf.js wordt pas opgehaald zodra er
   * echt een PDF langskomt — bezoekers die een PNG uploaden laden niets extra.
   * Mislukt het (geen internet, kapotte PDF), dan valt alles terug op de oude
   * melding en gaat het bestand nog steeds gewoon mee naar de drukker.
   */
  async #pdfNaarBeeld(file, side) {
    try {
      side.noteEl.textContent = this.pdfBezigText || this.noPreviewText;
      side.noteEl.hidden = false;
      const klaar = (belofte) =>
        Promise.race([belofte, new Promise((_, af) => setTimeout(() => af(new Error('te traag')), 12000))]);
      const pdfjs = await klaar(FlagConfigurator.#pdfBibliotheek());
      const buffer = await klaar(file.arrayBuffer());
      const doc = await klaar(pdfjs.getDocument({ data: buffer }).promise);
      const page = await klaar(doc.getPage(1));
      // Ruim renderen: de vlag is groot en dit beeld wordt ook het drukbestand.
      const basis = page.getViewport({ scale: 1 });
      const schaal = Math.min(8, Math.max(2, 2400 / Math.max(basis.width, basis.height)));
      const viewport = page.getViewport({ scale: schaal });
      const breed = Math.round(viewport.width);
      const hoog = Math.round(viewport.height);
      const canvas = document.createElement('canvas');
      canvas.width = breed;
      canvas.height = hoog;

      // pdf.js drijft zijn tekenlus met requestAnimationFrame aan, en die staat
      // stil zodra het tabblad niet zichtbaar is. Wisselt de klant tijdens het
      // uploaden even van tabblad, dan blijft hij anders naar "wordt ingelezen"
      // kijken. Daarom tijdens het tekenen terugvallen op setTimeout.
      const origRaf = window.requestAnimationFrame;
      window.requestAnimationFrame = (cb) =>
        document.hidden ? setTimeout(() => cb(performance.now()), 16) : origRaf.call(window, cb);
      try {
        await klaar(page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise);
      } finally {
        window.requestAnimationFrame = origRaf;
      }

      // De rest van de configurator verwacht iets met naturalWidth/naturalHeight.
      canvas.naturalWidth = breed;
      canvas.naturalHeight = hoog;
      return canvas;
    } catch (err) {
      window.__pdfFout = String(err && err.message ? err.message : err);
      return null;
    }
  }

  /**
   * pdf.js eenmalig laden en onthouden. Let op: een Worker mag niet
   * rechtstreeks vanaf een ander domein starten, dus we maken hem uit een
   * blob die de CDN-module importeert. Zonder die omweg blijft getDocument
   * eindeloos wachten en ziet de klant alleen "wordt ingelezen".
   */
  static #pdfBelofte = null;
  static #pdfBibliotheek() {
    if (FlagConfigurator.#pdfBelofte) return FlagConfigurator.#pdfBelofte;
    const basis = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.6.82/build/';
    FlagConfigurator.#pdfBelofte = import(basis + 'pdf.min.mjs').then((mod) => {
      const shim = new Blob([`import '${basis}pdf.worker.min.mjs';`], { type: 'text/javascript' });
      mod.GlobalWorkerOptions.workerPort = new Worker(URL.createObjectURL(shim), { type: 'module' });
      return mod;
    });
    return FlagConfigurator.#pdfBelofte;
  }

  /**
   * Zet het bestand alvast weg op ons eigen uploadpunt, terwijl de klant nog
   * aan het ontwerpen is. Lukt dat, dan hoeft bij het bestellen alleen de URL
   * mee en is de winkelwagen direct klaar. Mislukt het, dan gaat het bestand
   * gewoon op de oude manier mee — de klant merkt er niets van.
   */
  async #alvastUploaden(side, file) {
    side.vooraf = null;
    if (!this.uploadUrl || !file) return;

    const beurt = (side.uploadBeurt = (side.uploadBeurt || 0) + 1);
    try {
      const body = new FormData();
      body.append('file', file, file.name);
      const res = await fetch(this.uploadUrl, { method: 'POST', body });
      if (!res.ok) return;
      const data = await res.json();
      // klant koos intussen een ander bestand? dan dit resultaat negeren
      if (beurt !== side.uploadBeurt || !data.url) return;
      side.vooraf = data.url;
      this.#schakelBestandsveld(side);
    } catch (err) {
      /* vangnet: bestand gaat gewoon rechtstreeks mee */
    }
  }

  /** Bestand is al geupload: stuur alleen de URL mee i.p.v. het bestand zelf. */
  #schakelBestandsveld(side) {
    if (!side.vooraf) return;
    side.fileInput.disabled = true;
    if (!side.urlInput) {
      side.urlInput = document.createElement('input');
      side.urlInput.type = 'hidden';
      side.urlInput.name = side.fileInput.name;
      side.fileInput.parentNode.appendChild(side.urlInput);
    }
    side.urlInput.value = side.vooraf;
    side.urlInput.disabled = false;
  }

  // ---------- plaatsing ----------

  #action(side, act) {
    if (!side.img) return;
    side.stretch = false;
    if (act === 'clear') {
      this.#wisOntwerp(side);
      return;
    }
    if (act === 'center') {
      side.cx = 50;
      side.cy = 50;
    } else {
      const base = this.#containScale(side);
      const cover = Math.max(side.rect.w / side.img.naturalWidth, side.rect.h / side.img.naturalHeight);
      side.zoom = act === 'fill' ? cover / base : 1;
      side.zoomInput.value = String(Math.round(side.zoom * 100));
    }
    this.#render();
  }

  /**
   * Ontwerp weghalen en terug naar het lege voorbeeld.
   *
   * Zonder dit zat een klant vast aan zijn eerste keuze: een ander bestand
   * kiezen kon wel, maar helemaal terug naar blanco niet.
   */
  #wisOntwerp(side) {
    // De lijst toont een rij voor de achtergrond; die moet mee verdwijnen.
    setTimeout(() => this.#tekenLagenLijst(side), 0);
    if (side.objectUrl && !this.#beeldGedeeld(side)) URL.revokeObjectURL(side.objectUrl);
    side.objectUrl = null;
    side.img = null;
    side.vector = false;
    side.vlagCode = null;
    side.bronBestand = null;
    side.fileName = '';
    side.ppi = 0;
    side.cx = 50;
    side.cy = 50;
    side.zoom = 1;
    side.stretch = false;
    side.bg = '#ffffff';

    if (side.zoomInput) side.zoomInput.value = '100';
    if (side.bgInput) side.bgInput.value = '#ffffff';
    side.fileInput.value = '';
    side.fileInput.disabled = false;
    side.controls.hidden = true;
    side.noteEl.hidden = true;
    if (side.qualityEl) side.qualityEl.hidden = true;
    // Leeg, niet "Upload je ontwerp": die tekst staat nu in de knop zelf.
    side.nameEl.textContent = '';

    // alles wat naar de bestelling zou gaan weer loslaten
    side.vooraf = null;
    side.uploadBeurt = (side.uploadBeurt || 0) + 1;   // lopende upload negeren
    if (side.urlInput) {
      side.urlInput.value = '';
      side.urlInput.disabled = true;
    }
    side.libValue.value = '';
    side.libValue.disabled = true;
    side.libGrid.querySelectorAll('.is-chosen').forEach((e) => e.classList.remove('is-chosen'));
    side.posInput.value = '';
    side.posInput.disabled = true;
    side.drukKenmerk = null;
    side.drukPixels = null;
    if (side.drukInput) {
      side.drukInput.value = '';
      side.drukInput.disabled = true;
    }
    if (this.previewInput && !this.sides.some((z) => z !== side && z.img)) {
      this.previewInput.value = '';
      this.previewInput.disabled = true;
    }

    this.#render();
  }

  #containScale(side) {
    return Math.min(side.rect.w / side.img.naturalWidth, side.rect.h / side.img.naturalHeight);
  }

  #geometry(side) {
    const r = side.rect;
    if (side.stretch) return { x: r.x, y: r.y, w: r.w, h: r.h };
    const base = this.#containScale(side);
    const w = side.img.naturalWidth * base * side.zoom;
    const h = side.img.naturalHeight * base * side.zoom;
    return { x: r.x + (side.cx / 100) * r.w - w / 2, y: r.y + (side.cy / 100) * r.h - h / 2, w, h };
  }

  /** Kleiner dan de vlag valt er nooit buiten; groter dekt de vlag altijd volledig. */
  #clamp(side) {
    if (side.stretch) return;
    const r = side.rect;
    const base = this.#containScale(side);
    const w = side.img.naturalWidth * base * side.zoom;
    const h = side.img.naturalHeight * base * side.zoom;
    const hw = (w / 2 / r.w) * 100;
    const hh = (h / 2 / r.h) * 100;
    side.cx = Math.min(Math.max(hw, 100 - hw), Math.max(Math.min(hw, 100 - hw), side.cx));
    side.cy = Math.min(Math.max(hh, 100 - hh), Math.max(Math.min(hh, 100 - hh), side.cy));
  }

  /**
   * Klikken op de vlag opent de bestandskiezer voor precies die zijde.
   *
   * Bij een dubbelzijdige vlag staan links en rechts naast elkaar; klik je op
   * de linkerhelft dan gaat het over de linkerzijde. Zonder dit moest de klant
   * de juiste uploadknop onder de preview opzoeken, en dat is bij twee zijden
   * niet meteen duidelijk.
   *
   * Alleen op een lege zijde: staat er al een ontwerp, dan wil de klant slepen
   * en schalen, niet opnieuw uploaden.
   */
  #bindKlikOpDoek() {
    this.canvas.addEventListener('click', (e) => {
      const r = this.canvas.getBoundingClientRect();
      const { view } = this.layout;
      const x = ((e.clientX - r.left) / r.width) * view.w;
      const y = ((e.clientY - r.top) / r.height) * view.h;

      let gekozen = null;
      this.sides.forEach((side) => {
        const v = side.rect;
        if (x >= v.x && x <= v.x + v.w && y >= v.y && y <= v.y + v.h) gekozen = side;
      });
      // naast het doek geklikt: bij één zijde is dat toch die ene
      if (!gekozen && this.sides.length === 1) gekozen = this.sides[0];

      // In de ontwerptool: een tekst aantikken zet de cursor meteen in het
      // tekstveld, zodat je gelijk kunt typen (Jos, 12 sep). Niet na slepen, en
      // niet op een hoekje, want dat is om groter of kleiner te maken.
      if (this.classList.contains('is-vol')) {
        if (!gekozen || this.netGesleept) return;
        const punt = { x, y };
        if (this.#hoekjeOpPunt(gekozen, gekozen.gekozenLaag, punt) >= 0) return;
        const laag = this.#laagOpPunt(gekozen, punt);
        if (laag && laag.type === 'tekst') this.#bewerkTekst(gekozen, laag);
        return;
      }

      if (!gekozen || gekozen.img) return;

      // In de ontwerptool nooit. Daar is de klant elementen aan het schikken en
      // klikt hij voortdurend naast iets; dan is een bestandskiezer die
      // openspringt puur in de weg. Uploaden kan daar via de knop Uploaden.
      if (this.classList.contains('is-vol')) return;

      // Staat er al iets op deze kant — tekst, een symbool, een afbeelding —
      // dan is een klik bedoeld om dat te pakken, niet om te uploaden. De
      // kiezer hoort alleen open te gaan op een nog lege vlag, als uitnodiging
      // om te beginnen.
      if (gekozen.lagen && gekozen.lagen.length) return;

      // Net een tekst versleept? Dan eindigt dat gebaar ook in een klik, en
      // die mag de bestandskiezer niet openen.
      if (this.netGesleept) return;

      // Op een tekst geklikt: de klant wil die verplaatsen of aanpassen,
      // geen bestand kiezen.
      if (this.#laagOpPunt(gekozen, { x, y })) return;

      gekozen.fileInput.click();
    });
    this.#doekAanwijzer();
  }

  /**
   * Zet de cursor in het tekstveld van deze tekst, achteraan. Moet binnen de
   * klik zelf gebeuren: anders opent een iPhone het toetsenbord niet.
   */
  #bewerkTekst(side, l) {
    const lijst = side.tekstLijst;
    if (!lijst) return;
    const rij = [...lijst.querySelectorAll('.flagcfg-tekst')].find((el) => el.dataset.id === String(l.id));
    const invoer = rij && rij.querySelector('.flagcfg-tekst__invoer');
    if (!invoer) return;
    invoer.focus();
    const eind = invoer.value.length;
    try {
      invoer.setSelectionRange(eind, eind);
    } catch (err) {
      /* niet elk invoerveld ondersteunt dit */
    }
  }

  /**
   * Haalt het doek terug als de browser het beeld heeft weggegooid.
   *
   * Safari op een iPhone gooit bij geheugendruk, of na even een andere app, het
   * beeld van een canvas weg. Dan bleef de vlag leeg tot er toevallig opnieuw
   * getekend werd (Jos, 12 sep). Bij terugkeren naar de pagina en na het
   * herstellen van het tekenvlak daarom een vers tekenvlak en opnieuw tekenen.
   */
  #bindHerstelDoek() {
    const herstel = () => {
      if (!this.isConnected || !this.layout || !this.canvas) return;
      if (this.#bezigMetGebaar()) return;
      this.#zetCanvas();
      this.#render(true);
    };
    this.canvas.addEventListener('contextrestored', herstel);

    const zichtbaar = () => {
      if (!this.isConnected) {
        document.removeEventListener('visibilitychange', zichtbaar);
        return;
      }
      if (!document.hidden) herstel();
    };
    document.addEventListener('visibilitychange', zichtbaar);

    const terug = (e) => {
      if (!this.isConnected) {
        window.removeEventListener('pageshow', terug);
        return;
      }
      if (e.persisted) herstel();
    };
    window.addEventListener('pageshow', terug);
  }

  /**
   * De muisaanwijzer hoort te kloppen met wat een klik doet. Een handje boven
   * het doek belooft "klik hier om te uploaden"; zodra dat niet meer gebeurt —
   * in de ontwerptool, of als er al iets op de vlag staat — is een gewone
   * aanwijzer eerlijker.
   */
  #doekAanwijzer() {
    if (!this.canvas) return;
    const nodigt = !this.classList.contains('is-vol')
      && this.sides.some((s) => !s.img && !(s.lagen && s.lagen.length));
    const aanwijzer = nodigt ? 'pointer' : 'default';
    if (this.canvas.style.cursor !== aanwijzer) this.canvas.style.cursor = aanwijzer;
  }

  #bindGestures() {
    const punten = new Map();          // actieve vingers/muis
    let modus = null;                  // 'sleep' of 'knijp'
    let zijde = null;
    let start = {};

    const naarVlak = (e) => {
      // Tijdens het slepen de maat van het doek van het moment van oppakken.
      // Elke beweging opnieuw opvragen dwong de browser de hele pagina opnieuw op
      // te meten; dat was de hoofdoorzaak van het haperen (gemeten 18 ms per
      // beweging op een computer, op een telefoon een veelvoud).
      const r = this.gebaarRect || this.canvas.getBoundingClientRect();
      const { view } = this.layout;
      return { x: ((e.clientX - r.left) / r.width) * view.w, y: ((e.clientY - r.top) / r.height) * view.h };
    };

    // Een zijde met alleen tekst telt ook: je moet die tekst kunnen verslepen
    // zonder dat er een ontwerp onder ligt.
    const zoek = (p) =>
      this.sides.find(
        (s) =>
          (s.img || (s.lagen && s.lagen.length)) &&
          p.x >= s.rect.x && p.x <= s.rect.x + s.rect.w && p.y >= s.rect.y && p.y <= s.rect.y + s.rect.h
      );

    let sleepTekst = null;
    let sleepDoelen = null;   // uitlijnpunten, eenmalig bij het oppakken berekend
    let knijpLaag = null;     // element dat met twee vingers geschaald wordt
    let maatGewijzigd = false;

    const afstand = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const midden = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    const begrensZoom = (z) => Math.min(3, Math.max(0.2, z));
    // Zelfde grenzen als de schuifregelaar Grootte in de elementenlijst.
    const maatGrens = (l, waarde) => {
      const max = l.type === 'tekst' ? 40 : (l.type === 'eigen' ? 300 : 100);
      return Math.min(max, Math.max(4, Math.round(waarde * 2) / 2));
    };

    const zetZoom = (s, z) => {
      s.zoom = begrensZoom(z);
      s.zoomInput.value = String(Math.round(s.zoom * 100));
    };

    this.canvas.addEventListener('pointerdown', (e) => {
      const p = naarVlak(e);
      if (punten.size === 0) {
        // Een hoekje van het gekozen element mag net buiten de vlag liggen;
        // eerst daarop letten, dan pas op de zijde zelf.
        zijde = this.#zijdeMetHoekje(p) || zoek(p);
        if (!zijde) return;
      }
      punten.set(e.pointerId, p);
      if (punten.size === 1) {
        // Het slepen begint. Tot het loslaten: de maat van het doek vasthouden,
        // niets aan de pagina veranderen, en afbeeldingen op schermformaat
        // klaar hebben staan.
        this.gebaar = true;
        this.gebaarTijd = performance.now();
        this.gebaarRect = this.canvas.getBoundingClientRect();
        this.#warmSchermKopieen();
      }
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch (err) {
        /* niets */
      }

      if (punten.size === 1) {
        modus = 'sleep';
        // Ligt er een tekstlaag onder de vinger, dan versleep je die en niet
        // het ontwerp eronder. Dat is wat je verwacht als je een woord aanraakt.
        sleepTekst = this.#laagOpPunt(zijde, p);
        sleepDoelen = sleepTekst ? this.#uitlijnDoelen(zijde, sleepTekst) : null;
        // Voor het kader om het element. Alleen bij een laag: sleep je de
        // vullende achtergrondafbeelding, dan zou een kader om de hele vlag
        // niets toevoegen.
        zijde.sleepLaag = sleepTekst;
        start = sleepTekst
          ? { x: p.x, y: p.y, cx: sleepTekst.cx, cy: sleepTekst.cy }
          : { x: p.x, y: p.y, cx: zijde.cx, cy: zijde.cy };

        // Een hoekje van het gekozen element vastgepakt? Dan wordt het groter of
        // kleiner vanuit zijn midden in plaats van verschoven. Alleen de grootte
        // verandert, nooit de verhouding: er wordt niets uitgerekt.
        const hoekje = this.classList.contains('is-vol') ? this.#hoekjeOpPunt(zijde, zijde.gekozenLaag, p) : -1;
        if (hoekje >= 0) {
          modus = 'hoek';
          sleepTekst = zijde.gekozenLaag;
          sleepDoelen = null;
          zijde.sleepLaag = sleepTekst;
          const m = this.#hoekpunten(zijde, sleepTekst).midden;
          start = { m, d: Math.max(1, Math.hypot(p.x - m.x, p.y - m.y)), grootte: sleepTekst.grootte };
        } else if (this.classList.contains('is-vol')) {
          // Aanraken kiest het element; naast een element wist het de keuze.
          zijde.gekozenLaag = sleepTekst;
        }
      } else if (punten.size === 2 && zijde) {
        // Lag de eerste vinger op een element, dan maakt knijpen dat element
        // groter of kleiner. Anders, zoals altijd, het ontwerp eronder.
        knijpLaag = sleepTekst && zijde.lagen.includes(sleepTekst) ? sleepTekst : null;
        sleepTekst = null;
        zijde.sleepLaag = knijpLaag;
        const [a, b] = [...punten.values()];
        modus = 'knijp';
        start = {
          d: afstand(a, b) || 1, m: midden(a, b), zoom: zijde.zoom, cx: zijde.cx, cy: zijde.cy,
          grootte: knijpLaag ? knijpLaag.grootte : 0,
        };
      }
      // Alleen het ontwerp verliest zijn "vul de vlag"-stand door slepen; een
      // tekst verschuiven of een element schalen mag daar niets aan veranderen.
      if (zijde && !sleepTekst && !knijpLaag) zijde.stretch = false;
      this.canvas.classList.add('is-dragging');
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (!punten.has(e.pointerId) || !zijde) return;
      punten.set(e.pointerId, naarVlak(e));

      if (modus === 'sleep' && punten.size === 1) {
        const p = punten.get(e.pointerId);
        // Vanaf een paar eenheden beweging noemen we het slepen. De klik die
        // daarna volgt is dan geen bedoelde klik meer.
        if (Math.hypot(p.x - start.x, p.y - start.y) > 4) this.netGesleept = true;
        const doel = sleepTekst || zijde;
        doel.cx = start.cx + ((p.x - start.x) / zijde.rect.w) * 100;
        doel.cy = start.cy + ((p.y - start.y) / zijde.rect.h) * 100;
        if (sleepTekst) {
          // tekst mag tot de rand, maar niet erbuiten verdwijnen
          doel.cx = Math.min(100, Math.max(0, doel.cx));
          doel.cy = Math.min(100, Math.max(0, doel.cy));

          // Uitlijnen op de vlag en op de andere elementen. Alleen voor lagen:
          // het geuploade ontwerp eronder vult meestal de hele vlag, en daar
          // valt niets zinnigs mee uit te lijnen.
          const uit = this.#uitlijnen(zijde, sleepTekst, doel.cx, doel.cy, sleepDoelen);
          doel.cx = uit.cx;
          doel.cy = uit.cy;
          zijde.hulplijnen = uit.lijnen;
        }
        this.#tekenInFrame();
      } else if (modus === 'hoek' && punten.size === 1) {
        const p = punten.get(e.pointerId);
        const d = Math.hypot(p.x - start.m.x, p.y - start.m.y);
        sleepTekst.grootte = maatGrens(sleepTekst, start.grootte * (d / start.d));
        maatGewijzigd = true;
        this.netGesleept = true;
        this.#tekenInFrame();
      } else if (modus === 'knijp' && punten.size >= 2 && knijpLaag) {
        const [a, b] = [...punten.values()];
        knijpLaag.grootte = maatGrens(knijpLaag, start.grootte * (afstand(a, b) / start.d));
        maatGewijzigd = true;
        this.netGesleept = true;
        this.#tekenInFrame();
      } else if (modus === 'knijp' && punten.size >= 2) {
        const [a, b] = [...punten.values()];
        const m = midden(a, b);
        zetZoom(zijde, start.zoom * (afstand(a, b) / start.d));
        // schuif mee met het midden tussen de vingers
        zijde.cx = start.cx + ((m.x - start.m.x) / zijde.rect.w) * 100;
        zijde.cy = start.cy + ((m.y - start.m.y) / zijde.rect.h) * 100;
        this.#tekenInFrame();
      }
    });

    const stop = (e) => {
      if (!punten.has(e.pointerId)) return;
      punten.delete(e.pointerId);
      try {
        this.canvas.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* niets */
      }

      if (punten.size === 0) {
        modus = null;
        // De hulplijnen en het selectiekader horen alleen bij het slepen zelf;
        // loslaten wist ze allebei.
        this.sides.forEach((s) => { s.hulplijnen = null; s.sleepLaag = null; });
        // Groter of kleiner gemaakt met een hoekje of met knijpen: de
        // schuifregelaar in de lijst moet de nieuwe waarde laten zien.
        if (maatGewijzigd && zijde) this.#tekenLagenLijst(zijde);
        maatGewijzigd = false;
        knijpLaag = null;
        zijde = null;
        sleepTekst = null;
        sleepDoelen = null;
        this.canvas.classList.remove('is-dragging');
        // De klik komt vlak na het loslaten; daarna mag klikken weer gewoon.
        if (this.netGesleept) setTimeout(() => (this.netGesleept = false), 0);
        // Slepen voorbij. Een nog wachtende tekenbeurt vervalt: nu komt de
        // volledige, met scherpte, knoppen en de regels voor de bestelling.
        if (this.frameVraag) cancelAnimationFrame(this.frameVraag);
        this.frameVraag = 0;
        this.gebaar = false;
        this.gebaarRect = null;
        this.#render();
      } else if (punten.size === 1 && zijde && knijpLaag) {
        // Een element geschaald met twee vingers en er een losgelaten: niets
        // meer doen tot ook de tweede los is, anders schiet het element weg.
        modus = null;
      } else if (punten.size === 1 && zijde) {
        // van knijpen terug naar slepen met de overgebleven vinger
        const p = [...punten.values()][0];
        modus = 'sleep';
        sleepTekst = null;
        zijde.sleepLaag = null;
        start = { x: p.x, y: p.y, cx: zijde.cx, cy: zijde.cy };
      }
    };
    this.canvas.addEventListener('pointerup', stop);
    this.canvas.addEventListener('pointercancel', stop);

    // Met een muis een ander pijltje boven een hoekje, zodat je ziet dat je daar
    // kunt schalen.
    this.canvas.addEventListener('pointermove', (e) => {
      if (punten.size || e.pointerType !== 'mouse' || !this.classList.contains('is-vol')) return;
      const p = naarVlak(e);
      const s = this.#zijdeMetHoekje(p);
      const i = s ? this.#hoekjeOpPunt(s, s.gekozenLaag, p) : -1;
      const aanwijzer = i < 0 ? 'default' : (i === 0 || i === 3 ? 'nwse-resize' : 'nesw-resize');
      // Alleen schrijven als hij verandert: elke schrijfactie laat de browser de
      // pagina opnieuw opmaken.
      if (this.canvas.style.cursor !== aanwijzer) this.canvas.style.cursor = aanwijzer;
    });

    // desktop: ctrl/cmd + scrollwiel schaalt (zonder ctrl blijft de pagina gewoon scrollen)
    this.canvas.addEventListener(
      'wheel',
      (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        const s = zoek(naarVlak(e));
        if (!s) return;
        e.preventDefault();
        s.stretch = false;
        zetZoom(s, s.zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
        this.#render();
      },
      { passive: false }
    );
  }

  // ---------- tekenen ----------

  /**
   * Tekent tijdens het slepen hooguit één keer per beeldverversing.
   *
   * Een muis of vinger geeft vaak meer bewegingen door dan het scherm ververst.
   * Elke beweging meteen tekenen stapelt werk op dat niemand ziet, en dan loopt
   * het beeld achter de vinger aan.
   */
  #tekenInFrame() {
    this.gebaarTijd = performance.now();
    if (this.frameVraag) return;
    this.frameVraag = requestAnimationFrame(() => {
      this.frameVraag = 0;
      this.#render(true);
    });
  }

  /** Sleept er nu een vinger of muis op het doek? */
  #bezigMetGebaar() {
    if (!this.gebaar) return false;
    // Vangnet: komt het loslaten nooit binnen, dan niet eeuwig stil blijven.
    if (performance.now() - (this.gebaarTijd || 0) > 15000) {
      this.gebaar = false;
      this.gebaarRect = null;
      return false;
    }
    return true;
  }

  /**
   * Zwaar werk (controlebeeld, drukbestand van duizenden pixels) nooit midden in
   * een sleepbeweging. Pakt de klant vlak na het loslaten het volgende element,
   * dan wacht dit tot die beweging klaar is. Gemeten: het drukbestand alleen al
   * was een blokkade van 444 ms op een computer.
   */
  #naGebaar(sleutel, taak) {
    if (this.#bezigMetGebaar()) {
      clearTimeout(this[sleutel]);
      this[sleutel] = setTimeout(() => this.#naGebaar(sleutel, taak), 250);
      return;
    }
    taak();
  }

  /**
   * Een kopie van een afbeelding op de maat waarop hij nu in beeld staat.
   *
   * Een foto van 5000 pixels of een getekende landvlag (SVG) bij elke beweging
   * opnieuw verkleinen is op een telefoon zwaar; een kant-en-klare kopie op
   * schermformaat tekenen niet. Alleen voor het scherm: het drukbestand en de
   * vergroting tekenen altijd het origineel (zie #tekenLagen).
   *
   * Tijdens het slepen mag een kopie van net een andere maat gebruikt worden;
   * bij knijpen of een hoekje verandert de maat elke beweging. Daarbuiten alleen
   * een kopie die precies past. Past hij niet, dan nu het origineel, en wordt de
   * kopie even later op de achtergrond gemaakt.
   */
  #schermKopie(bron, breed, hoog, snel) {
    if (!bron || !(bron.naturalWidth || bron.width)) return null;
    const pxB = Math.round(breed * DPR);
    const pxH = Math.round(hoog * DPR);
    if (pxB < 1 || pxH < 1 || pxB > 4096 || pxH > 4096) return null;
    const oud = this.schermKopieen && this.schermKopieen.get(bron);
    if (oud && (snel || (Math.abs(oud.b - pxB) <= 1 && Math.abs(oud.h - pxH) <= 1))) return oud.doek;
    if (!snel) this.#kopieLater(bron, pxB, pxH);
    return null;
  }

  #kopieLater(bron, pxB, pxH) {
    if (!this.kopieWacht) this.kopieWacht = new Map();
    this.kopieWacht.set(bron, [pxB, pxH]);
    clearTimeout(this.kopieTimer);
    this.kopieTimer = setTimeout(() => this.#maakWachtendeKopieen(), 180);
  }

  #maakWachtendeKopieen() {
    if (this.#bezigMetGebaar()) {
      this.kopieTimer = setTimeout(() => this.#maakWachtendeKopieen(), 250);
      return;
    }
    const lijst = this.kopieWacht || new Map();
    this.kopieWacht = new Map();
    lijst.forEach(([pxB, pxH], bron) => this.#maakKopie(bron, pxB, pxH));
  }

  #maakKopie(bron, pxB, pxH) {
    try {
      const doek = document.createElement('canvas');
      doek.width = pxB;
      doek.height = pxH;
      const k = doek.getContext('2d');
      k.imageSmoothingQuality = 'high';
      k.drawImage(bron, 0, 0, pxB, pxH);
      if (!this.schermKopieen) this.schermKopieen = new WeakMap();
      // De vorige kopie van deze afbeelding meteen vrijgeven. Een iPhone ruimt
      // canvasgeheugen pas laat op, en loopt dan tegen zijn grens aan.
      const vorige = this.schermKopieen.get(bron);
      if (vorige && vorige.doek) {
        vorige.doek.width = 0;
        vorige.doek.height = 0;
      }
      this.schermKopieen.set(bron, { b: pxB, h: pxH, doek });
      return doek;
    } catch (err) {
      return null;
    }
  }

  /**
   * Bij het oppakken: ontbrekende schermkopieën meteen maken, zodat al het
   * slepen daarna licht blijft. Meestal staan ze er al (#kopieLater).
   */
  #warmSchermKopieen() {
    const { view } = this.layout;
    const zorg = (bron, breed, hoog) => {
      if (!bron || !(bron.naturalWidth || bron.width)) return;
      const pxB = Math.round(breed * DPR);
      const pxH = Math.round(hoog * DPR);
      if (pxB < 1 || pxH < 1 || pxB > 4096 || pxH > 4096) return;
      const oud = this.schermKopieen && this.schermKopieen.get(bron);
      if (oud && Math.abs(oud.b - pxB) <= 1 && Math.abs(oud.h - pxH) <= 1) return;
      this.#maakKopie(bron, pxB, pxH);
    };
    this.sides.forEach((side) => {
      if (!side.rect || side.rect.x >= view.w) return;
      if (side.img && side.img.naturalWidth) {
        const g = this.#geometry(side);
        zorg(side.img, g.w, g.h);
      }
      if (!this.tekstToegestaan || !side.lagen) return;
      side.lagen.forEach((l) => {
        if ((l.type !== 'eigen' && l.type !== 'kleur') || !l.plaatje) return;
        const v = this.#laagVlak(this.ctx, side.rect, l);
        zorg(l.plaatje, v.breed, v.hoog);
      });
    });
  }

  #render(skipOutput) {
    const ctx = this.ctx;
    const { view, mast, foot, ticks } = this.layout;
    // Altijd vanaf de basisstand beginnen. Liep een eerdere tekenbeurt halverwege
    // op een fout, dan bleef er anders een verschuiving of draaiing staan en viel
    // de hele vlag voortaan buiten beeld.
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, view.w, view.h);
    // Tijdens het slepen alleen tekenen en niets in de pagina schrijven; het
    // loslaten tekent daarna alles bij (scherpte, knoppen, bestelregels).
    const stil = this.#bezigMetGebaar();

    // Hier meeliften: bij elke hertekening kan de stand veranderd zijn — een
    // laag erbij, een upload, of de ontwerptool die opengaat.
    if (!stil) this.#doekAanwijzer();

    this.sides.forEach((side) => {
      const r = side.rect;

      // In de focusstand staat de andere zijde buiten beeld. Niet tekenen, maar
      // de scherpte en de vergrootknop wel bijhouden.
      if (r.x >= view.w) {
        if (!stil) {
          this.#toonScherpte(side);
          this.#toonPreviewKnop();
        }
        return;
      }

      const pad = () => {
        ctx.beginPath();
        if (this.isBeach) {
          const p = vlagPad(this.vorm, r);
          ctx.moveTo(p[0][0], p[0][1]);
          for (let i = 1; i < p.length; i++) ctx.lineTo(p[i][0], p[i][1]);
          ctx.closePath();
        } else {
          ctx.rect(r.x, r.y, r.w, r.h);
        }
      };

      pad();
      // Ook zonder geuploade afbeelding telt de gekozen achtergrondkleur:
      // met de ontwerptool is een egaal gekleurde vlag met tekst of een
      // symbool een gewone bestelling. Stond hier eerder wit afgedwongen,
      // terwijl het drukbestand de kleur wel gebruikte — dan drukt de
      // drukker iets anders dan de klant op zijn scherm zag.
      ctx.fillStyle = side.bg || '#ffffff';
      ctx.fill();

      if (side.img) {
        this.#clamp(side);
        const g = this.#geometry(side);
        ctx.save();
        try {
          pad();
          ctx.clip();
          ctx.drawImage(this.#schermKopie(side.img, g.w, g.h, stil) || side.img, g.x, g.y, g.w, g.h);
        } catch (err) {
          /* afbeelding (nog) niet te tekenen; de rest van de vlag wel */
        } finally {
          ctx.restore();
        }
      }

      // Tekst ligt bovenop het ontwerp, en wordt net als het ontwerp
      // afgesneden op de vlagvorm.
      if (side.lagen && side.lagen.length) {
        ctx.save();
        try {
          pad();
          ctx.clip();
          this.#tekenLagen(ctx, side, r);
          // Binnen dezelfde afsnijding, zodat een hulplijn bij een beachflag of
          // een gepunte vlag niet buiten het doek uitsteekt.
          this.#tekenHulplijnen(ctx, side, r);
          this.#tekenSelectie(ctx, side, r);
        } finally {
          ctx.restore();
        }
      }

      pad();
      ctx.strokeStyle = '#dcdde0';
      ctx.lineWidth = 2;
      ctx.stroke();

      if (!stil) {
        this.#toonScherpte(side);
        this.#toonPreviewKnop();
      }
    });

    if (this.layout.spandoek) {
      this.#tekenRingen(ctx, this.layout.flags[0]);
      if (!stil) this.#toonLeegMelding();
      if (!skipOutput) this.#output();
      return;
    }

    ctx.fillStyle = '#6f7276';
    this.#roundRect(ctx, mast.x, mast.y, mast.w, mast.h, mast.w / 2);
    if (foot) this.#roundRect(ctx, foot.x, foot.y, foot.w, foot.h, foot.h / 2);

    ctx.strokeStyle = '#b9bbbf';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    const dubbel = this.sides.length > 1;
    const focus = this.layout.focus;
    ticks.forEach((y) => {
      ctx.beginPath();
      if (focus === 0) {
        // mast rechts van de linkerzijde: streepjes naar de vlag toe
        ctx.moveTo(mast.x - 10, y);
        ctx.lineTo(mast.x, y);
      } else if (focus === 1) {
        ctx.moveTo(mast.x + mast.w, y);
        ctx.lineTo(mast.x + mast.w + 10, y);
      } else if (dubbel) {
        ctx.moveTo(mast.x - 18, y);
        ctx.lineTo(mast.x, y);
        ctx.moveTo(mast.x + mast.w, y);
        ctx.lineTo(mast.x + mast.w + 18, y);
      } else {
        ctx.moveTo(mast.x + mast.w, y);
        ctx.lineTo(mast.x + mast.w + 18, y);
      }
      ctx.stroke();
    });

    if (!stil) {
      this.#toonLeegMelding();
      this.#toonOntwerpStatus();
    }
    if (!skipOutput) this.#output();
  }

  #toonLeegMelding() {
    if (!this.emptyEl) return;
    const verberg = this.sides.some((s) => s.img || this.#heeftLagen(s));
    if (this.emptyEl.hidden !== verberg) this.emptyEl.hidden = verberg;
  }

  /**
   * Rekent uit hoeveel beeldpunten er per gedrukte centimeter overblijven en
   * zegt er iets over in gewone taal. Een logo van 800 px op een vlag van
   * 150 cm is nu eenmaal 14 ppi — dat wil je weten vóór je bestelt, niet erna.
   */
  #toonScherpte(side) {
    const el = side.qualityEl;
    if (!el) return;

    // De vullende afbeelding van de productpagina.
    if (!side.img || side.vector || !side.img.naturalWidth) {
      side.ppi = 0;
    } else {
      const r = side.rect;
      const g = this.#geometry(side);
      const cmBreed = this.flagCm.w * (g.w / r.w);
      side.ppi = Math.round(side.img.naturalWidth / (cmBreed / 2.54));
    }

    // En de eigen afbeeldingen die in de ontwerptool geplaatst zijn. Daar
    // sleept iemand een logo groter tot het te grof wordt, dus die moet deze
    // regel net zo goed volgen. De slechtste van de twee wordt getoond.
    // De stipjes per element tonen de wérkelijke scherpte van dat element,
    // ongewogen: de klant moet kunnen zien wat hij heeft staan. De regel
    // eronder vat samen wat dat voor de vlag als geheel betekent.
    this.#toonStippen(side);

    const laag = this.#laagScherpte(side);
    const uitLaag = laag.ppi && (!side.ppi || laag.ppi < side.ppi);
    const ppi = uitLaag ? laag.ppi : side.ppi;

    if (!ppi) {
      if (!el.hidden) el.hidden = true;
      // ook hierlangs: wie zijn te grove ontwerp weghaalt of vervangt door een
      // vectorbestand moet de rem meteen kwijt zijn.
      this.#bewaakScherpte();
      return;
    }

    const band = this.#scherpteBand(ppi);
    const tekst = {
      best: this.dataset.tQBest,
      good: this.dataset.tQGood,
      fair: this.dataset.tQFair,
      poor: this.dataset.tQPoor,
    }[band];

    // Alleen schrijven wat anders is: dit loopt bij elke tekenbeurt langs.
    const klasse = 'flagcfg__quality is-' + band;
    const regel = (tekst || '') + ' (' + ppi + ' ppi)';
    if (el.className !== klasse) el.className = klasse;
    if (el.textContent !== regel) el.textContent = regel;
    if (el.hidden) el.hidden = false;
    this.#bewaakScherpte();
  }

  /**
   * Houdt bestellingen tegen waar de drukker niets mee kan.
   *
   * Aanleiding: waarschuwen alleen bleek niet genoeg. Twee vlaggen van
   * 150 x 100 cm zijn de deur uit gegaan op 26 en 30 ppi — allebei een door
   * AI gemaakte afbeelding van pakweg 1500 pixels, uitgerekt over anderhalve
   * meter. De klant zag de waarschuwing en bestelde toch.
   *
   *   onder 18 ppi  : stevige waarschuwing, klant moet uitdrukkelijk bevestigen
   *   18 tot 36 ppi : mildere waarschuwing, ook met bevestiging
   *   vanaf 36 ppi  : niets aan de hand
   *
   * Jos wil bewust niet hard blokkeren: de klant houdt zijn eigen keuze. De
   * bevestiging wordt daarom in de bestelling vastgelegd (zie #zetPlaatsing),
   * zodat achteraf vaststaat dat het een bewuste keuze was en niet een fout
   * die de winkel heeft laten passeren.
   *
   * De slechtste zijde bepaalt. Een zijde zonder eigen afbeelding telt niet
   * mee: tekst, vormen en symbolen zijn vectortekeningen en op elk formaat
   * scherp.
   */
  /**
   * De scherpte van een eigen afbeelding die ín de ontwerptool is geplaatst.
   * De vullende afbeelding van de productpagina wordt elders gemeten; hier
   * gaat het om de losse elementen, die de klant zelf groter kan slepen.
   *
   * Tekst, vormen, symbolen en vlaggen uit de databank tellen niet mee: dat
   * zijn tekeningen en die blijven op elk formaat scherp.
   */
  /** De band waar een ppi in valt; één plek, zodat stip en regel niet uiteenlopen. */
  #scherpteBand(ppi) {
    if (ppi >= 72) return 'best';
    if (ppi >= 36) return 'good';
    if (ppi >= 18) return 'fair';
    return 'poor';
  }

  /**
   * Werkt de stipjes in de elementenlijst bij. Raakt alleen aan wat verandert,
   * want dit loopt bij elke tekenbeurt langs — ook tijdens het slepen.
   */
  #toonStippen(side) {
    if (!side.lagen) return;
    const metingen = this.#laagMetingen(side);
    const perLaag = new Map(metingen.map((m) => [m.laag, m]));

    for (const l of side.lagen) {
      const stip = l.stipEl;
      if (!stip) continue;
      const m = perLaag.get(l);
      if (!m) {
        if (!stip.hidden) stip.hidden = true;
        continue;
      }
      const band = this.#scherpteBand(m.ppi);
      const tekst = m.ppi + ' ppi';
      if (stip.dataset.band !== band) {
        stip.dataset.band = band;
        stip.className = 'flagcfg-tekst__scherpte is-' + band;
      }
      if (stip.textContent !== tekst) stip.textContent = tekst;
      if (stip.hidden) stip.hidden = false;
    }
  }

  #laagMetingen(side) {
    const uit = [];
    if (!this.tekstToegestaan || !side.lagen || !side.lagen.length) return uit;
    const r = side.rect;
    if (!r || !r.w || !r.h || !this.flagCm || !this.flagCm.w) return uit;

    for (const l of side.lagen) {
      if (l.type !== 'eigen' || l.vector) continue;
      const p = l.plaatje;
      if (!p || !p.naturalWidth) continue;
      const hoog = (l.grootte / 100) * r.h;
      const breed = hoog * (l.verhouding || 1);
      const cmBreed = this.flagCm.w * (breed / r.w);
      if (cmBreed <= 0) continue;
      uit.push({
        laag: l,
        naam: l.naam || '',
        ppi: Math.round(p.naturalWidth / (cmBreed / 2.54)),
        // hoeveel van het doek dit element beslaat
        deel: (breed * hoog) / (r.w * r.h),
      });
    }
    return uit;
  }

  /**
   * Het oordeel over de hele vlag, gewogen naar hoe groot een element erop
   * staat. Een wazig accent van drie procent is iets anders dan een vervaagde
   * foto die het hele doek vult; zonder die weging krijgt de klant bij elk
   * klein icoontje een rode melding en gaat hij ze negeren.
   *
   * Onder KLEIN_DEEL komt een element niet verder dan "krap" — het valt op de
   * vlag simpelweg niet genoeg op om hem af te keuren.
   */
  #laagScherpte(side) {
    const KLEIN_DEEL = 0.05;
    const metingen = this.#laagMetingen(side);
    let laagste = 0;
    let naam = '';
    for (const m of metingen) {
      const gewogen = m.deel < KLEIN_DEEL ? Math.max(m.ppi, 18) : m.ppi;
      if (!laagste || gewogen < laagste) {
        laagste = gewogen;
        naam = m.naam;
      }
    }
    return { ppi: laagste, naam: naam, aantal: metingen.length };
  }

  #bewaakScherpte() {
    const rij = this.querySelector('[data-scherpte]');
    if (!rij) return;
    const tekstEl = rij.querySelector('[data-scherptetekst]');
    const akkoordRij = rij.querySelector('[data-scherpteakkoordrij]');
    const vinkje = rij.querySelector('[data-scherpteakkoord]');

    // De slechtste van alles wat er ligt: de vullende afbeelding én de eigen
    // afbeeldingen die in de ontwerptool geplaatst zijn.
    const gemeten = [];
    this.sides.forEach((s) => {
      if (s.ppi > 0) gemeten.push(s.ppi);
      const laag = this.#laagScherpte(s);
      if (laag.ppi > 0) gemeten.push(laag.ppi);
    });
    const laagste = gemeten.length ? Math.min(...gemeten) : 0;

    let stand = 'vrij';
    if (laagste > 0 && laagste < 18) stand = 'blok';
    else if (laagste > 0 && laagste < 36) stand = 'akkoord';

    // Deze methode loopt bij elke tekenbeurt langs, ook tijdens het slepen.
    // Verandert er niets, dan het scherm met rust laten — anders kost het
    // onnodig werk op een telefoon.
    if (stand === this.scherpteStand) {
      this.#zetBestelknop();
      return;
    }
    if (vinkje) vinkje.checked = false;
    this.scherpteStand = stand;

    if (stand === 'vrij') {
      rij.hidden = true;
    } else {
      rij.hidden = false;
      const laag = stand === 'blok';
      rij.classList.toggle('is-blok', laag);
      if (tekstEl) {
        tekstEl.textContent = laag
          ? (this.dataset.tResBlok || '')
          : (this.dataset.tResKrap || '');
      }
      // Ook bij de laagste stand mag de klant zelf beslissen; alleen de toon
      // is scherper, zodat het geen achteloos vinkje wordt.
      const jaTekst = rij.querySelector('[data-scherpteakkoordtekst]');
      if (jaTekst) {
        jaTekst.textContent = laag
          ? (this.dataset.tResJaLaag || '')
          : (this.dataset.tResJa || '');
      }
      if (akkoordRij) akkoordRij.hidden = false;
    }

    this.#zetBestelknop();
  }

  /**
   * Eén plek die bepaalt of de bestelknop aan of uit staat. Het uploaden zet
   * hem ook uit; zonder deze gedeelde plek zette de een hem weer aan terwijl
   * de ander hem net had uitgezet.
   */
  #zetBestelknop() {
    const form = this.closest('form');
    const knop = form && form.querySelector('button[type="submit"]');
    if (!knop) return;

    if (this.bezigMetUploaden) {
      knop.disabled = true;
      return;
    }
    const vinkje = this.querySelector('[data-scherpteakkoord]');
    const moetBevestigen = this.scherpteStand === 'blok' || this.scherpteStand === 'akkoord';
    knop.disabled = !!(moetBevestigen && vinkje && !vinkje.checked);
  }

  /**
   * De regel in de bestelling die vastlegt dát de klant een krappe resolutie
   * bewust heeft geaccepteerd. Zonder die regel is achteraf niet te zien of
   * het een bewuste keuze was of iets dat is doorgeglipt — en dat is precies
   * wat de drukker wil weten.
   *
   * Geldt ook voor een ontwerp dat alleen uit tool-elementen bestaat: daar is
   * geen vullende afbeelding, maar een te groot gesleept logo telt net zo goed.
   */
  #akkoordNotitie(side) {
    const laag = this.#laagScherpte(side);
    const ergste = (laag.ppi && (!side.ppi || laag.ppi < side.ppi)) ? laag.ppi : side.ppi;
    if (!ergste || ergste >= 36) return '';
    const vinkje = this.querySelector('[data-scherpteakkoord]');
    if (!vinkje || !vinkje.checked) return '';

    // Niet één getal maar de onderdelen zelf: dan ziet de drukker om welk
    // element het gaat en hoeft hij niet te raden waar de korrel vandaan komt.
    const zwak = this.#laagMetingen(side)
      .filter((m) => m.ppi < 36)
      .sort((a, b) => a.ppi - b.ppi)
      .map((m) => {
        const pct = m.deel * 100;
        const deel = pct < 1 ? 'minder dan 1' : String(Math.round(pct));
        return (m.naam || 'afbeelding') + ' ' + m.ppi + ' ppi (' + deel + '% van de vlag)';
      });
    if (side.ppi && side.ppi < 36) {
      zwak.unshift('vullende afbeelding ' + side.ppi + ' ppi');
    }
    const details = zwak.length ? ' [' + zwak.join('; ') + ']' : '';

    return ergste < 18
      ? ' — LAGE RESOLUTIE, KLANT UITDRUKKELIJK AKKOORD' + details
      : ' — krappe resolutie, klant akkoord' + details;
  }

  #bindScherpteRem() {
    const vinkje = this.querySelector('[data-scherpteakkoord]');
    if (!vinkje) return;
    vinkje.addEventListener('change', () => {
      this.#zetBestelknop();
      // Ook de omschrijving voor de drukker opnieuw opbouwen: daar hoort in te
      // staan dát de klant akkoord ging. Zonder deze regel bleef die tekst
      // achter bij het vinkje.
      this.#output();
    });
  }

  /**
   * Zeilringen om de vijftig centimeter, zoals de drukkerij ze standaard zet.
   * Ze staan bewust ín het doek getekend: dan ziet de klant meteen hoeveel
   * ruimte hij aan de rand moet vrijhouden.
   */
  #tekenRingen(ctx, r) {
    const inzet = 13;
    const perRij = Math.max(2, Math.round(this.flagCm.w / 50) + 1);
    const perKolom = Math.max(2, Math.round(this.flagCm.h / 50) + 1);

    const punten = [];
    for (let i = 0; i < perRij; i++) {
      const x = r.x + inzet + ((r.w - inzet * 2) * i) / (perRij - 1);
      punten.push([x, r.y + inzet], [x, r.y + r.h - inzet]);
    }
    for (let j = 1; j < perKolom - 1; j++) {
      const y = r.y + inzet + ((r.h - inzet * 2) * j) / (perKolom - 1);
      punten.push([r.x + inzet, y], [r.x + r.w - inzet, y]);
    }

    punten.forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, 6.5, 0, Math.PI * 2);
      ctx.fillStyle = '#f4f4f5';
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = '#9a9da2';
      ctx.stroke();
    });
  }

  #roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
    else ctx.rect(x, y, w, h);
    ctx.fill();
  }

  // ---------- doorgeven aan de bestelling ----------

  #output() {
    // Ook een bestand zonder voorbeeld (bijvoorbeeld .ai) komt hier langs, en
    // daarvoor wordt niet getekend.
    if (!this.#bezigMetGebaar()) this.#toonOntwerpStatus();

    // Laatste controle vlak voor er iets naar de bestelling gaat: wat in het
    // formulier staat is wat de klant koopt. Wijkt de preview daarvan af, dan
    // eerst gelijktrekken — #zetMaat tekent opnieuw en komt hier meteen terug.
    if (!this.isBeach && this.variantVeld) {
      const cm = this.#maatVan(this.variantVeld.value);
      if (cm && (cm.w !== this.flagCm.w || cm.h !== this.flagCm.h)) {
        this.#zetMaat(cm);
        return;
      }
    }

    this.sides.forEach((side) => {
      const tekstUitleg = this.#laagOmschrijving(side);
      if (!side.img) {
        // Alleen tekst, geen ontwerp: dan is er nog steeds iets te drukken.
        if (tekstUitleg) {
          const soortT = this.isSpandoek ? 'Spandoek' : 'Vlag';
          side.posInput.value =
            soortT + ' ' + this.flagCm.w + 'x' + this.flagCm.h + ' cm - egale achtergrond ' + side.bg + tekstUitleg +
            this.#akkoordNotitie(side) +
            (side.drukPixels ? ' — DRUKKLAAR BESTAND MEEGELEVERD (' + side.drukPixels + ')' : '');
          side.posInput.disabled = false;
          return;
        }
        side.posInput.value = '';
        side.posInput.disabled = true;
        return;
      }
      const r = side.rect;
      const g = this.#geometry(side);
      // leesbare instructie voor de drukker (en nog steeds exact)
      const pct = (v) => v.toFixed(1).replace('.', ',') + '%';
      const vult =
        Math.abs(g.w - r.w) < 0.5 && Math.abs(g.h - r.h) < 0.5 && Math.abs(g.x - r.x) < 0.5 && Math.abs(g.y - r.y) < 0.5;
      const namen = { veer: 'veervorm', druppel: 'druppelvorm', recht: 'rechthoekig' };
      const soort = this.isSpandoek ? 'Spandoek' : 'Vlag';
      const maat = this.isBeach
        ? 'Beachflag ' + (namen[this.vorm] || this.vorm) + ' - '
        : soort + ' ' + this.flagCm.w + 'x' + this.flagCm.h + ' cm - ';
      const scherpte = (side.ppi ? ', ontwerp ca. ' + side.ppi + ' ppi op ware grootte' : '')
        + this.#akkoordNotitie(side);
      // Staat er een drukklaar bestand klaar, dan hoeft de drukker niets meer
      // in te passen; de percentages blijven erbij staan als controle.
      const drukklaar = side.drukPixels
        ? ' — DRUKKLAAR BESTAND MEEGELEVERD (' + side.drukPixels + ', exact op ' +
          this.flagCm.w + ' x ' + this.flagCm.h + ' cm)'
        : '';
      // "het hele doek" en niet "de hele vlag": deze regel gaat sinds de
      // ontwerptool ook op het spandoek mee naar de drukker.
      side.posInput.value =
        (vult
          ? maat + 'ontwerp vult het hele doek' + scherpte + drukklaar
          : maat +
            'ontwerp ' +
            pct((g.w / r.w) * 100) +
            ' breed x ' +
            pct((g.h / r.h) * 100) +
            ' hoog, ' +
            pct(((g.x - r.x) / r.w) * 100) +
            ' vanaf links, ' +
            pct(((g.y - r.y) / r.h) * 100) +
            ' vanaf boven, achtergrond ' +
            side.bg +
            scherpte +
            drukklaar) + tekstUitleg;
      side.posInput.disabled = false;
    });

    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.#naGebaar('previewTimer', () => this.#makePreview()), 350);

    clearTimeout(this.drukTimer);
    this.drukTimer = setTimeout(() => this.#naGebaar('drukTimer', () => this.#maakDrukbestanden()), 900);
  }

  /**
   * Maakt een compact controlebeeld (JPEG, ~900px breed) dat bij elke bestelling meegaat.
   * Bewust klein: het is een referentie voor de drukker, niet het drukbestand zelf.
   */
  #makePreview() {
    // Op hetzelfde rustmoment als het controlebeeld de ontwerp-ID bijwerken:
    // dan staat hij klaar voordat de klant op "in winkelwagen" drukt.
    this.#zetOntwerpId();
    if (!this.previewInput || !this.canvas.toBlob) return;
    // Het controlebeeld is een foto van het doek. In de focusstand staat daar
    // maar een zijde op, en dat hoort niet bij de bestelling. Bij het verlaten
    // van de focusstand wordt opnieuw getekend en komt het controlebeeld alsnog.
    if (this.layout && (this.layout.focus === 0 || this.layout.focus === 1)) return;
    if (!this.sides.some((s) => s.img || this.#heeftLagen(s))) return;
    // Welk controlebeeld is het nieuwste? Zie #wachtOpBestanden.
    const beurt = (this.previewBeurt = (this.previewBeurt || 0) + 1);
    try {
      const { view } = this.layout;
      const breedte = Math.min(900, view.w);
      const klein = document.createElement('canvas');
      klein.width = Math.round(breedte);
      klein.height = Math.round((view.h / view.w) * breedte);

      const k = klein.getContext('2d');
      k.fillStyle = '#ffffff'; // JPEG kent geen transparantie
      k.fillRect(0, 0, klein.width, klein.height);
      // Het kader om een gekozen element hoort niet in het controlebeeld.
      this.zonderSelectie = true;
      this.#render(true);
      k.drawImage(this.canvas, 0, 0, klein.width, klein.height);
      this.zonderSelectie = false;
      this.#render(true);

      klein.toBlob(
        async (blob) => {
          klein.width = 0;
          klein.height = 0;
          if (!blob || !this.uploadUrl) return;
          // Vooraf wegzetten en alleen de URL meesturen. Het bestelverzoek
          // blijft daardoor klein, zodat de winkelwagen meteen bijwerkt.
          try {
            const body = new FormData();
            body.append('file', blob, 'controlebeeld.jpg');
            const res = await fetch(this.uploadUrl, { method: 'POST', body });
            if (!res.ok) return;
            const data = await res.json();
            if (!data.url) return;
            this.previewInput.value = data.url;
            this.previewInput.disabled = false;
            this.previewKlaarBeurt = beurt;
          } catch (err) {
            /* vangnet: de plaatsingsregel en het drukbestand blijven de bron */
          }
        },
        'image/jpeg',
        0.82
      );
    } catch (err) {
      /* niets */
    }
  }

  /**
   * Maakt per zijde een drukklaar bestand: alleen het vlagvlak, op precies de
   * verhouding van het gekozen formaat, met het ontwerp er al in geplaatst.
   *
   * Zonder dit krijgt de drukker het losse ontwerp plus een zin met percentages
   * en moet hij het zelf inpassen. Dat kost studiotijd en ging bij bestelling
   * #1009 mis. Nu komt er een bestand binnen dat zo op de pers kan.
   *
   * De resolutie wordt niet hoger gezet dan het aangeleverde ontwerp aankan —
   * opblazen voegt niets toe en maakt het bestand alleen zwaarder.
   */
  #maakDrukbestanden() {
    if (!this.uploadUrl) return;
    // Beachflags nog niet: de printmaat in deze code (72 x 260 cm) is een
    // schatting voor de scherpteberekening, geen template van de fabriek. Een
    // "drukklaar" bestand op een geraden maat is erger dan geen bestand.
    if (this.isBeach) return;
    this.sides.forEach((side) => this.#maakDrukbestand(side));
  }

  /**
   * Eén drukbestand tegelijk op het tekenvlak. De zijden starten nog wel samen
   * (lettertypes laden, uploaden), maar het grote canvas maakt er maar één
   * tegelijk. Geeft een functie terug die het slot weer vrijgeeft.
   */
  async #wachtOpDrukSlot() {
    const vorige = this.drukSlot || Promise.resolve();
    let vrij;
    this.drukSlot = new Promise((res) => {
      vrij = res;
    });
    await vorige;
    return vrij;
  }

  /**
   * Mag dit ontwerp onbeperkt vergroot worden zonder scherpte te verliezen?
   *
   * Alleen bij een SVG. De browser rastert zo'n bestand bij het tekenen
   * opnieuw op de maat die je opgeeft, dus er is geen bovengrens. Een PDF is
   * hier al door pdf.js tot een bitmap van vaste maat gemaakt; die telt dus
   * gewoon mee als bitmap, ook al staat `side.vector` er op true.
   */
  #schaalbaar(side) {
    if (!side.vector) return false;
    if (side.vlagCode) return true;                   // vlag uit de databank is .svg
    const type = (side.bronBestand && side.bronBestand.type) || '';
    return type === 'image/svg+xml' || /\.svg$/i.test(side.fileName || '');
  }

  #drukMaat(side) {
    const r = side.rect;
    const bovengrens = Math.round((this.flagCm.w / 2.54) * 150);
    // Alleen tekst, geen ontwerp: er is geen bronbestand dat de bovengrens
    // bepaalt, dus gaan we naar de volle 150 ppi. Tekst is scherp op elk
    // formaat, dus dat kost niets aan kwaliteit.
    if (!side.img) {
      const breed = Math.max(1200, Math.min(8000, bovengrens));
      return { breed, hoog: Math.max(1, Math.round((breed * r.h) / r.w)) };
    }
    const g = this.#geometry(side);
    const deel = Math.max(g.w / r.w, 0.05);           // hoeveel van de breedte het ontwerp beslaat
    // Bij een vector zegt naturalWidth niets over de kwaliteit. Een SVG zonder
    // width/height meldt zichzelf zelfs als een paar honderd pixels: het
    // bestand van bestelling #1022 gaf 188 px terug, terwijl het oneindig
    // scherp is. Die waarde mocht de bovengrens dus niet bepalen — hij deed
    // dat wel, en dat leverde een drukbestand van 1200 px op een vlag van
    // 300 cm op: 10 ppi in plaats van 68. #toonScherpte paste deze regel al
    // wel toe (daar krijgt een vector terecht geen resolutiewaarschuwing);
    // hier ontbrak hij, en die twee spraken elkaar dus tegen.
    const uitOntwerp = this.#schaalbaar(side)
      ? Infinity
      : Math.round(side.img.naturalWidth / deel);
    const breed = Math.max(1200, Math.min(8000, bovengrens, uitOntwerp));
    return { breed, hoog: Math.max(1, Math.round((breed * r.h) / r.w)) };
  }

  async #maakDrukbestand(side) {
    if (!side.drukInput) return;
    const heeftLagen = this.tekstToegestaan && this.#heeftLagen(side);
    if (!side.img && !heeftLagen) return;

    const r = side.rect;
    const g = side.img ? this.#geometry(side) : { x: r.x, y: r.y, w: 0, h: 0 };
    const { breed, hoog } = this.#drukMaat(side);

    // niet opnieuw maken als er niets veranderd is. De lagen horen hier ook
    // in: verandert de klant een woord of een kleur, dan moet er een nieuw
    // drukbestand komen — anders drukt de drukker de vorige versie.
    const laagKenmerk = side.lagen
      .map((l) => [l.type, l.tekst, l.font, l.teken, l.naam, l.vorm, l.verhouding,
                   l.kleur, l.grootte, l.hoek, Math.round(l.cx), Math.round(l.cy)].join(','))
      .join(';');
    const kenmerk = [side.fileName, this.flagCm.w + 'x' + this.flagCm.h,
                     this.isBeach ? this.vorm : '', breed, hoog, side.bg, side.stretch ? 1 : 0,
                     // In verhouding tot het vlagvlak, niet in beeldpunten: het doek
                     // wordt op verschillende groottes getoond (gewoon of in de
                     // focusstand), en dat is geen reden voor een nieuw drukbestand.
                     Math.round(((g.x - r.x) / r.w) * 10000), Math.round(((g.y - r.y) / r.h) * 10000),
                     Math.round((g.w / r.w) * 10000), Math.round((g.h / r.h) * 10000), laagKenmerk].join('|');
    if (side.drukKenmerk === kenmerk) return;
    side.drukKenmerk = kenmerk;

    // Meteen loslaten: tot het nieuwe bestand klaar is mag er geen oude URL
    // meegaan. Anders krijgt de drukker bij een maatwijziging het bestand van
    // het vorige formaat — precies de fout die bestelling #1009 opleverde.
    side.drukPixels = null;
    side.drukInput.value = '';
    side.drukInput.disabled = true;

    // Webletters moeten binnen zijn voordat er getekend wordt. Anders valt de
    // browser stilletjes terug op Arial en krijgt de klant een andere vlag
    // gedrukt dan hij op zijn scherm zag.
    if (document.fonts && document.fonts.load) {
      const nodig = side.lagen
        .filter((l) => l.type === 'tekst' && l.tekst)
        .map((l) => '700 40px ' + l.font.split(',')[0].trim());
      try {
        await Promise.all([...new Set(nodig)].map((f) => document.fonts.load(f)));
      } catch (err) {
        /* lukt het laden niet, dan tekenen we met wat er is */
      }
    }

    // Kleurensymbolen worden als afbeelding getekend en die laden met
    // vertraging. Eerst wachten tot ze er allemaal zijn — anders staat het
    // symbool wel in de preview maar niet op het drukbestand, en dat merkt
    // niemand tot het pakket bij de klant ligt.
    const wachtend = side.lagen.filter(
      (l) => (l.type === 'kleur' || l.type === 'eigen') && !l.plaatje && !l.plaatjeStuk
    );
    if (wachtend.length) {
      wachtend.forEach((l) => this.#laadPlaatje(l));
      const klaar = await new Promise((res) => {
        const start = Date.now();
        const kijk = () => {
          if (wachtend.every((l) => l.plaatje || l.plaatjeStuk)) return res(true);
          if (Date.now() - start > 8000) return res(false);
          setTimeout(kijk, 120);
        };
        kijk();
      });
      // Niet gelukt, of de klant heeft intussen iets anders gedaan: dit
      // drukbestand niet maken. Het kenmerk vrijgeven zodat een volgende
      // poging het opnieuw probeert.
      if (!klaar || side.drukKenmerk !== kenmerk) {
        if (side.drukKenmerk === kenmerk) side.drukKenmerk = null;
        return;
      }
    }

    // Het tekenen hieronder gebeurt op drukformaat en houdt de browser even
    // bezig. Sleept de klant intussen weer, dan eerst die beweging afwachten.
    while (this.#bezigMetGebaar()) await new Promise((res) => setTimeout(res, 200));
    if (side.drukKenmerk !== kenmerk) return;   // intussen alweer iets veranderd

    // Om de beurt: twee drukbestanden van 8000 px tegelijk kostten samen ruim
    // 340 MB, en daar liep een iPhone op vast (#wachtOpDrukSlot).
    const vrij = await this.#wachtOpDrukSlot();
    let blob;
    let doek = null;
    try {
      if (side.drukKenmerk !== kenmerk) return;   // tijdens het wachten veranderd
      doek = document.createElement('canvas');
      doek.width = breed;
      doek.height = hoog;
      const d = doek.getContext('2d');
      d.fillStyle = side.bg || '#ffffff';
      d.fillRect(0, 0, breed, hoog);
      const schaal = breed / r.w;
      d.imageSmoothingQuality = 'high';
      if (side.img) {
        d.drawImage(side.img, (g.x - r.x) * schaal, (g.y - r.y) * schaal, g.w * schaal, g.h * schaal);
      }
      // Dezelfde tekstsom als in de preview, alleen op drukformaat. Het doek
      // begint hier linksboven op 0,0, dus geven we een rechthoek mee die
      // daarop staat — de percentages doen de rest.
      this.#tekenLagen(d, side, { x: 0, y: 0, w: breed, h: hoog });
      blob = await new Promise((res) => doek.toBlob(res, 'image/jpeg', 0.92));
    } catch (err) {
      return;                                   // tekenen mislukt: gewoon overslaan
    } finally {
      // Meteen vrijgeven. Zonder dit bleef elk drukbestand van 170 MB in het
      // geheugen hangen tot de browser zelf ging opruimen; op een iPhone kwam
      // het doek dan leeg in beeld.
      if (doek) {
        doek.width = 0;
        doek.height = 0;
      }
      vrij();
    }
    if (!blob) {
      // Mislukt, bijvoorbeeld door te weinig geheugen. Het kenmerk vrijgeven,
      // zodat de volgende wijziging het opnieuw probeert in plaats van nooit.
      if (side.drukKenmerk === kenmerk) side.drukKenmerk = null;
      return;
    }

    const naam = 'drukklaar-' + this.flagCm.w + 'x' + this.flagCm.h + 'cm-' + breed + 'x' + hoog + '.jpg';
    try {
      const body = new FormData();
      body.append('file', blob, naam);
      const res = await fetch(this.uploadUrl, { method: 'POST', body });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.url || side.drukKenmerk !== kenmerk) return;   // intussen weer gewijzigd
      side.drukInput.value = data.url;
      side.drukInput.disabled = false;
      side.drukPixels = breed + ' x ' + hoog + ' px';
      this.#output();
    } catch (err) {
      /* vangnet: de drukker heeft nog het originele bestand en de plaatsingsregel */
    }
  }

  /**
   * Terugkoppeling tijdens het uploaden. Zonder dit lijkt er niets te gebeuren
   * bij grote ontwerpbestanden, en klikken klanten nog eens (dubbele bestelling).
   */
  #bindBusyState() {
    const form = this.closest('form');
    const melding = this.querySelector('[data-busy]');
    if (!form || !melding) return;

    const knop = () => form.querySelector('button[type="submit"]');

    const bezig = () => {
      // vermeld de omvang: dan snapt de klant waarom een groot ontwerp langer duurt
      let mb = 0;
      this.sides.forEach((s) => {
        const f = s.fileInput.files && s.fileInput.files[0];
        if (f) mb += f.size;
      });
      mb = mb / 1048576;
      const basis = this.dataset.tBusy || '';
      melding.textContent = mb >= 1 ? basis + ' (' + mb.toFixed(1).replace('.', ',') + ' MB)' : basis;
      melding.hidden = false;
      this.bezigMetUploaden = true;
      const b = knop();
      if (b) b.setAttribute('aria-busy', 'true');
      this.#zetBestelknop();
    };

    const klaar = () => {
      melding.hidden = true;
      this.bezigMetUploaden = false;
      const b = knop();
      if (b) b.removeAttribute('aria-busy');
      // niet blind weer aanzetten: als het ontwerp te grof is moet de knop uit
      // blijven staan.
      this.#zetBestelknop();
    };

    form.addEventListener('submit', () => {
      if (!this.sides.some((s) => s.fileInput.files && s.fileInput.files.length)) return;
      bezig();
      // veiligheidsnet: nooit langer dan een halve minuut blokkeren
      clearTimeout(this.busyTimer);
      this.busyTimer = setTimeout(klaar, 30000);
    });

    ['cart:update', 'cart:add', 'cart:error'].forEach((n) =>
      document.addEventListener(n, () => {
        clearTimeout(this.busyTimer);
        klaar();
      })
    );
  }

  // ---------- ontwerp bewaren en terughalen vanuit de winkelmand ----------
  //
  // Een klant die drie vlaggen in zijn winkelmand heeft, moet kunnen zien welke
  // welke is en er een opnieuw kunnen openen. Het ontwerp zelf (lagen, kleuren,
  // posities, geuploade bestanden) bewaren we in de browser met
  // FlagOntwerpOpslag (assets/flag-ontwerpopslag.js). In de bestelling komt
  // alleen een korte sleutel: _Ontwerp-id.

  /**
   * Wacht tot de opslagmodule er is.
   *
   * Die module staat onderaan de pagina en wordt, net als dit script, met defer
   * geladen. Dit script staat hoger en draait dus eerder. "Nog niet gevonden" is
   * daarom geen "niet bewaard": er wordt gewacht tot hij er is. Zonder dat
   * wachten zag Jos bij "Ontwerp aanpassen" de melding dat het ontwerp niet
   * bewaard was, terwijl het er gewoon stond. In de test viel het niet op,
   * omdat beide scripts daar al in het geheugen stonden en vlak na elkaar liepen.
   */
  #opslag() {
    return new Promise((klaar) => {
      if (window.FlagOntwerpOpslag) return klaar(window.FlagOntwerpOpslag);
      let gemeld = false;
      const meld = (waarde) => {
        if (gemeld) return;
        gemeld = true;
        clearInterval(peiling);
        clearTimeout(opgeven);
        document.removeEventListener('flagcfg:opslag-klaar', bijSeintje);
        klaar(waarde);
      };
      const bijSeintje = () => meld(window.FlagOntwerpOpslag || null);
      document.addEventListener('flagcfg:opslag-klaar', bijSeintje);
      // Vangnet als het seintje er al was voordat we luisterden.
      const peiling = setInterval(() => {
        if (window.FlagOntwerpOpslag) meld(window.FlagOntwerpOpslag);
      }, 50);
      // Laadt de module helemaal niet (geblokkeerd, netwerkfout), dan na een
      // ruime tijd opgeven.
      const opgeven = setTimeout(() => meld(window.FlagOntwerpOpslag || null), 10000);
    });
  }

  #winkelWortel() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /** De gegevens van een laag die het ontwerp bepalen: geen beelden, geen paginaonderdelen. */
  #laagGegevens(l) {
    const overslaan = ['objectUrl', 'bronBestand', 'plaatje', 'plaatjeBezig', 'plaatjeStuk', 'stipEl', 'stipNaNaam'];
    const uit = {};
    Object.keys(l).forEach((k) => {
      if (overslaan.includes(k)) return;
      const v = l[k];
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') uit[k] = v;
    });
    return uit;
  }

  /**
   * Een vaste vingerafdruk van het ontwerp zoals het nu is. Hetzelfde ontwerp
   * geeft dezelfde ID, zodat twee keer toevoegen zoals altijd een hoger aantal
   * oplevert en geen tweede regel. Een ander ontwerp krijgt een andere ID.
   */
  #vingerafdruk() {
    const sleutel = (f) => (f ? [f.name || '', f.size || 0, f.lastModified || 0].join('|') : '');
    const afwerking = this.querySelector('[data-finishkeuze]');
    const gegevens = {
      v: this.variantVeld ? this.variantVeld.value : '',
      a: afwerking && !afwerking.disabled ? afwerking.value : '',
      z: this.sides.map((s) => ({
        bg: s.bg || '#ffffff',
        img: s.img ? 1 : 0,
        vlag: s.img ? s.vlagCode || '' : '',
        bestand: s.img && !s.vlagCode ? sleutel(s.bronBestand) : '',
        zoom: Math.round((s.zoom || 1) * 1000),
        cx: Math.round((s.cx || 0) * 10),
        cy: Math.round((s.cy || 0) * 10),
        rek: s.stretch ? 1 : 0,
        lagen: (s.lagen || []).map((l) => {
          const g = this.#laagGegevens(l);
          delete g.id;
          delete g.svg;
          if (l.bronBestand) g.bestand = sleutel(l.bronBestand);
          return g;
        }),
      })),
    };
    const tekst = JSON.stringify(gegevens);
    // FNV-1a, twee keer met een ander begin: kort, en ruim uniek genoeg voor
    // de paar regels in een winkelmand.
    const fnv = (begin) => {
      let h = begin >>> 0;
      for (let i = 0; i < tekst.length; i++) {
        h ^= tekst.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
      }
      return h.toString(36);
    };
    return 'o' + fnv(2166136261) + fnv(3339675911);
  }

  /** Zet de sleutel in het verborgen veld _Ontwerp-id. */
  #zetOntwerpId() {
    const veld = this.querySelector('[data-ontwerpid]');
    if (!veld) return;
    if (!this.sides || !this.sides.some((s) => s.img || this.#heeftLagen(s))) {
      veld.value = '';
      veld.disabled = true;
      return;
    }
    const afdruk = this.#vingerafdruk();
    // In de bewerkstand houdt de regel zijn eigen sleutel zolang er niets
    // veranderd is. Anders valt een gedupliceerde regel bij opslaan zonder
    // wijziging samen met het origineel.
    veld.value = this.bewerk && this.bewerk.startAfdruk === afdruk ? this.bewerk.id : afdruk;
    veld.disabled = false;
  }

  /** Legt het hele ontwerp vast in de browser, onder de gegeven sleutel. */
  async #momentopname(id) {
    const o = await this.#opslag();
    if (!o || !id) return;
    try {
      const bestandsvelden = (f, reserve) => ({
        bestand: f,
        bestandNaam: (f && f.name) || reserve || '',
        bestandType: (f && f.type) || '',
        bestandTijd: (f && f.lastModified) || 0,
      });
      const zijden = await Promise.all(
        this.sides.map(async (s) => {
          const titel = s.root.querySelector('.flagcfg__sidetitle');
          const zijde = {
            label: titel ? titel.textContent.trim() : '',
            bg: s.bg || '#ffffff',
            zoom: s.zoom,
            cx: s.cx,
            cy: s.cy,
            stretch: !!s.stretch,
            vector: !!s.vector,
            fileName: s.fileName || '',
            vlagCode: s.img ? s.vlagCode || '' : '',
            vooraf: s.vooraf || '',
            libLabel: s.libValue && !s.libValue.disabled ? s.libValue.value : '',
            bestand: null,
            lagen: (s.lagen || []).map((l) => {
              const g = this.#laagGegevens(l);
              if (l.bronBestand) Object.assign(g, bestandsvelden(l.bronBestand, l.naam));
              return g;
            }),
            voorbeeld: null,
          };
          if (s.img && !s.vlagCode && s.bronBestand) Object.assign(zijde, bestandsvelden(s.bronBestand, s.fileName));
          return zijde;
        })
      );
      const afwerking = this.querySelector('[data-finishkeuze]');
      const ontwerp = {
        id,
        v: 1,
        pad: location.pathname,
        variantId: this.variantVeld ? this.variantVeld.value : '',
        afwerking: afwerking && !afwerking.disabled ? afwerking.value : '',
        zijden,
      };
      // Eerst het ontwerp zelf wegschrijven, zonder voorbeeldplaatjes. Dat gaat
      // snel, zodat het ook bewaard is als de klant meteen doorklikt naar de
      // winkelmand: een onderbroken schrijfactie laat anders niets achter.
      await o.bewaar(ontwerp);

      // Daarna per zijde een nette vergroting voor "Bekijk ontwerp" in de
      // winkelmand. Het controlebeeld is daarvoor te klein als er op een
      // telefoon ontworpen is. Mislukt dit, dan blijft het controlebeeld de
      // terugval.
      let metVoorbeeld = false;
      for (let i = 0; i < this.sides.length; i++) {
        const s = this.sides[i];
        if (this.isBeach || !(s.img || this.#heeftLagen(s))) continue;
        zijden[i].voorbeeld = await new Promise((klaar) => {
          try {
            const doekje = this.#previewDoek(s, 1600);
            doekje.toBlob((b) => {
              doekje.width = 0;
              doekje.height = 0;
              klaar(b);
            }, 'image/jpeg', 0.86);
          } catch (err) {
            klaar(null);
          }
        });
        if (zijden[i].voorbeeld) metVoorbeeld = true;
      }
      if (metVoorbeeld) await o.bewaar(ontwerp);
    } catch (err) {
      /* bewaren is een extra; het bestellen zelf gaat gewoon door */
    }
  }

  /** Zet een bewaard ontwerp terug op het doek: afbeeldingen, lagen, kleuren en posities. */
  async #herstelOntwerp(ontwerp) {
    const afwerking = this.querySelector('[data-finishkeuze]');
    if (afwerking && ontwerp.afwerking && [...afwerking.options].some((o) => o.value === ontwerp.afwerking)) {
      afwerking.value = ontwerp.afwerking;
      afwerking.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const laadBeeld = (src, cors) =>
      new Promise((klaar) => {
        const img = new Image();
        if (cors) img.crossOrigin = 'anonymous';
        img.onload = () => klaar(img);
        img.onerror = () => klaar(null);
        img.src = src;
      });
    const alsBestand = (blob, naam, type, tijd) => {
      if (!blob) return null;
      try {
        return new File([blob], naam || 'ontwerp', { type: type || blob.type || '', lastModified: tijd || Date.now() });
      } catch (err) {
        return blob;
      }
    };

    const zijden = ontwerp.zijden || [];
    await Promise.all(
      this.sides.map(async (side, i) => {
        const z = zijden[i];
        if (!z) return;

        side.bg = z.bg || '#ffffff';
        [side.bgInput, side.bgInput2].forEach((el) => {
          if (el) el.value = side.bg;
        });

        // De afbeelding die het doek vult: een landvlag, of een eigen bestand.
        if (z.vlagCode) {
          const img = await laadBeeld(this.flagBase + 'flag-' + z.vlagCode + '.svg', true);
          if (img) {
            side.img = img;
            side.vector = true;
            side.vlagCode = z.vlagCode;
            side.bronBestand = null;
            side.fileName = z.fileName || '';
            side.libValue.value = z.libLabel || side.fileName;
            side.libValue.disabled = !side.libValue.value;
            side.nameEl.textContent = this.chosenText + ': ' + side.fileName;
            const knop = side.libGrid && side.libGrid.querySelector('[data-code="' + z.vlagCode + '"]');
            if (knop) knop.classList.add('is-chosen');
          }
        } else if (z.bestand) {
          const bestand = alsBestand(z.bestand, z.bestandNaam, z.bestandType, z.bestandTijd);
          const pdf = /\.pdf$/i.test(z.bestandNaam || '') || (z.bestandType || '') === 'application/pdf';
          let img = null;
          if (pdf) {
            img = await this.#pdfNaarBeeld(bestand, side);
          } else {
            const url = URL.createObjectURL(bestand);
            img = await laadBeeld(url, false);
            if (img) side.objectUrl = url;
            else URL.revokeObjectURL(url);
          }
          if (img) {
            side.img = img;
            side.vector = !!z.vector;
            side.vlagCode = null;
            side.bronBestand = bestand;
            side.fileName = z.fileName || z.bestandNaam || '';
            side.nameEl.textContent = side.fileName;
            side.libValue.value = side.fileName;
            side.libValue.disabled = !side.fileName;
            // Het origineel staat al bij de uploadserver: dezelfde URL gaat
            // mee naar de drukker, er hoeft niets opnieuw geupload te worden.
            if (z.vooraf) {
              side.vooraf = z.vooraf;
              this.#schakelBestandsveld(side);
            } else if (bestand instanceof File) {
              this.#alvastUploaden(side, bestand);
            }
          }
        }
        if (side.img) {
          side.zoom = typeof z.zoom === 'number' ? z.zoom : 1;
          side.cx = typeof z.cx === 'number' ? z.cx : 50;
          side.cy = typeof z.cy === 'number' ? z.cy : 50;
          side.stretch = !!z.stretch;
          if (side.zoomInput) side.zoomInput.value = String(Math.round(side.zoom * 100));
          side.controls.hidden = false;
          side.noteEl.hidden = true;
        }

        // De elementen: tekst, vormen, symbolen en eigen afbeeldingen.
        side.lagen = (z.lagen || []).map((g) => {
          const l = Object.assign({}, g, { id: this.#nieuwId() });
          delete l.bestand;
          delete l.bestandNaam;
          delete l.bestandType;
          delete l.bestandTijd;
          if (l.type === 'kleur' && l.svg) this.#laadPlaatje(l);
          if (l.type === 'eigen') {
            if (g.vlagCode) {
              laadBeeld(this.flagBase + 'flag-' + g.vlagCode + '.svg', true).then((img) => {
                if (!img) {
                  l.plaatjeStuk = true;
                  return;
                }
                l.plaatje = img;
                this.#render();
              });
            } else if (g.bestand) {
              const bestand = alsBestand(g.bestand, g.bestandNaam, g.bestandType, g.bestandTijd);
              l.bronBestand = bestand;
              const url = URL.createObjectURL(bestand);
              l.objectUrl = url;
              laadBeeld(url, false).then((img) => {
                if (!img) {
                  l.plaatjeStuk = true;
                  URL.revokeObjectURL(url);
                  return;
                }
                l.plaatje = img;
                this.#render();
              });
            } else {
              l.plaatjeStuk = true;
            }
          }
          return l;
        });
      })
    );

    this.sides.forEach((side) => {
      this.#toonBgWis(side);
      this.#tekenLagenLijst(side);
    });
    this.#render();
  }

  /** Korte melding boven het doek. */
  #toonBewerkMelding(tekst, fout) {
    if (!tekst) return;
    let el = this.querySelector('[data-flagcfg-bewerkmelding]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-flagcfg-bewerkmelding', '');
      el.setAttribute('role', 'status');
      const stage = this.querySelector('.flagcfg__stage');
      if (stage && stage.parentNode) stage.parentNode.insertBefore(el, stage);
      else this.prepend(el);
    }
    // Rechtstreeks opgemaakt, net als de Klaar-knop: de gebundelde opmaak
    // ververst niet altijd tegelijk met dit script.
    el.style.cssText = [
      'margin:0 0 .8rem', 'padding:.7rem .9rem', 'border-radius:8px', 'font-size:.88rem', 'line-height:1.4',
      'background:' + (fout ? '#fdf2f2' : '#eef3fb'),
      'color:' + (fout ? '#8a1f1f' : '#22314d'),
      'border:1px solid ' + (fout ? '#f1c9c9' : '#d3dcee'),
    ].join(';');
    el.textContent = tekst;
  }

  /** Zet de pagina in de stand "je past een ontwerp uit je winkelmand aan". */
  #zetBewerkModus() {
    const form = this.closest('form');
    if (!form) return;
    const knop = form.querySelector('button[type="submit"]');
    if (knop) {
      // Alleen de tekst vervangen, niet de opbouw van de knop: het thema
      // hangt er zelf onderdelen aan op.
      const doel =
        [...knop.querySelectorAll('*')].find((el) => !el.children.length && el.textContent.trim()) || knop;
      if (this.dataset.tBewerkOpslaan) doel.textContent = this.dataset.tBewerkOpslaan;
    }
    // "Direct kopen" zou een nieuwe regel maken en meteen afrekenen; in deze
    // stand is dat verwarrend.
    const blok = form.closest('product-form-component') || form.parentElement;
    if (blok) {
      blok.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout, [data-shopify="payment-button"]').forEach((el) => {
        el.style.display = 'none';
      });
    }
    const aantal = form.querySelector('input[name="quantity"]');
    if (aantal && this.bewerk) {
      aantal.value = String(this.bewerk.aantal);
      aantal.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#toonBewerkMelding(this.dataset.tBewerkBanner || '', false);
  }

  /** Kwam de klant via "Ontwerp aanpassen"? Dan het ontwerp terughalen en de tool openen. */
  async #startBewerken(id, regel) {
    const o = await this.#opslag();
    const ontwerp = o ? await o.haal(id) : null;
    if (!ontwerp) {
      this.#toonBewerkMelding(this.dataset.tBewerkKwijt || '', true);
      return;
    }

    let winkelregel = null;
    try {
      const winkelmand = await fetch(this.#winkelWortel() + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
      // Eerst op de sleutel uit de link, anders op het ontwerp-ID. De sleutel
      // van een regel kan veranderen (bijvoorbeeld als het aantal intussen is
      // aangepast); het ontwerp-ID verandert niet.
      winkelregel =
        winkelmand.items.find((i) => i.key === regel) ||
        winkelmand.items.find((i) => i.properties && i.properties['_Ontwerp-id'] === id) ||
        null;
    } catch (err) {
      /* dan gewoon als nieuw ontwerp behandelen */
    }

    await this.#herstelOntwerp(ontwerp);

    // Staat de regel niet meer in de winkelmand (intussen verwijderd), dan
    // is er niets om bij te werken: het wordt dan een gewone nieuwe bestelling.
    if (winkelregel) {
      this.bewerk = {
        id,
        regel: winkelregel.key,
        aantal: winkelregel.quantity,
        variantId: winkelregel.variant_id,
        startAfdruk: this.#vingerafdruk(),
      };
      this.#zetBewerkModus();
    }
    this.#zetOntwerpId();
    if (this.tekstToegestaan) this.#volScherm(true);
  }

  /**
   * Wacht tot het drukbestand, het controlebeeld en een eventueel geupload
   * origineel van het nieuwe ontwerp klaar zijn. Anders gaat bij het bijwerken
   * de vorige versie mee naar de drukker.
   */
  #wachtOpBestanden() {
    return new Promise((klaar) => {
      const begin = Date.now();
      const kijk = () => {
        const druk =
          this.isBeach ||
          this.sides.every((s) => {
            if (!(s.img || this.#heeftLagen(s)) || !s.drukInput) return true;
            return !!s.drukInput.value && !s.drukInput.disabled;
          });
        const origineel = this.sides.every((s) => !(s.img && s.bronBestand && !s.vlagCode) || !!s.vooraf);
        const beeld =
          !this.previewInput ||
          ((this.previewBeurt || 0) === (this.previewKlaarBeurt || 0) && !!this.previewInput.value);
        if ((druk && origineel && beeld) || Date.now() - begin > 20000) return klaar();
        setTimeout(kijk, 250);
      };
      // Het controlebeeld komt 350 ms na de laatste wijziging; die kans eerst geven.
      setTimeout(kijk, 450);
    });
  }

  /** Vervangt de winkelmandregel door het aangepaste ontwerp, in plaats van een nieuwe toe te voegen. */
  async #werkRegelBij(form, id) {
    const knop = form.querySelector('button[type="submit"]');
    if (knop) {
      knop.disabled = true;
      knop.setAttribute('aria-busy', 'true');
    }
    const wortel = this.#winkelWortel();
    try {
      await this.#wachtOpBestanden();
      this.#zetOntwerpId();
      const veld = this.querySelector('[data-ontwerpid]');
      const nieuwId = (veld && !veld.disabled && veld.value) || id;

      // Niets veranderd: dan valt er ook niets bij te werken.
      if (nieuwId === this.bewerk.id) {
        location.href = wortel + 'cart';
        return;
      }

      await this.#momentopname(nieuwId);

      const gegevens = new FormData(form);
      const eigenschappen = {};
      for (const [naam, waarde] of gegevens.entries()) {
        const m = /^properties\[(.+)\]$/.exec(naam);
        if (m && typeof waarde === 'string' && waarde !== '') eigenschappen[m[1]] = waarde;
      }
      eigenschappen['_Ontwerp-id'] = nieuwId;
      // De regel vlak voor het bijwerken opnieuw opzoeken: sleutel en aantal
      // kunnen veranderd zijn sinds de pagina openging. Is hij intussen uit de
      // winkelmand gehaald, dan wordt het aangepaste ontwerp een nieuwe regel.
      let sleutel = this.bewerk.regel;
      let aantal = this.bewerk.aantal || 1;
      let oudeVariant = Number(this.bewerk.variantId);
      let bestaatNog = true;
      try {
        const nu = await fetch(wortel + 'cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => r.json());
        const regel =
          nu.items.find((i) => i.properties && i.properties['_Ontwerp-id'] === this.bewerk.id) ||
          nu.items.find((i) => i.key === this.bewerk.regel);
        if (regel) {
          sleutel = regel.key;
          aantal = regel.quantity;
          oudeVariant = Number(regel.variant_id);
        } else {
          bestaatNog = false;
        }
      } catch (err) {
        /* dan met wat we bij het openen wisten */
      }
      const variant = Number(gegevens.get('id')) || oudeVariant;

      const stuur = (pad, body) =>
        fetch(wortel + pad, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((r) => {
          if (!r.ok) throw new Error(pad + ' ' + r.status);
          return r.json();
        });

      if (!bestaatNog) {
        await stuur('cart/add.js', { items: [{ id: variant, quantity: aantal, properties: eigenschappen }] });
      } else if (variant === oudeVariant) {
        await stuur('cart/change.js', { id: sleutel, quantity: aantal, properties: eigenschappen });
      } else {
        // Een andere maat is een andere variant, en die kan Shopify niet op een
        // bestaande regel wisselen. Eerst de nieuwe toevoegen en dan pas de
        // oude weghalen: mislukt het toevoegen, dan staat de oude er nog.
        await stuur('cart/add.js', { items: [{ id: variant, quantity: aantal, properties: eigenschappen }] });
        // Na het toevoegen heeft de oude regel een andere sleutel gekregen:
        // Shopify rekent de hele winkelmand opnieuw door. Daarom opnieuw
        // opzoeken op het oude ontwerp-ID en de oude maat, en pas dan weghalen.
        // Getest: met de sleutel van voor het toevoegen bleef de oude regel staan.
        const naToevoegen = await fetch(wortel + 'cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => r.json());
        const oud =
          naToevoegen.items.find(
            (i) =>
              i.properties &&
              i.properties['_Ontwerp-id'] === this.bewerk.id &&
              Number(i.variant_id) === oudeVariant
          ) || naToevoegen.items.find((i) => i.key === sleutel);
        if (oud) await stuur('cart/change.js', { id: oud.key, quantity: 0 });
      }
      location.href = wortel + 'cart';
    } catch (err) {
      this.#toonBewerkMelding(this.dataset.tBewerkMislukt || '', true);
      if (knop) {
        knop.disabled = false;
        knop.removeAttribute('aria-busy');
      }
    }
  }

  #bindOntwerpOpslag() {
    const form = this.closest('form');
    if (!form) return;

    // Op window en in de vangfase: dan gebeurt dit voordat het thema het
    // formulier uitleest. Het thema luistert zelf op document, ook in de
    // vangfase, en een luisteraar op het formulier komt dus altijd te laat.
    window.addEventListener(
      'submit',
      (e) => {
        if (e.target !== form) return;
        // Zonder ontwerp niet bestellen: de rij gaat open (Jos, 15 sep 2026).
        if (this.#moetOntwerpVragen()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.#vraagOmOntwerp();
          return;
        }
        this.#zetOntwerpId();
        const veld = this.querySelector('[data-ontwerpid]');
        const id = veld && !veld.disabled ? veld.value : '';

        if (this.bewerk) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.#werkRegelBij(form, id);
          return;
        }
        if (id) this.#momentopname(id);
      },
      true
    );

    // De bestelknop van het thema speelt bij de tik zelf al een animatie af,
    // vóór het versturen. Daarom hier ook de tik al tegenhouden, anders vliegt
    // er een vlag naar de winkelmand die er nooit aankomt.
    window.addEventListener(
      'click',
      (e) => {
        const knop = e.target instanceof Element && e.target.closest('button[type="submit"]');
        if (!knop || knop.form !== form || !this.#moetOntwerpVragen()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.#vraagOmOntwerp();
      },
      true
    );

    const params = new URLSearchParams(location.search);
    const id = params.get('ontwerp');
    const regel = params.get('regel');
    if (id && regel) this.#startBewerken(id, regel);
  }

  /** Voorkomt dat velden van andere apps (Customily) dubbel meegaan. */
  #disableForeignProperties() {
    const form = this.closest('form');
    if (!form) return;
    form.querySelectorAll('[name^="properties["]').forEach((el) => {
      if (!this.contains(el)) el.disabled = true;
    });
  }
}

console.info('[ontwerptool] versie ' + FLAGCFG_VERSIE);

if (!customElements.get('flag-configurator')) {
  customElements.define('flag-configurator', FlagConfigurator);
}
